import { DECK_SURFACE_CSS } from './decks.css.js';
import {
  COLORS,
  DECK_GLYPH_ITEM_BORDER,
  DECK_GLYPH_ITEM_PADDING,
  DECK_GLYPH_ROW_HEIGHT,
  MOTION_EASINGS,
  MOTION_SPRINGS,
} from './tokens.js';
import {
  CATALOG_CARD_STYLES,
  CATALOG_CARD_WIDTH,
} from '../CatalogCard.js';
import { SCROLLING_CARD_NAME_STYLES } from '../ScrollingCardName.js';
import { pixelIconDataUri } from '../PixelIcon.js';
import { CARD_BACK_PATTERN } from '../CardBackGlyph.js';
import { pixelPatternDataUri } from '../PixelPattern.js';
import { STATUS_FAMILY_TONES } from '../cardStatus.js';
import { BETA_TICKER_STYLES } from '../BetaTicker.js';
import { playbackTiming, TURN_PLAYBACK_TIMING } from '../TurnPlayback.js';

/**
 * The result title's pixel outline (01 §3.4 outline 3): eight 3px offsets in
 * the outline colour and a 6px step in the result's own colour.
 */
const RESULT_TITLE_OUTLINE = [
  [-3, -3], [0, -3], [3, -3], [-3, 0], [3, 0], [-3, 3], [0, 3], [3, 3],
].map(([x, y]) => `${x}px ${y}px 0 var(--c-outline)`).join(', ')
  + ', 0 6px 0 var(--cards-match-result-step)';

/** A beat's share of its window, as a keyframe offset. */
const pctOf = (ms: number, windowMs: number): number => Math.round(ms / windowMs * 1000) / 10;

/**
 * Battle beats as CSS time (03 §5), `--beat-<key>`. Every playback animation
 * reads its window from here rather than from a baked-in number, so a playback
 * speed retimes the CSS and the playback waits from one table.
 */
export const beatVars = (timing: Readonly<Record<string, number>>): string => Object.entries(timing)
  .map(([key, ms]) => `--beat-${key.replace(/[A-Z]/g, (ch) => `-${ch.toLowerCase()}`)}: ${ms}ms;`)
  .join('\n  ');

/**
 * Ordered-dither masks for the pixel dissolve of a dying card: a 4×4 Bayer
 * matrix, each mask keeping the cells whose threshold is at least `removed`
 * (0 keeps all sixteen, 16 none). Black on transparent, since a mask reads alpha;
 * tiled at DITHER_TILE so every matrix cell is one hard pixel block.
 */
const BAYER_4 = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5] as const;
const DITHER_TILE = '12px';
export function ditherMask(removed: number): string {
  const cells = BAYER_4.flatMap((threshold, index) => threshold >= removed
    ? [`M${index % 4} ${Math.floor(index / 4)}h1v1h-1z`]
    : []);
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='4' height='4' shape-rendering='crispEdges'><path fill='black' d='${cells.join('')}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
/** The board's status plates, one rule per family (04 §2.1). */
function statusFamilyToneRules(): string {
  return Object.entries(STATUS_FAMILY_TONES)
    .map(([family, { face, ink }]) => `:is(.cards-board-card-statuses, .cards-board-card-m14-markers) [data-tone="${family}"] { --cards-tone-face: ${face}; --cards-tone-ink: ${ink}; }`)
    .join('\n');
}
const maskStop = (removed: number): string =>
  `-webkit-mask-image: ${ditherMask(removed)}; mask-image: ${ditherMask(removed)};`;

/**
 * A card killed by a blow: frozen and white-hot for the hit-stop, knocked back,
 * cracked and greyed (the companion -tone keyframe), then eaten away in pixel
 * blocks while its shards fall (.cards-death-shards). `from` is where the blow
 * finds it — at rest in its slot, or at the contact point of its own lunge.
 * Generated per window because the hit-stop is milliseconds, not a share.
 */
function shatterKeyframes(name: string, windowMs: number, from: 'rest' | 'contact'): string {
  const hold = pctOf(TURN_PLAYBACK_TIMING.hitStop, windowMs);
  const knock = Math.round(hold + 14);
  const settle = knock + 10;
  const start = from === 'rest'
    ? 'translate(0, 0) scale(1)'
    : 'translate(var(--cards-lunge-dx, 0px), var(--cards-lunge-dy, 0px)) scale(1.04)';
  // Knocked the way the blow travelled; a contact-pose card is thrown back home
  // and a little past it.
  const thrown = (share: number): string => from === 'rest'
    ? `translate(calc(var(--cards-hit-dx, 0px) * ${share}), calc(var(--cards-hit-dy, 4px) * ${share}))`
    : `translate(calc(var(--cards-lunge-ux, 0) * ${-6 * share}px), calc(var(--cards-lunge-uy, 0) * ${-6 * share}px))`;
  const sink = (px: number, scale: number): string => from === 'rest'
    ? `translate(calc(var(--cards-hit-dx, 0px) * .6), calc(var(--cards-hit-dy, 4px) * .6 + ${px}px)) scale(${scale})`
    : `translate(calc(var(--cards-lunge-ux, 0) * -3.6px), calc(var(--cards-lunge-uy, 0) * -3.6px + ${px}px)) scale(${scale})`;
  return `@keyframes ${name} {
  0%, ${hold}% { opacity: 1; transform: ${start}; animation-timing-function: ${MOTION_EASINGS.out}; }
  ${knock}% { opacity: 1; transform: ${thrown(1)} scale(1); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  ${settle}% { opacity: .8; transform: ${sink(0, 1)}; }
  40% { opacity: .8; transform: ${sink(0, 1)}; ${maskStop(0)} }
  48% { transform: ${sink(1, .995)}; ${maskStop(4)} }
  58% { transform: ${sink(2, .99)}; ${maskStop(8)} }
  68% { transform: ${sink(3, .98)}; ${maskStop(12)} }
  78% { transform: ${sink(4, .975)}; ${maskStop(14)} }
  86% { opacity: .8; transform: ${sink(5, .97)}; ${maskStop(16)} }
  100% { opacity: 0; transform: ${sink(5, .97)}; ${maskStop(16)} }
}
@keyframes ${name}-tone {
  0% { filter: brightness(2.1); }
  ${hold}% { filter: brightness(1.6); }
  ${knock}% { filter: grayscale(.4) brightness(1.15); }
  ${settle}% { filter: grayscale(.78) brightness(.58) contrast(1.2); }
  100% { filter: grayscale(.78) brightness(.58) contrast(1.2); }
}`;
}

/**
 * The reveal flip as a real turn in depth rather than a squash: the card lifts
 * off its slot, turns edge-on (where playback swaps the back for the face, at
 * 60%), and comes down face-up with a small slam.
 *
 * One element cannot have two sides, so the second half keeps turning while
 * scaleX goes to -1: rotateY(θ) scaleX(-1) for θ in 90–180° is exactly the far
 * side of the card, unmirrored. scaleX passes through 0 only at the edge-on
 * frame, where the card has no width anyway — so the turn is continuous, with
 * no keyframe pair at the swap for a frame to land between.
 *
 * Every stop keeps the same function list so the transform interpolates
 * numerically, and the whole thing runs linear with a curve on each stop: the
 * old single curve made the card slow down INTO edge-on and hang there.
 * --cards-reveal-from is the pile pose of the back this card replaces (see
 * Cell); the card holds it until it turns and rides down into the slot on the
 * way back down.
 */
const REVEAL_PILE = 'calc(var(--cards-overlay-stack-shift, 18px) * -1 * var(--cards-reveal-from, 0))';
function revealKeyframes(name: string, fadeIn: boolean): string {
  const pose = (y: string, turn: number, mirror: number, sx: number, sy: number): string =>
    `transform: translateY(${y}) perspective(420px) rotateY(${turn}deg) scaleX(${mirror}) scale(${sx}, ${sy});`;
  const lifted = (px: number): string => `calc(${REVEAL_PILE} - ${px}px)`;
  // No shadow keyframes: the card keeps its own step, which turns with it like
  // the card's edge (box-shadow never animates, 03 §7).
  return `@keyframes ${name} {
  0% { opacity: ${fadeIn ? 0 : 1}; ${pose(REVEAL_PILE, 0, 1, 1, 1)} }
  8% { opacity: 1; ${pose(REVEAL_PILE, 0, 1, 1, 1)} }
  34% { ${pose(REVEAL_PILE, 0, 1, 1, 1)} animation-timing-function: ${MOTION_EASINGS.in}; }
  55% { ${pose(lifted(7), 72, 1, 1.08, 1.08)} animation-timing-function: linear; }
  60% { ${pose(lifted(8), 90, 0, 1.1, 1.1)} animation-timing-function: linear; }
  65% { ${pose(lifted(8), 108, -1, 1.1, 1.1)} animation-timing-function: ${MOTION_EASINGS.out}; }
  76% { ${pose('-5px', 176, -1, 1.06, 1.06)} animation-timing-function: ${MOTION_EASINGS.in}; }
  82% { ${pose('1px', 180, -1, 1.05, .92)} animation-timing-function: ${MOTION_EASINGS.out}; }
  91% { ${pose('0px', 180, -1, .98, 1.03)} animation-timing-function: ${MOTION_EASINGS.inOut}; }
  100% { ${pose('0px', 180, -1, 1, 1)} }
}`;
}

/**
 * The overlay receiver's reveal-then-gulp on one clock (overlayReveal): the
 * first 42% is revealKeyframes' turn in depth compressed into the cardReveal
 * share of the window (edge-on at 25%, where the JS swaps the face), lying on
 * its host at the pile's offset instead of riding down into the slot; the rest
 * is the flytrap gulp. One function list on every stop, so the turn
 * interpolates number by number — the face-up card is rotateY(180) scaleX(−1),
 * which is why the gulp's squash writes its scaleX negated.
 */
function overlayRevealStops(): string {
  const pile = 'calc(var(--cards-overlay-stack-shift, 18px) * -1 * var(--cards-reveal-from, 1))';
  const share = TURN_PLAYBACK_TIMING.cardReveal / TURN_PLAYBACK_TIMING.overlayReveal;
  const at = (revealPct: number): number => Math.round(revealPct * share * 10) / 10;
  const pose = (y: string, turn: number, mirror: number, sx: number, sy: number): string =>
    `transform: translateY(${y}) perspective(420px) rotateY(${turn}deg) scaleX(${mirror}) scale(${sx}, ${sy});`;
  const lifted = (px: number): string => `calc(${pile} - ${px}px)`;
  return [
    `  0% { ${pose(pile, 0, 1, 1, 1)} }`,
    `  ${at(34)}% { ${pose(pile, 0, 1, 1, 1)} animation-timing-function: ${MOTION_EASINGS.in}; }`,
    `  ${at(55)}% { ${pose(lifted(7), 72, 1, 1.08, 1.08)} animation-timing-function: linear; }`,
    `  ${at(60)}% { ${pose(lifted(8), 90, 0, 1.1, 1.1)} animation-timing-function: linear; }`,
    `  ${at(65)}% { ${pose(lifted(8), 108, -1, 1.1, 1.1)} animation-timing-function: ${MOTION_EASINGS.out}; }`,
    `  ${at(76)}% { ${pose(lifted(5), 176, -1, 1.06, 1.06)} animation-timing-function: ${MOTION_EASINGS.in}; }`,
    `  ${at(82)}% { ${pose(`calc(${pile} + 1px)`, 180, -1, 1.05, .92)} animation-timing-function: ${MOTION_EASINGS.out}; }`,
    `  ${at(91)}% { ${pose(pile, 180, -1, .98, 1.03)} animation-timing-function: ${MOTION_EASINGS.inOut}; }`,
    // Face-up, settled, still lying on its host: read before the eating starts.
    `  42% { ${pose(pile, 180, -1, 1, 1)} }`,
    // Held while the host sags under it and lurches up in the grip.
    `  60% { ${pose(pile, 180, -1, 1, 1)} }`,
    `  77% { ${pose('0px', 180, -1.14, 1.16, 1.16)} }`,
    `  87% { ${pose('0px', 180, -.8, 1.08, 1.08)} }`,
    `  94% { ${pose('0px', 180, -1.04, 1.02, 1.02)} }`,
    `  100% { ${pose('0px', 180, -1, 1, 1)} }`,
  ].join('\n');
}

/**
 * Frozen (02 §3.4): an 8×8 pixel ice triangle for each corner of the art
 * window, ice-300 with an ice-600 hypotenuse. `flipX`/`flipY` turn the top-left
 * one into the other three.
 */
function iceCorner(flipX: boolean, flipY: boolean): string {
  const cells = (edge: boolean): string => {
    const rects: string[] = [];
    for (let y = 0; y < 8; y += 1) {
      for (let x = 0; x < 8; x += 1) {
        const sum = x + y;
        if (edge ? sum !== 7 : sum >= 7) continue;
        rects.push(`M${flipX ? 7 - x : x} ${flipY ? 7 - y : y}h1v1h-1z`);
      }
    }
    return rects.join('');
  };
  const svg = "<svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' shape-rendering='crispEdges'>"
    + `<path fill='${COLORS.freeze}' d='${cells(false)}'/><path fill='${COLORS.freezeEdge}' d='${cells(true)}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
const ICE_CORNERS = [
  `${iceCorner(false, false)} left top / 8px 8px no-repeat`,
  `${iceCorner(true, false)} right top / 8px 8px no-repeat`,
  `${iceCorner(false, true)} left bottom / 8px 8px no-repeat`,
  `${iceCorner(true, true)} right bottom / 8px 8px no-repeat`,
].join(',\n    ');

/**
 * The night table's pixel grid (design system 01 §8): one 2×2 dot of
 * `tableDot` every 32px, a static tile. It never animates.
 */
const TABLE_DOTS = (() => {
  const svg = "<svg xmlns='http://www.w3.org/2000/svg' width='32' height='32' shape-rendering='crispEdges'>"
    + `<rect width='2' height='2' fill='${COLORS.tableDot}'/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
})();

export const CARD_DETAIL_DIALOG_SCALE = 1.5;
export const CARD_DETAIL_DIALOG_MOBILE_SCALE = 1.2;

/** Breathing room the lobby deck plaque keeps around its glyph-strip row. */
const LOBBY_DECK_PLAQUE_PADDING_BLOCK = 10;
/** stroke-l: the plaque is a secondary button-plaque (02 §9). */
const LOBBY_DECK_PLAQUE_BORDER = 3;
/** Fixed plaque height — see the `.lobby-deck-plaque` rule for why. */
export const LOBBY_DECK_PLAQUE_HEIGHT = DECK_GLYPH_ROW_HEIGHT
  + 2 * (LOBBY_DECK_PLAQUE_PADDING_BLOCK + LOBBY_DECK_PLAQUE_BORDER);
/**
 * Height reserved for the part of the lobby panel that swaps when matchmaking
 * starts — see the `.lobby-stack` rule. Measured from the taller layout, the
 * search card: its heading carries a kicker plus a title where idle has only the
 * logo, and its status line wraps to two lines on narrow screens. 214px is the
 * tallest that layout gets from 320px up, so neither state has to grow.
 */
export const LOBBY_STACK_HEIGHT = 214;

/**
 * U4 static surface styles. They are assembled into KIT_CSS and injected once
 * by main.tsx; feature components no longer mount per-render style tags.
 */
export const GLOBAL_BASE_CSS = `
:root {
  ${beatVars(TURN_PLAYBACK_TIMING)}
}
/* ×2 playback (03 §5): the same table playbackSpeed.ts waits on, stamped on
   <html> so portalled overlays follow it too. Reduced motion needs no table of
   its own: every animation is already collapsed to a frame. */
:root[data-playback-speed="2"] {
  ${beatVars(playbackTiming(2))}
}
/* Per-frame interaction variables, declared non-inherited.
 *
 * These eleven (plus the two hover-tilt ones) are rewritten on every animation
 * frame of a drag by applyCardDragMotion/applyCardPointerMotion. An ordinary
 * custom property inherits, so each write invalidates the style of the element's
 * WHOLE subtree — and a card's subtree is a pixel-art glyph of up to four hundred
 * <rect> nodes. On a phone that was a few hundred style recalcs per frame per
 * dragged card: the freeze players felt while moving a card.
 *
 * Only the element itself reads these (the transform chain, transform-origin),
 * so switching off inheritance costs nothing visually and confines each write to
 * one element. The glare variables are NOT here: the glare is the ::after pseudo,
 * and a pseudo-element only sees its element's custom properties by inheriting
 * them — registered non-inherited, the sheen sat frozen in the corner and the
 * drag glare at full strength (fixed in DS7). syntax "*" keeps them untyped on
 * purpose: a typed syntax would make them interpolable, and any transition
 * touching them would then start easing the drag position instead of tracking
 * the finger.
 */
@property --cards-drag-x { syntax: '*'; inherits: false; }
@property --cards-drag-y { syntax: '*'; inherits: false; }
@property --cards-drag-rotate-x { syntax: '*'; inherits: false; }
@property --cards-drag-rotate-y { syntax: '*'; inherits: false; }
@property --cards-drag-rotate-z { syntax: '*'; inherits: false; }
@property --cards-drag-skew-x { syntax: '*'; inherits: false; }
@property --cards-drag-skew-y { syntax: '*'; inherits: false; }
@property --cards-drag-scale-x { syntax: '*'; inherits: false; }
@property --cards-drag-scale-y { syntax: '*'; inherits: false; }
@property --cards-drag-origin-x { syntax: '*'; inherits: false; }
@property --cards-drag-origin-y { syntax: '*'; inherits: false; }
@property --cards-motion-rotate-x { syntax: '*'; inherits: false; }
@property --cards-motion-rotate-y { syntax: '*'; inherits: false; }
/* Same reasoning for the sheet height the sandbox gesture writes per frame: only
 * .lab-sandbox-drawer reads it, and its subtree is the entire card catalog. */
@property --lab-sandbox-sheet-h { syntax: '*'; inherits: false; }
/* The sandbox's fly-home ghost writes these per frame too (see Sandbox.tsx), and
 * its subtree is a whole passive card. Only the ghost element itself reads them,
 * so nothing below has to be recalculated when one changes. */
@property --cards-return-w { syntax: '*'; inherits: false; }
@property --cards-return-h { syntax: '*'; inherits: false; }
@property --cards-return-gap { syntax: '*'; inherits: false; }
@property --cards-return-shadow { syntax: '*'; inherits: false; }
/* The battle flight's two fixed face boxes (data-return-morph). These have to
 * inherit — the faces are children of the ghost and size themselves off them —
 * and they can afford to: the source pair is written once, at mount, and the
 * destination pair only when the destination is re-measured to a different size,
 * which happens a handful of times per flight rather than sixty times a second.
 * The per-frame work moved to inline transform/opacity on the faces themselves,
 * which reaches no other element at all. */
@property --cards-return-src-w { syntax: '*'; inherits: true; }
@property --cards-return-src-h { syntax: '*'; inherits: true; }
@property --cards-return-dst-w { syntax: '*'; inherits: true; }
@property --cards-return-dst-h { syntax: '*'; inherits: true; }
button, input, select, textarea { font-family: inherit; }
button, [role="button"] { -webkit-tap-highlight-color: transparent; }
/* The table every user screen lies on (01 §8): the night arcade hall — a flat
   indigo with its pixel dot grid. The variable keeps its old name: «сукно»
   (felt) is the docs' word for the table surface. The grid is pinned to the
   viewport: each screen paints the felt on its own box, and the full-screen
   loader starts at the top edge while a screen starts under the menu, so a
   grid anchored to the box jumped by the menu's height on every swap. */
:root {
  --cards-felt: ${TABLE_DOTS} fixed, var(--c-table-bg);
  /* The empty slot's dash (02 §4.3, 05 §4): a free cell, deck slot, passive
     slot or reward slot is the same faint cream dash; only its width follows
     the slot's size (stroke-l large, stroke-m small). */
  --cards-slot-dash: color-mix(in srgb, var(--c-text-on-dark) 16%, transparent);
}
/* Keyboard focus is visible on every interactive element (01 §13): a gold ring
   outside the object, its resting look otherwise unchanged. Fields show focus
   with their own gold rim instead (02 §2.6). */
:where(a[href], button, summary, [role="button"], [tabindex]:not([tabindex="-1"])):focus-visible {
  outline: var(--stroke-l) solid var(--c-selection);
  outline-offset: var(--stroke-l);
}
.cards-game-view, .cards-game-view * {
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
}
.cards-game-view .cards-event-log,
.cards-game-view .cards-event-log * {
  -webkit-user-select: text;
  user-select: text;
  -webkit-touch-callout: default;
}
.cards-status {
  min-height: calc(100vh - var(--cards-beta-ticker-reserve));
  box-sizing: border-box;
  display: grid;
  place-items: center;
  padding: var(--space-6);
  background: var(--cards-felt);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
}
.cards-board-scroll,
.cards-hand-list,
.cards-event-log {
  scrollbar-color: var(--c-panel-raised) transparent;
  scrollbar-width: thin;
}
/* The turn HUD (02 §5.3, Q3): a slate plate with the round, the four steps of
   the turn and, during playback, «ПРОПУСТИТЬ». Until the battle layout is redone
   (DS4b) it lies over the left end of the opponent's queue rail — the rail's
   25px height and corner — instead of taking a row of its own, so the board
   keeps its geometry; only the skip button takes the pointer. */
.cards-playback-hud {
  position: absolute;
  z-index: 20;
  top: var(--cards-board-padding, 12px);
  left: var(--cards-board-padding, 12px);
  display: flex;
  align-items: center;
  gap: var(--space-3);
  height: 25px;
  box-sizing: border-box;
  padding: 0 var(--space-3);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xl);
  background: var(--c-panel-bg);
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
  pointer-events: none;
  white-space: nowrap;
}
.cards-playback-hud-round { color: var(--c-selection); font: var(--type-display-2xs); line-height: 1; text-transform: uppercase; }
.cards-playback-hud-phases { display: flex; gap: 2px; margin: 0; padding: 0; list-style: none; }
.cards-playback-hud-phase {
  padding: 3px var(--space-1);
  border-radius: var(--radius-xs);
  color: var(--c-text-disabled);
  font: var(--type-display-3xs);
  line-height: 1;
  text-transform: uppercase;
}
.cards-playback-hud-phase[data-state="done"] { color: var(--c-text-muted-on-dark); }
.cards-playback-hud-phase[data-state="current"] { background: var(--c-panel-raised); color: var(--c-selection); }
/* Narrow screens: the plate keeps the round and the current step only, so it
   never reaches the queue's centred orbs. */
@media (max-width: 1100px) {
  .cards-playback-hud-phase:not([data-state="current"]) { display: none; }
}
@media (max-width: 620px) {
  .cards-playback-hud { gap: var(--space-2); padding: 0 var(--space-2); }
}
.cards-card-glyph,
.cards-deck-passive-glyph,
.cards-ai-glyph {
  display: block;
  flex: 0 0 auto;
  image-rendering: pixelated;
}
/* Pixel art scales by whole pixels only (01 §10). AuthoredGlyph snaps its own
   size; CSS that resizes a glyph rounds down to --cards-glyph-step, the glyph's
   size at 1×: 20px for the card art, 9px for the older 9×9 glyphs. */
svg[data-grid] { --cards-glyph-step: 20px; }
svg[data-grid="9"] { --cards-glyph-step: 9px; }
/* The deck passive (02 §3.6): its glyph, name and rule. The full plate is a
   slate panel framed in gold — the passive is chosen, not dealt. */
.cards-deck-passive-details {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
  color: var(--c-text-on-dark);
}
.cards-deck-passive-details-comfortable {
  padding: var(--space-3);
  border: var(--stroke-m) solid var(--c-selection);
  border-radius: var(--radius-m);
  background: var(--c-panel-bg);
}
/* The compact plaque (02 §3.6) — the player panel, the lobby deck list, replay:
   a gold stroke-m frame, radius-m, kept tight so the panel row does not grow. */
.cards-deck-passive-details-compact {
  gap: 6px;
  padding: 0 var(--space-1);
  border: var(--stroke-m) solid var(--c-selection);
  border-radius: var(--radius-m);
}
.cards-deck-passive-copy { min-width: 0; }
.cards-deck-passive-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}
.cards-deck-passive-heading strong {
  overflow: hidden;
  font: var(--type-text-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cards-deck-passive-description {
  display: block;
  margin-top: var(--space-1);
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-xs);
  font-weight: 500;
}
.cards-deck-passive-details-compact .cards-deck-passive-description {
  overflow: hidden;
  margin-top: 1px;
  font: var(--type-text-xs);
  font-weight: 500;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cards-scrolling-card-name {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
}
.cards-scrolling-card-name-track {
  display: inline-flex;
  gap: 18px;
  width: max-content;
  min-width: 100%;
}
.cards-card-hover-popover {
  position: fixed;
  z-index: 150;
  transform: scale(var(--cards-hover-popover-scale, 1));
  transform-origin: top left;
  animation: cards-hover-popover-in var(--dur-base) var(--ease-overshoot);
  /* Interactive so the pointer can travel onto it and click (e.g. to expand a
     keyword). A hover bridge keeps it open while the pointer is over it. The
     content is the fixed catalog-card footprint; long text scrolls inside it.
     A cell-state companion card sits to the right of the main card. */
  pointer-events: auto;
  display: flex;
  gap: 8px;
  align-items: stretch;
}
/* Each preview card takes an equal share of the fixed popover width so the
   battle hover matches the catalog/editor card footprint exactly. */
.cards-card-hover-popover > .cards-catalog-card { flex: 1 1 0; min-width: 0; }
.cards-card-hover-popover[data-closing="true"] {
  pointer-events: none;
  animation: cards-hover-popover-out var(--dur-quick) var(--ease-in) forwards;
}
@media (prefers-reduced-motion: reduce) {
  .cards-card-hover-popover { animation: none; }
  .cards-card-hover-popover[data-closing="true"] { opacity: 0; }
}
/* Battle/dialog previews reuse the exact catalog card at its fixed footprint,
   then magnify the whole dialog uniformly so type, glyphs and actions all grow
   together. The inverse viewport bounds keep the transformed result on-screen. */
/* Viewport units, not percentages: the overlay is an auto-sized grid track, so a
   percentage here resolves against the card's intrinsic text width and the dialog
   balloons to near-fullscreen on wordy cards. */
.cards-card-detail-dialog {
  width: min(${CATALOG_CARD_WIDTH}px, calc((100vw - 32px) / ${CARD_DETAIL_DIALOG_SCALE}));
  max-height: calc((100dvh - 32px) / ${CARD_DETAIL_DIALOG_SCALE});
  overflow-y: auto;
  transform: scale(${CARD_DETAIL_DIALOG_SCALE});
  transform-origin: center;
  /* Dedicated enter so the keyframe ends exactly on the static scale(1.5) —
     a generic modal-dialog enter would drop the magnify mid-animation. */
  animation: cards-card-detail-dialog-in var(--dur-base) var(--ease-overshoot);
}
/* Drops in like every modal panel (03 §3.4): from 16px above, on overshoot. The
   translate comes first so the drop is 16 screen pixels, not 16 magnified ones. */
@keyframes cards-card-detail-dialog-in {
  from { opacity: 0; transform: translateY(-16px) scale(${CARD_DETAIL_DIALOG_SCALE}); }
  to { opacity: 1; transform: translateY(0) scale(${CARD_DETAIL_DIALOG_SCALE}); }
}
@keyframes cards-card-detail-dialog-mobile-in {
  from { opacity: 0; transform: translateY(-16px) scale(${CARD_DETAIL_DIALOG_MOBILE_SCALE}); }
  to { opacity: 1; transform: translateY(0) scale(${CARD_DETAIL_DIALOG_MOBILE_SCALE}); }
}
/* Inside a closing ghost (Modal.tsx clone) the enter must not replay; the
   static scale(1.5) stays and the ghost's overlay fade carries the exit. */
.cards-kit-modal-overlay[data-closing="true"] .cards-card-detail-dialog { animation: none; }
@media (prefers-reduced-motion: reduce) {
  .cards-card-detail-dialog { animation: none; }
}
/* With a cell-state companion the dialog widens to two cards side by side. */
.cards-card-detail-dialog-wide {
  width: min(${CATALOG_CARD_WIDTH * 2 + 8}px, calc((100vw - 32px) / ${CARD_DETAIL_DIALOG_SCALE}));
}
.cards-card-detail-body { display: flex; gap: 8px; align-items: stretch; }
.cards-card-detail-body > .cards-catalog-card { flex: 1 1 0; min-width: 0; }
.cards-card-detail-dialog .cards-catalog-card { width: 100%; margin: 0 auto; }
.cards-card-detail-actions {
  /* The dialog column is one catalog-card wide, so actions stack vertically. */
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
}
.cards-card-detail-actions .cards-kit-button { width: 100%; }
.cards-card-detail-actions-inline {
  flex-direction: row;
  gap: var(--space-2);
}
/* The dialog magnifies its card (×1.5 / ×1.2), so its action pair is drawn at
   the kit's compact size (.cards-kit-button--compact, K2) and grows with the
   card; compact never takes the pixel face, whatever size the caller asked. */
.cards-card-detail-actions-inline .cards-kit-button { width: auto; }
.cards-card-detail-actions-inline .cards-kit-button.cards-kit-button--display {
  font: var(--type-text-s);
  font-weight: 600;
  text-transform: none;
}
.cards-card-detail-close.cards-kit-button { flex: 1; }
.cards-card-detail-actions-inline .cards-card-detail-close.cards-kit-button { flex: 1 1 0; }

.cards-target-picker {
  width: min(520px, calc(100vw - 32px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  box-sizing: border-box;
  /* A choice during play (02 §7): the modal panel of 02 §2.5 — radius-2xl,
     step-4, space-6 inside — with option plaques. */
  padding: var(--space-6);
  --cards-panel-radius: var(--radius-2xl);
  --cards-panel-step: var(--step-4);
  color: var(--c-text-on-dark);
  font: var(--type-text-s);
}
.cards-target-picker h2 {
  margin: 0;
  font: var(--type-display-s);
  text-shadow: var(--text-outline-1);
  text-transform: uppercase;
}
.cards-target-picker-description {
  margin: var(--space-2) 0 var(--space-3);
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-s);
}
.cards-target-picker-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--space-2);
}
/* An option is a secondary plaque; the chosen one gets the gold frame and its
   order number on a gold tag. */
.cards-target-picker-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
  min-height: 42px;
  padding: 7px 9px;
  border: var(--stroke-l) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-panel-raised);
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
  color: var(--c-text-on-dark);
  cursor: pointer;
  font: var(--type-text-s);
  font-weight: 600;
  text-align: left;
}
.cards-target-picker-option[aria-pressed="true"] {
  border-color: var(--c-selection);
  color: var(--c-text-on-dark);
}
.cards-target-picker-option:disabled { cursor: not-allowed; opacity: .5; box-shadow: none; }
.cards-target-picker-position {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
  min-width: 20px;
  height: 20px;
  box-sizing: border-box;
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  background: var(--c-energy);
  color: var(--c-text-on-energy);
  font: var(--type-display-3xs);
}
.cards-target-picker-status {
  margin-top: var(--space-3);
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-s);
}
.cards-target-picker-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin-top: var(--space-3);
}

.cards-game-view {
  display: flex;
  height: calc(100vh - var(--cards-beta-ticker-reserve));
  max-height: calc(100vh - var(--cards-beta-ticker-reserve));
  overflow: hidden;
  /* The match lies on the same night table as every other screen (01 §8). */
  background: var(--cards-felt);
  color: var(--c-text-on-dark);
  /* Reading text in the match is the reading face (01 §3.1); numbers and labels
     set the pixel face themselves. */
  font-family: var(--font-text);
}
.cards-game-view-error { padding: 16px; }
.cards-game-main {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px;
  overflow: hidden;
}
.cards-player-scoreboard {
  display: grid;
  flex: 0 0 auto;
  overflow: hidden;
  /* A bare holder for the panel, which draws its own plate (02 §5.1). The
     transparent 1px border keeps the battle's measured geometry. */
  border: 1px solid transparent;
  border-radius: var(--radius-xl);
  background: transparent;
}
.cards-board-stage {
  width: 100%;
  min-width: 0;
  /* Never shrinks (flex-shrink 0) so the board is never cut; it grows to fill
     the space between the scoreboard and the bottom controls and centres the
     board within it. When space is tight the hand shrinks/scrolls instead. */
  flex: 1 0 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: safe center;
  overflow: hidden;
}
.cards-match-result-overlay {
  position: fixed;
  z-index: 90;
  inset: 0;
  display: grid;
  place-items: center;
  /* The match result (02 §8): a scrim over the whole match and a banner panel. */
  background: var(--c-scrim);
  pointer-events: none;
}
.cards-match-result-banner {
  min-width: 280px;
  padding: 22px 30px;
  --cards-panel-radius: var(--radius-2xl);
  --cards-panel-step: var(--step-4);
  color: var(--cards-match-result-color);
  text-align: center;
  animation: cards-match-result var(--dur-emphasis) var(--ease-overshoot);
}
/* The banner lands with two held frames of white outline (03 §4.14). */
.cards-match-result-banner { position: relative; }
.cards-match-result-banner::after {
  content: "";
  position: absolute;
  inset: calc(var(--stroke-l) * -2);
  border: var(--stroke-l) solid var(--c-combat-core);
  border-radius: calc(var(--radius-2xl) + var(--stroke-l));
  pointer-events: none;
  opacity: 0;
  animation: cards-cue-ring var(--dur-emphasis) step-end var(--dur-emphasis);
}
/* A loss shudders once after it lands. */
.cards-match-result-banner[data-outcome="lose"] {
  animation:
    cards-match-result var(--dur-emphasis) var(--ease-overshoot),
    cards-match-result-shudder var(--dur-base) step-end var(--dur-emphasis);
}
@keyframes cards-match-result-shudder {
  0% { translate: -4px 0; }
  25% { translate: 4px 0; }
  50% { translate: -2px 0; }
  75% { translate: 2px 0; }
  100% { translate: 0 0; }
}
/* A win's confetti (03 §4.14): 24 pixel squares in gold, red, blue and cream
   thrown out of the banner, falling in held two-frame steps. */
.cards-match-confetti {
  position: absolute;
  z-index: 2;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}
.cards-match-confetti > span {
  position: absolute;
  top: 38%;
  width: 6px;
  height: 6px;
  background: var(--c-selection);
  opacity: 0;
  animation: cards-confetti-fall var(--dur-celebrate) steps(36, end) forwards;
}
.cards-match-confetti > span:nth-child(4n + 2) { background: var(--c-attack); }
.cards-match-confetti > span:nth-child(4n + 3) { background: var(--c-health); }
.cards-match-confetti > span:nth-child(4n) { background: var(--c-text-on-dark); }
${Array.from({ length: 24 }, (_, index) => {
  const left = 30 + (index * 37) % 41;
  const drift = ((index * 29) % 15) - 7;
  const lag = (index * 7) % 6;
  const size = index % 3 === 0 ? 'width: 4px; height: 4px; ' : '';
  return `.cards-match-confetti > span:nth-child(${index + 1}) { left: ${left}%; --cards-confetti-x: ${drift * 6}px; ${size}animation-delay: calc(var(--dur-celebrate) * ${lag} / 24); }`;
}).join('\n')}
@keyframes cards-confetti-fall {
  0% { opacity: 1; transform: translate(0, 0); }
  15% { opacity: 1; transform: translate(calc(var(--cards-confetti-x) * .4), -40px); }
  85% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--cards-confetti-x), 46vh); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-match-confetti { display: none; }
  .cards-match-result-banner::after { animation: none; }
}
/* Win: gold on a red step; loss: red on ink; draw: cream on slate. The 3px
   outline is spelled out here: --text-outline-3 resolves its step on :root. */
.cards-match-result-title {
  font: var(--type-display-xl);
  text-shadow: ${RESULT_TITLE_OUTLINE};
  text-transform: uppercase;
}
.cards-match-result-detail {
  margin-top: var(--space-3);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
}
.cards-match-result-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 18px;
  pointer-events: auto;
}
.cards-match-result-action-error {
  margin-top: var(--space-3);
  color: var(--c-text-negative-on-dark);
  font: var(--type-text-s);
}
/* During playback the banner is the HUD's right end: it announces the beat to
   assistive technology (the step strip already shows it) and holds the skip
   button. The flying phase banner of 03 §4.1 arrives with DS5. */
.cards-turn-playback-banner {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  pointer-events: auto;
}
.cards-turn-playback-banner > span {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}
/* Compact secondary plaques inside the plate (02 §5.3): the speed switch and
   the skip. ×2 lit in gold while it is on. */
:is(.cards-turn-playback-skip, .cards-turn-playback-speed).cards-kit-button {
  min-height: 19px;
  padding: 0 var(--space-2);
  border-width: var(--stroke-m);
  border-radius: var(--radius-m);
  font: var(--type-display-3xs);
  line-height: 1;
}
/* Transient text notices stay off the table, as before the HUD came back. */
.cards-playback-notice { display: none; }
.cards-turn-playback-speed[aria-pressed="true"] { color: var(--c-selection); }
/* A new round hops into the HUD (03 §4.1): the number remounts with its round. */
.cards-playback-hud-round { animation: cards-hud-round-in var(--dur-move) var(--ease-overshoot); }
@keyframes cards-hud-round-in {
  from { transform: scale(.6); }
}
/* The phase plate flying over the field (03 §4.1), ×1 only: in from the left
   on the overshoot, held, out to the right — over the beat that is already
   running, so it never lengthens the turn. Centred by translate, so the flight
   owns transform. Under reduced motion the lit step in the HUD says it alone. */
.cards-phase-banner {
  --cards-phase-tone: var(--c-selection);
  position: absolute;
  z-index: 30;
  top: 50%;
  left: 50%;
  translate: -50% -50%;
  padding: var(--space-3) var(--space-6);
  color: var(--cards-phase-tone);
  font: var(--type-display-s);
  line-height: 1;
  text-shadow: var(--text-outline-2);
  text-transform: uppercase;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  animation: cards-phase-banner calc(var(--dur-emphasis) * 2 + var(--dur-quick)) linear forwards;
}
.cards-phase-banner[data-phase="effects"] { --cards-phase-tone: var(--c-positive); }
.cards-phase-banner[data-phase="combat"] { --cards-phase-tone: var(--c-attack); }
.cards-phase-banner[data-phase="deaths"] { --cards-phase-tone: var(--c-negative); }
.cards-phase-banner[data-phase="round_end"] { --cards-phase-tone: var(--c-text-muted-on-dark); }
@keyframes cards-phase-banner {
  0% { opacity: 1; transform: translateX(-60vw); animation-timing-function: ${MOTION_EASINGS.overshoot}; }
  42% { opacity: 1; transform: translateX(0); }
  84% { opacity: 1; transform: translateX(0); animation-timing-function: ${MOTION_EASINGS.in}; }
  100% { opacity: 0; transform: translateX(60vw); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-phase-banner { display: none; }
  .cards-playback-hud-round { animation: none; }
}

/* Legacy preset deck selection: seat cards and deck options are panels on the
   felt; the chosen deck wears the gold selection rim. */
.cards-seat-status-card {
  min-width: min(100%, 250px);
  flex: 1 1 250px;
  padding: var(--space-3) var(--space-4);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-panel-raised);
}
.cards-seat-status-card[data-state="disconnected"] { border-color: var(--c-negative); }
.cards-seat-status-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}
.cards-seat-status-label { color: var(--c-text-muted-on-dark); font: var(--type-text-xs); letter-spacing: .06em; text-transform: uppercase; }
.cards-seat-status-detail { margin-top: var(--space-1); color: var(--c-text-on-dark); font: var(--type-text-s); }
.cards-deck-option {
  min-width: 0;
  display: grid;
  grid-template-rows: auto auto auto auto 1fr auto;
  gap: var(--space-3);
  padding: var(--space-5);
}
.cards-deck-option[data-selected="true"] { border-color: var(--c-selection); }
.cards-deck-option[data-coming-soon="true"] { opacity: .68; }
.cards-deck-option-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
}
.cards-deck-option-eyebrow { color: var(--c-text-muted-on-dark); font: var(--type-text-xs); text-transform: uppercase; letter-spacing: .06em; }
.cards-deck-option h2 { margin: var(--space-1) 0 0; color: var(--c-text-on-dark); font: var(--type-text-l); font-weight: 600; }
.cards-deck-option-kicker { color: var(--c-selection); font: var(--type-display-3xs); text-transform: uppercase; }
.cards-deck-option-description { min-height: 58px; margin: 0; color: var(--c-text-muted-on-dark); font: var(--type-text-s); }
.cards-deck-option-traits { display: grid; gap: var(--space-2); margin: 0; font: var(--type-text-s); }
.cards-deck-option-traits dt,
.cards-deck-option-traits dd { display: inline; margin: 0; }
.cards-deck-option-traits dt[data-tone="positive"] { color: var(--c-positive); }
.cards-deck-option-traits dt[data-tone="negative"] { color: var(--c-text-negative-on-dark); }
.cards-deck-composition-label { margin-bottom: var(--space-2); color: var(--c-text-muted-on-dark); font: var(--type-text-xs); text-transform: uppercase; letter-spacing: .06em; }
.cards-deck-composition-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-1) var(--space-3);
  margin: 0;
  padding: 0;
  list-style: none;
}
.cards-deck-composition-card {
  min-width: 0;
  overflow: hidden;
  color: var(--c-text-on-dark);
  cursor: default;
  font: var(--type-text-xs);
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cards-deck-composition-card[data-interactive="true"] { cursor: pointer; }
.cards-deck-composition-card > span { color: var(--c-text-muted-on-dark); font-variant-numeric: tabular-nums; }
.cards-deck-seat-statuses {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  margin-bottom: var(--space-5);
  padding: var(--space-4);
}
.cards-deck-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 300px), 1fr));
  gap: var(--space-4);
}
.cards-deck-selection-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: var(--space-4);
  margin-top: var(--space-5);
  padding: var(--space-4) 0 0;
  border-top: var(--stroke-l) solid var(--c-table-rule);
}
.cards-deck-waiting-status { color: var(--c-text-muted-on-table); font: var(--type-text-s); }

/* The cabinet panel on the felt (05 §3): logo, actions, deck plaque. */
.cards-lobby-panel {
  width: min(560px, 100%);
  max-height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  --cards-panel-radius: var(--radius-2xl);
  --cards-panel-step: var(--step-4);
  padding: var(--space-6);
}

/* Admin (05 §12): the «корпус» — solid slate housing instead of the felt;
   kit components and tokens, gold bars, code face for identifiers. */
/* The admin route mounts no BETA strip, so it takes the full height. */
.activity-admin-screen.cards-kit-screen { height: 100dvh; padding-block: var(--space-8); background: var(--c-housing-bg); }
.activity-admin-panel {
  display: grid;
  gap: var(--space-5);
  width: min(620px, 100%);
  box-sizing: border-box;
  padding: var(--space-6);
  --cards-panel-step: var(--step-4);
}
.activity-admin-login { display: grid; gap: var(--space-3); }
.activity-admin-login > label { color: var(--c-text-muted-on-dark); font: var(--type-text-xs); letter-spacing: .06em; text-transform: uppercase; }
.activity-admin-flags { display: grid; gap: var(--space-3); }
.activity-admin-flag {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-5);
  padding: var(--space-4);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-panel-raised);
}
.activity-admin-flag h2 { margin: 0; font: var(--type-text-m); font-weight: 600; }
.activity-admin-flag p { margin: var(--space-1) 0 0; color: var(--c-text-muted-on-dark); font: var(--type-text-s); }
.activity-admin-switch { min-width: 66px; flex-shrink: 0; }
.activity-admin-analytics { display: grid; gap: var(--space-4); }
.activity-admin-analytics > .cards-kit-panel-header { margin-top: var(--space-2); }
.activity-admin-charts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--space-3);
}
.activity-admin-chart {
  display: grid;
  grid-template-rows: auto 110px auto;
  gap: var(--space-2);
  min-width: 0;
  margin: 0;
  padding: var(--space-3);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-field-bg);
}
.activity-admin-chart figcaption { min-height: 32px; color: var(--c-text-muted-on-dark); font: var(--type-text-xs); }
.activity-admin-chart-bars {
  display: flex;
  align-items: end;
  gap: 2px;
  min-width: 0;
  border-bottom: var(--stroke-m) solid var(--c-panel-edge);
}
.activity-admin-chart-column { display: flex; flex: 1; align-items: end; height: 100%; min-width: 1px; }
.activity-admin-chart-column span { width: 100%; min-height: 3px; background: var(--c-energy); }
.activity-admin-chart-axis { display: flex; justify-content: space-between; color: var(--c-text-muted-on-dark); font: var(--type-text-xs); font-family: var(--font-code); }
.activity-admin-funnel {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-4);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-field-bg);
}
.activity-admin-funnel h2 { margin: 0 0 2px; font: var(--type-text-m); font-weight: 600; }
.activity-admin-funnel-row { display: grid; grid-template-columns: minmax(120px, 1fr) minmax(80px, 2fr) auto; align-items: center; gap: var(--space-3); font: var(--type-text-s); }
.activity-admin-funnel-row > div { height: 8px; overflow: hidden; background: var(--c-panel-raised); }
.activity-admin-funnel-row i { display: block; height: 100%; min-width: 2px; background: var(--c-energy); }
.activity-admin-funnel-row strong { min-width: 2ch; font-family: var(--font-code); text-align: right; }
.activity-admin-ai {
  display: grid;
  gap: var(--space-2);
  padding: var(--space-4);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-field-bg);
}
.activity-admin-ai h2 { margin: 0 0 2px; font: var(--type-text-m); font-weight: 600; }
.activity-admin-ai > div { display: flex; justify-content: space-between; gap: var(--space-3); font: var(--type-text-s); }
.activity-admin-ai span, .activity-admin-ai p { color: var(--c-text-muted-on-dark); }
.activity-admin-ai p { margin: 2px 0 0; font: var(--type-text-xs); font-weight: 500; }
@media (max-width: 500px) {
  .activity-admin-panel { padding: 18px; }
  .activity-admin-flag { align-items: flex-start; flex-direction: column; }
  .activity-admin-switch { align-self: stretch; }
  .activity-admin-charts { grid-template-columns: 1fr; }
  .activity-admin-chart { grid-template-rows: auto 80px auto; }
  .activity-admin-funnel-row { grid-template-columns: minmax(90px, 1fr) minmax(60px, 1fr) auto; gap: 6px; }
}
.lobby-logo { display: block; height: 30px; width: auto; margin: 0 0 12px; }
/* Header, primary actions and deck plaque swap wholesale when a search starts:
   the header trades the logo for a kicker plus a title, and the search card
   replaces both the three buttons and the plaque. Reserving one height for the
   whole stack keeps the centered panel from resizing on the switch, and the
   action row is the growing item, so whichever layout is shorter spends the
   slack inside itself — taller buttons, or a roomier search card. */
.lobby-stack {
  display: flex;
  flex-direction: column;
  min-height: ${LOBBY_STACK_HEIGHT}px;
}
.lobby-actions { display: flex; flex-direction: column; flex-grow: 1; gap: var(--space-3); }
.lobby-actions-idle {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.lobby-actions-idle > :first-child { grid-column: 1 / -1; }
/* One face per row (02 §2.1): «Найти соперника» is too long for the pixel face,
   so the short «Челленджи» beside it stays in the reading face too. */
.lobby-actions-idle .cards-kit-button.cards-kit-button--display {
  font: var(--type-text-s);
  font-weight: 600;
  text-transform: none;
}
/* The idle actions and the search timer are swapped as whole blocks; the
   incoming block fades in so the switch does not read as a hard DOM cut. */
.lobby-actions { animation: cards-screen-enter var(--dur-base) var(--ease-out); }
.lobby-actions-searching { align-items: stretch; }
/* The search card is sunk into the cabinet like a field: a gold pixel timer
   over the status line (05 §3). */
.lobby-search-timer-block {
  display: flex;
  flex-direction: column;
  /* Takes the stack's leftover height and keeps the timer centred in it, so a
     status line that wraps — or the reconnect warning — resizes nothing. */
  flex-grow: 1;
  min-height: 0;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--stroke-l) solid var(--c-panel-edge);
  border-radius: var(--radius-m);
  background: var(--c-field-bg);
  box-shadow: inset 0 var(--step-2) 0 color-mix(in srgb, var(--c-step-shadow) 35%, transparent);
  text-align: center;
}
.lobby-search-timer {
  color: var(--c-selection);
  font: var(--type-display-l);
  font-variant-numeric: tabular-nums;
  text-shadow: var(--text-outline-2);
}
.lobby-search-status { margin: 0; min-height: 20px; color: var(--c-text-muted-on-dark); font: var(--type-text-s); }
.lobby-search-status[data-reconnecting="true"],
.lobby-search-error { color: var(--c-selection); }
.lobby-search-error { display: block; margin-top: var(--space-1); font: var(--type-text-xs); }

/* Selected-deck plaque under the primary actions (02 §9): the kit's secondary
   Button (its look, hover and press), laid out as a row. Its height is fixed at
   the glyph-strip row: the remembered deck resolves over the network, so a
   plaque sized by its content would grow — and shove the centered lobby panel —
   the moment the strip and the deck name replace the "выберите колоду" line.
   Both labels stay on one line for the same reason. */
.lobby-deck-plaque.cards-kit-button {
  justify-content: flex-start;
  gap: var(--space-3);
  width: 100%;
  height: ${LOBBY_DECK_PLAQUE_HEIGHT}px;
  margin-top: var(--space-3);
  padding: 0 var(--space-4);
  text-align: left;
}
.lobby-deck-plaque-name,
.lobby-deck-plaque-empty {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.lobby-deck-plaque-name { font-weight: 600; }
.lobby-deck-plaque-empty { color: var(--c-text-muted-on-dark); }
.lobby-deck-plaque-hint {
  color: var(--c-selection);
  font: var(--type-display-2xs);
  text-transform: uppercase;
}
/* On phones two glyph windows are enough; the name keeps the width. */
@media (max-width: 620px) {
  .lobby-deck-plaque .deck-glyph-strip-item:nth-child(n + 3) { display: none; }
}
/* Deck glyph strip (02 §9): each glyph in its own light art window. */
.deck-glyph-strip { display: inline-flex; gap: var(--space-1); flex-shrink: 0; }
.deck-glyph-strip-item {
  display: inline-flex;
  padding: ${DECK_GLYPH_ITEM_PADDING}px;
  border: ${DECK_GLYPH_ITEM_BORDER}px solid var(--c-outline);
  border-radius: var(--radius-xs);
  background: var(--c-card-art-window);
  box-shadow: inset 0 var(--step-1) 0 color-mix(in srgb, var(--c-step-shadow) 12%, transparent);
  line-height: 0;
}

/* Deck selection uses a compact, scroll-bounded body. */
.lobby-deck-modal {
  display: flex;
  flex-direction: column;
  width: min(560px, 100%);
  max-height: min(80vh, 640px);
}
.lobby-deck-modal-body {
  overflow-y: auto;
  max-height: 60vh;
  margin-top: 12px;
}
/* The body height is FIXED (not content-driven): the dialog opens at its
   final size and stays there whether the API returns 0 decks or 20 — the
   list scrolls inside. A content-driven height jumped on load (user report). */
.lobby-deck-modal-body { height: min(40vh, 300px); min-height: 172px; }
.lobby-deck-modal-row-skeleton { border-bottom-color: transparent; }
.lobby-deck-modal-skeleton-glyphs,
.lobby-deck-modal-skeleton-name,
.lobby-deck-modal-skeleton-action {
  border-radius: var(--radius-xs);
  background: color-mix(in srgb, var(--c-text-muted-on-dark) 16%, transparent);
  animation: cards-skeleton-pulse var(--dur-loader) var(--steps-blink) infinite;
}
.lobby-deck-modal-skeleton-glyphs { width: 92px; height: 28px; flex-shrink: 0; }
.lobby-deck-modal-skeleton-name { height: 13px; max-width: 180px; }
.lobby-deck-modal-skeleton-action { width: 74px; height: 26px; flex-shrink: 0; }
@keyframes cards-skeleton-pulse {
  50% { opacity: .45; }
}
.lobby-deck-modal-list { list-style: none; margin: 0; padding: 0; }
.lobby-deck-modal-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 4px;
  border-bottom: var(--stroke-s) solid var(--c-panel-edge);
}
.lobby-deck-modal-row-main { flex: 1; min-width: 0; }
.lobby-deck-modal-row-name { font-weight: 700; }
.lobby-deck-modal-empty,
.lobby-deck-modal-status { padding: 16px 4px; color: var(--c-text-muted-on-dark); }
.lobby-deck-modal-footer { margin-top: 12px; }

.cards-deck-viewer {
  width: min(760px, 100%);
  max-height: 100%;
  overflow-y: auto;
}
.cards-deck-viewer-empty { padding: 34px 10px; color: var(--c-text-muted-on-dark); text-align: center; }
/* Tracks, footprint and proportions come from the shared catalog grid so a deck
   card in battle is the exact same tile as in the card catalog. */
.cards-deck-viewer-cards { margin-top: 16px; }
.cards-deck-viewer-card { position: relative; min-width: 0; }
.cards-replay-decks {
  position: fixed;
  z-index: 120;
  right: 16px;
  bottom: 16px;
  left: 16px;
  max-height: min(46vh, 520px);
  overflow: auto;
  box-sizing: border-box;
  padding: var(--space-3);
  --cards-panel-step: var(--step-4);
  color: var(--c-text-on-dark);
  font: var(--type-text-s);
}
.cards-replay-decks > h2 { margin: 0 0 var(--space-3); color: var(--c-selection); font: var(--type-display-2xs); text-transform: uppercase; }
.cards-replay-decks-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 14px; }
.cards-replay-deck { min-width: 0; }
.cards-replay-deck > h3 { margin: 0 0 var(--space-2); color: var(--c-text-rival-on-dark); font: var(--type-text-s); font-weight: 600; }
.cards-replay-deck[data-viewer="true"] > h3 { color: var(--c-text-on-dark); }
.cards-replay-deck-list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 5px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.cards-replay-deck-card {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  padding: var(--space-1) var(--space-2);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-zone-bg);
  font: var(--type-text-xs);
}
.cards-replay-deck-position { color: var(--c-text-muted-on-dark); font-variant-numeric: tabular-nums; }
.cards-replay-deck-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cards-event-log {
  /* Turn-log UI is disabled for now (kept in the DOM for tests/data). */
  display: none;
  position: sticky;
  top: 0;
  width: 240px;
  height: 100%;
  flex-shrink: 0;
  overflow-y: scroll;
  box-sizing: border-box;
  border-left: var(--stroke-s) solid var(--c-panel-edge);
  padding: var(--space-2);
  font: var(--type-text-xs);
  scrollbar-gutter: stable;
  user-select: text;
  -webkit-user-select: text;
  -webkit-touch-callout: default;
}
.cards-event-log-game-id {
  margin: -8px -8px 9px;
  padding: 9px 8px;
  border-bottom: var(--stroke-s) solid var(--c-panel-edge);
  background: var(--c-field-bg);
  color: var(--c-text-on-dark);
  font: var(--type-text-xs);
  font-family: var(--font-code);
  overflow-wrap: anywhere;
}
.cards-event-log-entry { margin-bottom: 6px; color: var(--cards-event-color); line-height: 1.4; }
.auth-account-panel {
  position: fixed;
  z-index: 30;
  top: 38px;
  right: 12px;
  max-width: 360px;
  padding: var(--space-3);
  color: var(--c-text-on-dark);
  font: var(--type-text-s);
}
.deck-note.deck-note-intro { margin-bottom: 18px; }
.deck-picker-footer { margin-top: 22px; }
.deck-picker-actions { display: flex; justify-content: space-between; flex-wrap: wrap; gap: 10px; }
.deck-picker-footer-end { display: flex; justify-content: flex-end; }

/* U6: static board/hand presentation. Live geometry remains instance-owned. */
/* The player panel (02 §5.1): a slate panel in an ink outline — the rival's
   pink on the opponent's — with a step. The outline is drawn inward over the
   old 3px left border, so the panel keeps its height and the board its room
   until DS4b. A player has no colour of their own (01 §2.3). */
.cards-player-panel {
  --cards-panel-edge: var(--c-rival);
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  border-left: var(--stroke-l) solid var(--cards-panel-edge);
  border-radius: var(--radius-xl);
  padding: 5px 14px;
  outline: var(--stroke-l) solid var(--cards-panel-edge);
  outline-offset: calc(var(--stroke-l) * -1);
  background: var(--c-panel-bg);
  box-shadow: var(--cards-panel-ring, 0 0 transparent), 0 var(--step-3) 0 var(--c-step-shadow);
}
.cards-player-panel[data-viewer="true"] { --cards-panel-edge: var(--c-outline); }
/* Hack sights on a player, as on a card (04 §1.2): gold for a candidate, the
   rival's pink filled for the pick. */
.cards-player-panel[data-hack-selected="true"] {
  --cards-panel-ring: 0 0 0 var(--stroke-l) var(--c-rival);
}
.cards-player-panel[data-hack-candidate="true"]:not([data-hack-selected="true"]) {
  --cards-panel-ring: 0 0 0 var(--stroke-m) var(--c-selection);
}
.cards-player-panel[data-combat-impact="true"] {
  animation:
    cards-player-hit var(--beat-attack-impact) var(--ease-out),
    cards-player-hit-tone var(--beat-attack-impact) step-end;
}
/* Healed: the health chip bounces (03 §4.12) and the number counts up; the
   panel itself stays put — a shake is for a blow. */
.cards-player-panel[data-player-heal="true"] .cards-player-health > span {
  animation: cards-chip-bump var(--beat-status-change) linear;
}
/* A blow on the player (03 §4.3): the health chip is outlined red for one
   frame while it counts down. Positioned only for the beat, so the chip at
   rest paints exactly as before. */
.cards-player-panel:is([data-combat-impact="true"], [data-player-heal="true"]) .cards-player-health > span { position: relative; }
.cards-player-panel[data-combat-impact="true"] .cards-player-health > span::after {
  content: "";
  position: absolute;
  inset: calc(var(--stroke-m) * -2);
  border: var(--stroke-m) solid var(--c-negative);
  border-radius: var(--radius-s);
  pointer-events: none;
  opacity: 0;
  animation: cards-flash-once var(--beat-attack-impact) step-end;
}
@keyframes cards-flash-once {
  0% { opacity: 1; }
  15%, 100% { opacity: 0; }
}
/* A heal on the player (03 §4.12): «+N» rises over the chip, three green
   pixels lift off it. */
.cards-player-panel[data-player-heal="true"] .cards-player-health { position: relative; }
.cards-player-health-delta {
  position: absolute;
  left: 50%;
  bottom: 100%;
  z-index: 3;
  color: var(--c-positive);
  font: var(--type-display-s);
  line-height: 1;
  text-shadow: var(--text-outline-2);
  pointer-events: none;
  animation: cards-playback-delta var(--beat-status-change) var(--ease-out) forwards;
}
.cards-player-panel[data-player-heal="true"] .cards-player-health > span::before {
  content: "";
  position: absolute;
  left: 30%;
  top: 0;
  width: 3px;
  height: 3px;
  background: var(--c-positive);
  box-shadow: 8px 3px 0 var(--c-positive), 16px -1px 0 var(--c-positive);
  pointer-events: none;
  opacity: 0;
  animation: cards-pixels-rise var(--beat-status-change) step-end forwards;
}
@keyframes cards-pixels-rise {
  0% { opacity: 1; transform: translateY(0); }
  25% { opacity: 1; transform: translateY(-5px); }
  50% { opacity: 1; transform: translateY(-10px); }
  75%, 100% { opacity: 0; transform: translateY(-14px); }
}
/* A card leaving the pile hops the deck count (03 §4.11). */
.cards-player-deck > span[data-drawn="true"] {
  display: inline-block;
  animation: cards-chip-bump var(--beat-card-draw) linear;
}
@media (prefers-reduced-motion: reduce) {
  .cards-player-health-delta { animation: none; transform: translate(-50%, 0); }
  .cards-player-panel[data-player-heal="true"] .cards-player-health > span::before { display: none; }
}
/* What a skipped batch changed keeps a still selection ring for a moment
   (03 §5, MOTION_DURATIONS.afterSkip): the card's ring, the health chip's. */
.cards-board-card[data-skip-changed="true"] {
  --cards-card-ring: 0 0 0 var(--stroke-l) var(--c-selection);
}
.cards-player-panel[data-skip-changed="true"] .cards-player-health > span {
  box-shadow: 0 0 0 var(--stroke-m) var(--c-selection), 0 var(--step-1) 0 var(--c-step-shadow);
}
/* A chip taking a change (03 §4.3): 1 → 1.35 → 1, out of a move. */
@keyframes cards-chip-bump {
  0% { transform: scale(1); animation-timing-function: ${MOTION_EASINGS.out}; }
  35% { transform: scale(1.35); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  100% { transform: scale(1); }
}
.cards-player-identity {
  display: flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.cards-player-passive {
  position: relative;
  min-width: 0;
  max-width: min(280px, 28vw);
  border-radius: var(--radius-s);
}
/* A deck passive just fired: pulse the badge in the passive's own accent
   (--cards-passive-glow, the bright glyph tone) so the trigger reads as coming
   from it. Three intensities keep the five passives one visual family. */
.cards-player-passive[data-passive-pulse] {
  animation:
    cards-passive-pulse var(--beat-status-change) var(--ease-out),
    cards-passive-pulse-tone var(--beat-status-change) step-end;
}
.cards-player-passive[data-passive-pulse="discharge"] {
  animation:
    cards-passive-pulse-strong var(--beat-status-change) var(--ease-out),
    cards-passive-pulse-strong-tone var(--beat-status-change) step-end;
}
.cards-player-passive[data-passive-pulse]::after {
  content: "";
  position: absolute;
  inset: 0;
  border: var(--stroke-l) solid var(--cards-passive-glow, var(--c-selection));
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  animation: cards-cue-ring var(--beat-status-change) step-end;
}
.cards-player-passive[data-passive-pulse="reset"] {
  animation:
    cards-passive-reset var(--beat-status-change) var(--ease-out),
    cards-passive-reset-tone var(--beat-status-change) step-end;
}
.cards-player-passive-delta {
  position: absolute;
  top: -3px;
  right: 2px;
  z-index: 2;
  font: var(--type-display-xs);
  color: var(--cards-passive-glow, var(--c-positive));
  text-shadow: var(--text-outline-1);
  pointer-events: none;
  animation: cards-passive-delta var(--beat-status-change) var(--ease-out) forwards;
}
.cards-player-content {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  gap: 4px 16px;
}
.cards-player-topline {
  display: flex;
  align-items: center;
  flex: 1 1 auto;
  justify-content: flex-start;
  gap: 10px 16px;
  min-width: 0;
}
/* The avatar is the bare seat glyph on the panel, cream for you, pink for the
   rival (01 §10). The dark square it used to sit in read as a hole in the panel. */
.cards-player-avatar {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  box-sizing: border-box;
  width: 34px;
  height: 34px;
  padding: 3px;
  color: var(--c-rival);
}
.cards-player-avatar svg {
  width: 100%;
  height: 100%;
}
.cards-player-panel[data-viewer="true"] .cards-player-avatar { color: var(--c-text-on-dark); }
.cards-player-name-badge {
  flex: 0 1 auto;
  overflow: hidden;
  color: var(--c-text-rival-on-dark);
  font: var(--type-display-xs);
  line-height: 1;
  text-overflow: ellipsis;
  text-transform: uppercase;
  white-space: nowrap;
}
.cards-player-panel[data-viewer="true"] .cards-player-name-badge { color: var(--c-text-on-dark); }
.cards-player-name-badge[data-empty="true"] { flex: 0 0 auto; }
.cards-player-stats {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 6px 16px;
}
.cards-player-health,
.cards-player-energy,
.cards-player-deck {
  min-width: 0;
  white-space: nowrap;
}
/* Health is a blue chip with a pixel heart (02 §5.1). */
.cards-player-health > span {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  box-sizing: border-box;
  height: 30px;
  padding: 0 var(--space-2);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-s);
  background: var(--c-health);
  box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
  color: var(--c-text-on-color);
  font: var(--type-display-m);
  line-height: 1;
  text-shadow: var(--text-outline-1);
}
.cards-player-health .cards-pixel-icon { width: 14px; height: 14px; filter: drop-shadow(0 1px 0 var(--c-outline)); }
/* Energy is a row of 18px coins: lit for what the plan can still spend, a dashed
   ring for what the staged plan holds, slate for what this round used. Past six
   coins the row folds to one coin and the number. */
/* A flex row, not text: the coins centre on the health chip whatever the
   reading face's line box is (they used to ride the line's baseline). */
.cards-player-energy { display: flex; align-items: center; }
.cards-player-energy > span { display: inline-flex; align-items: center; gap: var(--space-1); }
.cards-player-energy-coins { display: inline-flex; gap: 3px; }
.cards-player-energy-coin {
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-round);
  background: var(--c-energy);
  box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
}
.cards-player-energy-coin[data-state="reserved"] {
  border: var(--stroke-m) dashed var(--c-energy);
  background: transparent;
  box-shadow: none;
}
.cards-player-energy-coin[data-state="spent"] { background: var(--c-panel-raised); box-shadow: none; }
/* The number stays in the text for screen readers and tests; it shows only
   once the row has folded. */
.cards-player-energy-value {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}
.cards-player-energy-coins[data-many="true"] + .cards-player-energy-value {
  position: static;
  width: auto;
  height: auto;
  clip-path: none;
  color: var(--c-energy);
  font: var(--type-display-xs);
  text-shadow: var(--text-outline-1);
}
/* The deck is a count, not a resource: quieter, in the pixel face. */
.cards-player-deck { color: var(--c-text-muted-on-dark); font: var(--type-display-xs); }
.cards-player-ready { flex: none; }
/* The secondary counters ride the end of the panel row at their own width.
   A 100% basis (left from when the row wrapped) took the whole panel in the
   one-line row and squeezed the nickname and the passive to a couple of letters. */
.cards-player-secondary {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  gap: 5px 14px;
}
.cards-player-secondary:empty { display: none; }
/* Battle panels keep a passive's compact glyph and title, not its rules text.
   The complete description remains available from the existing hover/tap card. */
.cards-player-panel .cards-deck-passive-description { display: none; }
/* The viewer's count is a button into the remaining-deck browser; it keeps the
   stat metrics rather than the kit's button chrome. */
.cards-player-deck.cards-kit-button {
  min-height: 0;
  border: 0;
  padding: 0;
  background: transparent;
  color: var(--c-text-muted-on-dark);
  font: var(--type-display-xs);
}

.cards-board-scroll {
  min-width: 0;
  max-width: 100%;
  /* Desktop board is sized (via the cell clamp) to fit its column with the
     turn-log gone, so it never scrolls horizontally. */
  overflow-x: hidden;
  overflow-y: hidden;
  touch-action: auto;
  padding-bottom: 4px;
}
.cards-board-field {
  position: relative;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
  padding: var(--cards-board-padding, 12px);
  background: transparent;
}
.cards-spell-queue {
  position: relative;
  z-index: 8;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 42px;
  box-sizing: border-box;
  /* A thin zoneBg rail drawn on the table (02 §6.1). */
  border: 1px solid transparent;
  border-radius: var(--radius-round);
  padding: 3px 9px;
  background: var(--c-zone-bg);
}
.cards-spell-queue[data-side="opponent"] { margin-bottom: 7px; }
.cards-spell-queue[data-side="viewer"] { margin-top: 7px; }
.cards-spell-queue[data-empty="true"] {
  border-style: dashed;
  border-color: var(--cards-slot-dash);
  opacity: .48;
}
.cards-spell-queue[data-empty="true"][data-relevant="true"] { opacity: .7; }
.cards-spell-queue-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-width: 0;
  flex: 1;
}
.cards-spell-orb {
  position: relative;
  z-index: 9;
  display: grid;
  place-items: center;
  width: 34px;
  height: 34px;
  flex: 0 0 34px;
  box-sizing: border-box;
  overflow: visible;
  /* An orb is a spell coin (02 §6.1): the spell face in an ink outline — the
     rival's pink in their queue — with a step, its glyph in a light window. */
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: 50%;
  padding: 2px;
  background: var(--c-card-face-spell);
  color: var(--c-text-on-spell);
  cursor: pointer;
  box-shadow: var(--cards-orb-rim, 0 0 transparent), 0 var(--step-2) 0 var(--c-step-shadow);
  transition: transform var(--dur-quick) var(--ease-out);
  animation: cards-spell-orb-fade-in var(--dur-move) var(--ease-overshoot) both;
}
.cards-spell-queue[data-side="opponent"] .cards-spell-orb { border-color: var(--c-rival); }
.cards-spell-orb:hover {
  box-shadow: var(--cards-orb-rim, 0 0 transparent), 0 calc(var(--step-2) + var(--step-1)) 0 var(--c-step-shadow);
  transform: translateY(calc(var(--step-1) * -1));
}
.cards-spell-orb:disabled { cursor: default; }
/* A virus wears the hazard stripes, a reaction the ice rim (02 §6.1). */
.cards-spell-orb[data-card-type="VIRUS"] {
  background: repeating-linear-gradient(135deg, var(--c-card-rim-structure) 0 3px, var(--c-outline) 3px 6px);
}
.cards-spell-orb[data-card-type="REACTION"] { --cards-orb-rim: inset 0 0 0 var(--stroke-m) var(--c-card-rim-reaction); }
.cards-spell-orb-glyph,
.cards-spell-orb-back {
  position: absolute;
  inset: 2px;
  display: grid;
  place-items: center;
  overflow: hidden;
  border-radius: inherit;
}
.cards-spell-orb-glyph svg { width: 100%; height: 100%; }
/* The glyph sits in a 20px light window: the orb's 30px inside less 5px a side. */
.cards-spell-orb-glyph {
  inset: 5px;
  background: var(--c-card-art-window);
}
.cards-spell-orb-back {
  z-index: 2;
  opacity: 0;
  /* The same back as every card's (02 §6.1): the weave under the gold mark. */
  background: ${pixelPatternDataUri(CARD_BACK_PATTERN, 3)} center / 21px 21px, var(--c-card-back);
  pointer-events: none;
}
.cards-spell-orb-back svg {
  width: max(9px, round(down, 70%, 9px));
  height: max(9px, round(down, 70%, 9px));
}
.cards-spell-orb[data-face-down="true"] .cards-spell-orb-back { opacity: 1; }
.cards-spell-orb[data-face-down="true"] .cards-spell-orb-glyph { opacity: 0; }
.cards-spell-orb[data-revealing="true"] {
  animation: cards-spell-orb-reveal var(--beat-card-reveal) linear both;
}
.cards-spell-orb[data-revealing="true"] .cards-spell-orb-back {
  animation: cards-spell-orb-back-reveal var(--beat-card-reveal) step-end both;
}
.cards-spell-orb[data-exiting="true"] {
  pointer-events: none;
  animation: cards-spell-orb-fade-out var(--dur-quick) var(--ease-in) forwards;
}
/* The orb's mode is the kit's neutral Badge (02 §6.1); here it only sits on
   the orb's corner, a size smaller. */
.cards-spell-orb-mode.cards-kit-badge {
  position: absolute;
  z-index: 3;
  right: -6px;
  bottom: -6px;
  display: block;
  max-width: 64px;
  overflow: hidden;
  padding: 1px 3px;
  font: var(--type-display-3xs);
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cards-spell-fibers {
  position: fixed;
  z-index: 109;
  inset: 0;
  overflow: visible;
  pointer-events: none;
}
/* Plan threads in five pixel colours (02 §6.2); pink is never one of yours. */
.cards-spell-fiber {
  --cards-fiber-color: var(--c-selection);
  opacity: .39;
  transition: opacity var(--dur-quick) var(--ease-out);
}
.cards-spell-fiber[data-color-index="1"] { --cards-fiber-color: var(--c-freeze); }
.cards-spell-fiber[data-color-index="2"] { --cards-fiber-color: var(--c-text-on-dark); }
.cards-spell-fiber[data-color-index="3"] { --cards-fiber-color: var(--c-heat); }
.cards-spell-fiber[data-color-index="4"] { --cards-fiber-color: var(--c-positive); }
/* Hovering either end of a fiber isolates it: the rest recede far enough that
   one thread can be traced across a board carrying five of them. */
.cards-spell-fibers[data-focused="true"] .cards-spell-fiber { opacity: .1; }
.cards-spell-fiber[data-active="true"],
.cards-spell-fibers[data-focused="true"] .cards-spell-fiber[data-active="true"] { opacity: .95; }
.cards-spell-fiber[data-active="true"] path[data-strand="glow"] { stroke-width: 7; opacity: .35; }
.cards-spell-fiber path {
  fill: none;
  stroke: var(--cards-fiber-color);
  stroke-linecap: butt;
  vector-effect: non-scaling-stroke;
}
/* No blurred glow (D67): a second, 7px line of the same colour at 25% under
   the thread. */
.cards-spell-fiber path[data-strand="glow"] {
  stroke-width: 7;
  opacity: .25;
}
/* A thread is a pixel dash 3px wide with dashes in multiples of 3 (02 §6.2). */
.cards-spell-fiber path[data-strand="thread"] {
  stroke-width: 3;
  opacity: .96;
  stroke-dasharray: 6 3;
  animation: cards-spell-fiber-flow var(--dur-flow) linear infinite;
}
.cards-spell-fiber path[data-thread-index="1"] {
  stroke-dasharray: 3 3;
  animation-delay: calc(var(--dur-flow) / -3);
}
.cards-spell-fiber path[data-thread-index="2"] {
  opacity: .78;
  stroke-dasharray: 9 3;
  animation-delay: calc(var(--dur-flow) * -2 / 3);
}
/* Resolution keeps the planning thread rather than replacing it with a bar:
   the fiber grows to the target carrying a spark at its tip, then retracts. */
.cards-spell-fiber[data-variant="cast"] { opacity: .9; }
.cards-spell-fiber[data-variant="cast"] path[data-strand="glow"] {
  stroke-width: 7;
  opacity: .35;
}
.cards-spell-fiber[data-variant="cast"] path[data-strand="thread"] {
  stroke-width: 3;
  stroke-dasharray: 6 3;
  animation-duration: calc(var(--dur-flow) / 4);
}
/* The cast's spark leads a short trail of hard pixels (data-strand="mote",
   placed by FiberOverlay): a crisp core with a white rim instead of a blurred
   glow, so it reads as the same pixel art as the cards it flies between. */
.cards-spell-fiber circle[data-strand="spark"] {
  fill: var(--cards-fiber-color);
  stroke: var(--c-combat-core);
  stroke-width: 1;
}
/* Pixels riding a thread: on a planned spell they flow from the orb to its
   target, so a board full of threads still says which way each one pulls. */
.cards-spell-fiber rect[data-strand="mote"] {
  fill: var(--cards-fiber-color);
  shape-rendering: crispEdges;
}
/* Where a thread catches its target: a white core and four chips thrown off
   it, once (FiberOverlay sets data-latched on the frame the thread arrives —
   for a cast, that is the hit). Positioned on the head by the overlay. */
.cards-spell-fiber g[data-strand="knot"] rect {
  fill: var(--cards-fiber-color);
  opacity: 0;
  shape-rendering: crispEdges;
  transform-box: fill-box;
  transform-origin: center;
}
.cards-spell-fiber g[data-strand="knot"] rect[data-knot="core"] { fill: var(--c-combat-core); }
/* The knot is a 9×9 pixel diamond (02 §6.2): the white core cut to stair-stepped
   rows of 1-3-5-7-9-7-5-3-1, so it reads as a sprite, not a rotated square. */
.cards-spell-fiber g[data-strand="knot"] rect[data-knot="core"] {
  clip-path: polygon(44.44% 0%, 55.56% 0%, 55.56% 11.11%, 66.67% 11.11%, 66.67% 22.22%, 77.78% 22.22%, 77.78% 33.33%, 88.89% 33.33%, 88.89% 44.44%, 100% 44.44%, 100% 55.56%, 88.89% 55.56%, 88.89% 66.67%, 77.78% 66.67%, 77.78% 77.78%, 66.67% 77.78%, 66.67% 88.89%, 55.56% 88.89%, 55.56% 100%, 44.44% 100%, 44.44% 88.89%, 33.33% 88.89%, 33.33% 77.78%, 22.22% 77.78%, 22.22% 66.67%, 11.11% 66.67%, 11.11% 55.56%, 0% 55.56%, 0% 44.44%, 11.11% 44.44%, 11.11% 33.33%, 22.22% 33.33%, 22.22% 22.22%, 33.33% 22.22%, 33.33% 11.11%, 44.44% 11.11%);
}
/* A resting thread is faint on purpose (five can cross the board); the moment
   one catches, it flares to full strength and fades back, so the latch is seen. */
.cards-spell-fiber[data-latched="true"] {
  animation: cards-fiber-latch-flare var(--dur-latch) var(--ease-out);
}
@keyframes cards-fiber-latch-flare {
  from { opacity: .95; }
}
.cards-spell-fiber[data-latched="true"] g[data-strand="knot"] rect[data-knot="core"] {
  animation: cards-fiber-knot-core var(--dur-latch) step-end forwards;
}
.cards-spell-fiber[data-latched="true"] g[data-strand="knot"] rect[data-knot="chip"] {
  animation: cards-fiber-knot-chip var(--dur-latch) var(--ease-out) forwards;
}
.cards-spell-fiber g[data-strand="knot"] rect[data-knot="chip"]:nth-child(2) { --cards-knot-x: -10px; --cards-knot-y: -10px; }
.cards-spell-fiber g[data-strand="knot"] rect[data-knot="chip"]:nth-child(3) { --cards-knot-x: 10px; --cards-knot-y: -10px; }
.cards-spell-fiber g[data-strand="knot"] rect[data-knot="chip"]:nth-child(4) { --cards-knot-x: -10px; --cards-knot-y: 10px; }
.cards-spell-fiber g[data-strand="knot"] rect[data-knot="chip"]:nth-child(5) { --cards-knot-x: 10px; --cards-knot-y: 10px; }
@keyframes cards-fiber-knot-core {
  0% { opacity: 1; transform: scale(1.4); }
  35% { opacity: 1; transform: scale(.9); }
  70% { opacity: .6; transform: scale(.6); }
  100% { opacity: 0; transform: scale(.6); }
}
@keyframes cards-fiber-knot-chip {
  0% { opacity: 1; transform: translate(0, 0); }
  70% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--cards-knot-x, 8px), var(--cards-knot-y, 8px)); }
}
.cards-spell-fiber[data-variant="cast"][data-phase="impact"] circle[data-strand="spark"] {
  transform-box: fill-box;
  transform-origin: center;
  animation: cards-cast-fiber-spark var(--beat-cast-impact) var(--ease-out) forwards;
}
.cards-spell-fiber[data-variant="devour"] { opacity: .62; }
.cards-spell-fiber[data-variant="devour"] path[data-strand="glow"] {
  stroke-width: 10;
  opacity: .18;
  animation: cards-devour-fiber-glow var(--beat-devour) var(--ease-out) forwards;
}
.cards-spell-fiber[data-variant="devour"] path[data-strand="thread"] {
  stroke-width: 1.3;
  opacity: 0;
  stroke-dasharray: 1;
  animation: cards-devour-fiber-pull var(--beat-devour) linear forwards;
}
/* The centre tendon is the trap's grip on the prey: it lands with the shut and
   stays drawn through the pull, heavier than the vines around it. */
.cards-spell-fiber[data-variant="devour"] path[data-thread-index="0"] {
  stroke-width: 3;
  animation-name: cards-devour-fiber-tendon;
}
.cards-board-locations {
  display: flex;
  align-items: stretch;
  gap: var(--cards-location-gap, 26px);
}
/* A location is drawn on the table, not laid on it (02 §4.1): a zoneBg plate
   with a dim ink frame and no step. The frame is 3px as the design system asks,
   2px of border plus 1px inset, so the board's geometry stays as it was until
   DS4b. */
.cards-board-location {
  position: relative;
  isolation: isolate;
  box-sizing: border-box;
  overflow: hidden;
  border: 2px solid color-mix(in srgb, var(--c-step-shadow) 25%, transparent);
  border-radius: var(--radius-2xl);
  padding: 7px;
  background: var(--c-zone-bg);
  color: var(--c-text-muted-on-dark);
  box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--c-step-shadow) 25%, transparent);
  transition: opacity var(--dur-quick) var(--ease-out);
}
.cards-board-location[data-active="true"] {
  box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
}
.cards-board-location[data-dimmed="true"] { opacity: .45; }
.cards-board-location-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  min-height: 20px;
  margin-bottom: 6px;
  padding: 0 2px 5px;
  border-bottom: var(--stroke-s) solid color-mix(in srgb, var(--c-text-on-dark) 12%, transparent);
  color: var(--c-selection);
  font: var(--type-display-xs);
  line-height: 1;
  text-shadow: var(--text-outline-1);
  text-transform: uppercase;
}
.cards-location-symbol { color: var(--c-selection); }
.cards-location-symbol .cards-pixel-icon { width: 14px; height: 14px; vertical-align: -2px; filter: drop-shadow(0 1px 0 var(--c-outline)); }
.cards-board-location-half {
  position: relative;
}
/* An occupied cell shows no frame of its own: the card in it carries its owner
   (02 §4.3). An empty one is a dashed cell drawn on the location — 3px of dash
   drawn as an inward outline over the 1px border, so the slot keeps its size. */
.cards-board-cell:not([data-empty="true"]) { border-color: transparent; background: transparent; }
.cards-board-cell[data-empty="true"] {
  border-color: transparent;
  background: var(--c-zone-bg);
  outline: var(--stroke-l) dashed var(--cards-slot-dash);
  outline-offset: calc(var(--stroke-l) * -1);
}
.cards-board-cell[data-empty="true"]:hover {
  outline-color: color-mix(in srgb, var(--c-text-on-dark) 32%, transparent);
}
.cards-board-group {
  display: flex;
  gap: var(--cards-cell-gap, 6px);
}
/* The front line (02 §4.2): a faint cream rule with a red «VS» plate. */
.cards-board-front-line {
  position: relative;
  display: flex;
  align-items: center;
  gap: 5px;
  height: 14px;
  color: var(--c-text-on-color);
}
.cards-board-front-line::before,
.cards-board-front-line::after {
  content: "";
  flex: 1;
  border-top: var(--stroke-l) solid color-mix(in srgb, var(--c-text-on-dark) 15%, transparent);
}
.cards-board-front-line > span {
  padding: 1px var(--space-1);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-m);
  background: var(--c-attack);
  box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
  color: var(--c-text-on-color);
  font: var(--type-display-3xs);
  line-height: 1;
  text-shadow: var(--text-outline-1);
}

/* The combat cue element paints nothing itself — the attacker card lunges
   into the target (cards-card-lunge) — but it stays mounted as the playback
   phase sentinel and geometry carrier for the impact ring/damage number. */
.cards-combat-pulse {
  position: fixed;
  z-index: 110;
  pointer-events: none;
}
/* The impact burst, anchored on the contact point and turned onto the strike's
   angle (both written by the board), so the chips spray FORWARD — out of the
   blow, into and past what it hit. Pixel language throughout: a hard cross for
   the flash and square chips, no blur and no round ring. It all plays inside
   the hit-stop and just after it, which is when the eye is on this spot. */
.cards-combat-impact-ring {
  position: fixed;
  z-index: 111;
  width: 0;
  height: 0;
  pointer-events: none;
  transform: rotate(var(--cards-combat-angle, 0deg));
}
.cards-combat-impact-ring::before {
  content: '';
  position: absolute;
  left: -13px;
  top: -13px;
  width: 26px;
  height: 26px;
  background:
    linear-gradient(var(--c-combat-core), var(--c-combat-core)) center / 100% 4px no-repeat,
    linear-gradient(var(--c-combat-core), var(--c-combat-core)) center / 4px 100% no-repeat,
    linear-gradient(var(--c-attack), var(--c-attack)) center / 12px 12px no-repeat;
  animation: cards-impact-flash var(--beat-attack-impact) step-end forwards;
}
.cards-combat-impact-ring > span {
  position: absolute;
  left: -2px;
  top: -2px;
  width: 4px;
  height: 4px;
  background: var(--c-combat-core);
  opacity: 0;
  animation: cards-impact-spark calc(var(--beat-attack-impact) * .55) var(--ease-out) forwards;
}
/* Eight square sparks (03 §4.3): six of the core's white, two of the blow's red. */
.cards-combat-impact-ring > span:nth-child(3),
.cards-combat-impact-ring > span:nth-child(6) { background: var(--c-attack); }
/* A strike that deals nothing — an aura handing its buff over plays as one —
   must not spray blood: the same burst in the player's green. */
.cards-combat-impact-ring[data-harmless="true"]::before {
  background:
    linear-gradient(var(--c-combat-core), var(--c-combat-core)) center / 100% 4px no-repeat,
    linear-gradient(var(--c-combat-core), var(--c-combat-core)) center / 4px 100% no-repeat,
    linear-gradient(var(--c-positive), var(--c-positive)) center / 12px 12px no-repeat;
}
.cards-combat-impact-ring[data-harmless="true"] > span:nth-child(3),
.cards-combat-impact-ring[data-harmless="true"] > span:nth-child(6) { background: var(--c-positive); }
.cards-combat-impact-ring > span:nth-child(1) { --cards-spark-x: 30px; --cards-spark-y: -12px; width: 5px; height: 5px; }
.cards-combat-impact-ring > span:nth-child(2) { --cards-spark-x: 26px; --cards-spark-y: 14px; }
.cards-combat-impact-ring > span:nth-child(3) { --cards-spark-x: 15px; --cards-spark-y: -25px; }
.cards-combat-impact-ring > span:nth-child(4) { --cards-spark-x: 13px; --cards-spark-y: 23px; width: 5px; height: 5px; }
.cards-combat-impact-ring > span:nth-child(5) { --cards-spark-x: 36px; --cards-spark-y: 2px; width: 3px; height: 3px; }
.cards-combat-impact-ring > span:nth-child(6) { --cards-spark-x: -9px; --cards-spark-y: -17px; width: 3px; height: 3px; }
.cards-combat-impact-ring > span:nth-child(7) { --cards-spark-x: -7px; --cards-spark-y: 19px; width: 3px; height: 3px; }
.cards-combat-impact-ring > span:nth-child(8) { --cards-spark-x: 22px; --cards-spark-y: -2px; width: 6px; height: 6px; animation-delay: 20ms; }
/* Punched in oversized on the hit-stop and then tossed off the card it belongs
   to. A hard 1px outline in place of the old blurred glow, so it reads as the
   same pixel art as the cards; big hits are drawn bigger (data-magnitude) so
   their weight lands before the digits are read. */
.cards-combat-damage-number {
  position: fixed;
  z-index: 112;
  color: var(--c-negative);
  font: var(--type-display-s);
  line-height: 1;
  text-shadow: var(--text-outline-2);
  animation: cards-damage-number var(--beat-attack-impact) linear forwards;
  pointer-events: none;
  transform: translate(-50%, -50%);
}
/* Heavier hits print bigger (03 §4.4): 1–2 display-s, 3–4 display-m, 5–7
   display-l, 8+ display-l on a red tag in cream. */
.cards-combat-damage-number[data-magnitude="2"] { font: var(--type-display-m); line-height: 1; }
.cards-combat-damage-number[data-magnitude="3"],
.cards-combat-damage-number[data-magnitude="4"] { font: var(--type-display-l); line-height: 1; }
.cards-combat-damage-number[data-magnitude="4"] {
  padding: var(--space-1) var(--space-2);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-s);
  background: var(--c-negative);
  color: var(--c-text-on-color);
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
  text-shadow: var(--text-outline-1);
}
@media (prefers-reduced-motion: reduce) {
  .cards-combat-impact-ring > span { display: none; }
}
.cards-effect-link {
  --cards-effect-link-color: var(--c-selection);
  position: fixed;
  z-index: 110;
  height: 4px;
  border-radius: var(--radius-round);
  background: linear-gradient(90deg, transparent, var(--cards-effect-link-color) 38%, var(--c-text-on-dark));
  pointer-events: none;
  transform: rotate(var(--cards-combat-angle));
  transform-origin: 0 50%;
}
.cards-effect-link[data-kind="absorb"],
.cards-effect-link[data-kind="devour"] { display: none; }
.cards-effect-link[data-kind="trigger"] { --cards-effect-link-color: var(--c-negative); }
.cards-effect-link[data-phase="travel"]:not([data-kind="devour"]) {
  animation: cards-effect-link-travel var(--beat-effect-travel) var(--ease-out) forwards;
}
.cards-effect-link[data-phase="impact"] {
  animation: cards-combat-impact var(--beat-cast-impact) var(--ease-out) forwards;
}
.cards-death-fall-cue {
  position: fixed;
  z-index: 110;
  height: 56px;
  pointer-events: none;
}
.cards-death-fall-cue > span {
  position: absolute;
  left: -27px;
  top: -27px;
  display: grid;
  place-items: center;
  width: 54px;
  height: 54px;
  transform-origin: 50% 8%;
}
.cards-death-fall-cue[data-phase="travel"] > span {
  animation:
    cards-departed-death-fall var(--beat-effect-travel) var(--ease-out) forwards,
    cards-departed-death-fall-tone var(--beat-effect-travel) step-end forwards;
}
.cards-death-fall-cue[data-phase="impact"] > span {
  animation:
    cards-departed-death-fall-impact var(--beat-cast-impact) var(--ease-out) forwards,
    cards-departed-death-fall-impact-tone var(--beat-cast-impact) step-end forwards;
}
.cards-effect-link > span {
  position: absolute;
  right: -5px;
  top: -5px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: var(--cards-effect-link-color);
  box-shadow: 0 0 0 var(--stroke-m) var(--c-outline);
}
.cards-effect-link[data-phase="impact"][data-amount]::after {
  content: attr(data-amount);
  position: absolute;
  right: -24px;
  top: -30px;
  color: var(--c-text-on-color);
  font: var(--type-display-s);
  line-height: 1;
  text-shadow: var(--text-outline-2);
  transform: rotate(calc(var(--cards-combat-angle) * -1));
}
.cards-cast-cue {
  position: fixed;
  z-index: 113;
  width: 48px;
  height: 48px;
  margin: -24px;
  display: grid;
  place-items: center;
  border: var(--stroke-l) solid var(--c-selection);
  border-radius: var(--radius-m);
  background: var(--c-panel-bg);
  box-shadow: 0 0 0 var(--stroke-m) var(--c-outline), 0 var(--step-2) 0 var(--c-step-shadow);
  pointer-events: none;
}
.cards-cast-cue[data-phase="travel"] {
  animation: cards-cast-cue-travel var(--beat-effect-travel) var(--ease-out) forwards;
}
.cards-cast-cue[data-phase="impact"] {
  animation:
    cards-cast-cue-impact var(--beat-cast-impact) var(--ease-out) forwards,
    cards-cast-cue-impact-tone var(--beat-cast-impact) step-end forwards;
}
.cards-cast-cue[data-phase="impact"][data-amount]::after {
  content: attr(data-amount);
  position: absolute;
  right: -22px;
  top: -22px;
  color: var(--c-text-on-color);
  font: var(--type-display-s);
  line-height: 1;
  text-shadow: var(--text-outline-2);
}

.cards-board-cell {
  position: relative;
  isolation: isolate;
  width: var(--cards-cell-instance-width);
  height: var(--cards-cell-height, 138px);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: var(--radius-m);
  padding: var(--cards-cell-padding, 4px);
  cursor: default;
  transition: transform var(--dur-quick) var(--ease-out);
}
/* The sight of a valid target (02 §4.3): four pixel corners in gold, 10×10 and
   3px thick, over the cell and anything in it. Hidden until the cell is one. */
.cards-board-cell::before {
  --cards-aim: var(--c-selection);
  content: "";
  position: absolute;
  z-index: 9;
  inset: 0;
  background:
    linear-gradient(var(--cards-aim), var(--cards-aim)) left top / 10px var(--stroke-l),
    linear-gradient(var(--cards-aim), var(--cards-aim)) left top / var(--stroke-l) 10px,
    linear-gradient(var(--cards-aim), var(--cards-aim)) right top / 10px var(--stroke-l),
    linear-gradient(var(--cards-aim), var(--cards-aim)) right top / var(--stroke-l) 10px,
    linear-gradient(var(--cards-aim), var(--cards-aim)) left bottom / 10px var(--stroke-l),
    linear-gradient(var(--cards-aim), var(--cards-aim)) left bottom / var(--stroke-l) 10px,
    linear-gradient(var(--cards-aim), var(--cards-aim)) right bottom / 10px var(--stroke-l),
    linear-gradient(var(--cards-aim), var(--cards-aim)) right bottom / var(--stroke-l) 10px;
  background-repeat: no-repeat;
  opacity: 0;
  pointer-events: none;
}
.cards-board-cell[data-highlight="true"]::before,
.cards-board-cell[data-target-selected="true"]::before { opacity: 1; }
.cards-board-cell[data-highlight="true"]::before {
  animation: cards-board-aim-blink var(--dur-blink) var(--steps-blink) infinite alternate;
}
@keyframes cards-board-aim-blink {
  from { opacity: 1; }
  to { opacity: .45; }
}
@media (prefers-reduced-motion: reduce) {
  .cards-board-cell[data-highlight="true"]::before { animation: none; }
}
.cards-board-cell-empty-mark {
  color: color-mix(in srgb, var(--c-text-on-dark) 22%, transparent);
  font: var(--type-display-xs);
}
.cards-board-cell::after {
  content: "";
  position: absolute;
  z-index: 8;
  inset: 0;
  border-radius: inherit;
  background: color-mix(in srgb, var(--c-selection) 18%, transparent);
  opacity: 0;
  pointer-events: none;
  transform: scaleY(0);
  transform-origin: bottom;
  transition:
    opacity var(--dur-quick) var(--ease-out),
    transform var(--dur-base) var(--ease-out);
}
.cards-board-cell[data-dimmed="true"] { opacity: .4; }
.cards-board-cell[data-interactive="true"] { cursor: pointer; }
.cards-board-cell[data-drop-enabled="true"] { cursor: copy; }
/* Under the carried card: the dash turns into a solid gold frame, the cell
   fills with gold at 18% and rises 3px (02 §4.3). */
.cards-board-cell[data-pointer-drag-over="true"] {
  outline: var(--stroke-l) solid var(--c-selection);
  outline-offset: calc(var(--stroke-l) * -1);
  transform: translateY(-3px);
}
.cards-board-cell[data-pointer-drag-over="true"]::after {
  opacity: 1;
  transform: scaleY(1);
  animation: cards-board-aim-blink var(--dur-blink) var(--steps-blink) infinite alternate;
}
@media (prefers-reduced-motion: reduce) {
  .cards-board-cell[data-pointer-drag-over="true"]::after { animation: none; }
}
/* A cell signal (03 §4.8): two nested sights in the effect's colour spread out
   on transform and go out in held steps — the aim corners (::before) and a ring
   carrying the ±N (::after). No box-shadow, no animated radius. */
.cards-board-cell[data-playback-cue] { --cards-cell-cue: var(--c-positive); }
.cards-board-cell:is([data-playback-cue$="-negative"], [data-playback-cue$="-remove"]) {
  --cards-cell-cue: var(--c-negative);
}
.cards-board-cell[data-playback-cue]::before {
  --cards-aim: var(--cards-cell-cue);
  opacity: 1;
  animation:
    cards-cell-cue-spread var(--beat-cell-pulse) var(--ease-out) forwards,
    cards-cell-cue-out var(--beat-cell-pulse) step-end forwards;
}
.cards-board-cell[data-playback-cue]::after {
  content: attr(data-playback-cue-delta);
  display: grid;
  place-items: center;
  border: var(--stroke-l) solid var(--cards-cell-cue);
  background: color-mix(in srgb, var(--cards-cell-cue) 24%, transparent);
  color: var(--c-text-on-color);
  font: var(--type-display-s);
  text-shadow: var(--text-outline-1);
  opacity: 1;
  transform: scale(.6);
  transform-origin: center;
  animation:
    cards-cell-cue-ring var(--beat-cell-pulse) var(--ease-overshoot) forwards,
    cards-cell-cue-out var(--beat-cell-pulse) step-end forwards;
}
@keyframes cards-cell-cue-spread {
  from { transform: scale(.7); }
  to { transform: scale(1.12); }
}
@keyframes cards-cell-cue-ring {
  from { transform: scale(.6); }
  to { transform: scale(1); }
}
@keyframes cards-cell-cue-out {
  0%, 55% { opacity: 1; }
  70% { opacity: .66; }
  85% { opacity: .33; }
  100% { opacity: 0; }
}
/* Reduced motion: both sights stand still and lit for the beat. */
@media (prefers-reduced-motion: reduce) {
  .cards-board-cell[data-playback-cue]::before,
  .cards-board-cell[data-playback-cue]::after { animation: none; transform: none; opacity: 1; }
}

/* Whole-location placement (slimefall/contamination_map): a spell selects one
   of the three locations, never a player side within it. Keep the affordance
   on the section so its frame, both rows and centre line read as one target. */
.cards-board-location[data-table-drop-enabled="true"] { cursor: copy; }
.cards-board-location[data-table-interactive="true"] { cursor: pointer; }
/* A location as a target: the gold frame of 02 §4.1, filled a little on hover. */
.cards-board-location[data-table-highlight="true"] {
  box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
}
.cards-board-location[data-table-interactive="true"]:hover {
  background: color-mix(in srgb, var(--c-selection) 10%, var(--c-zone-bg));
  box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
}
.cards-board-location[data-table-dimmed="true"] { opacity: .4; }
.cards-board-location[data-pointer-drag-over="true"] {
  background: color-mix(in srgb, var(--c-selection) 18%, var(--c-zone-bg));
  box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
}

/* Targetless card in flight (royal_decree/general_levy/reactions): the whole
   board stage is one activation surface — any point plays the card. The
   board field's own opaque backdrop is tinted with the same mix, otherwise it
   reads as a dark stripe cutting through the highlighted stage. */
.cards-board-stage { position: relative; border-radius: var(--radius-m); }
.cards-board-stage[data-untargeted-active="true"] {
  background: color-mix(in srgb, var(--c-selection) 6%, transparent);
  box-shadow: inset 0 0 0 var(--stroke-m) color-mix(in srgb, var(--c-selection) 45%, transparent);
  cursor: pointer;
}
.cards-board-stage[data-untargeted-drop-enabled="true"] { cursor: copy; }
.cards-board-stage[data-untargeted-drop-enabled="true"][data-pointer-drag-over="true"] {
  background: color-mix(in srgb, var(--c-selection) 14%, transparent);
  box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
}
.cards-board-stage[data-untargeted-drop-enabled="true"][data-pointer-drag-over="true"] .cards-board-field {
  background: color-mix(in srgb, var(--c-selection) 10%, transparent);
}

/* Multi-step board targeting (foreign_flesh/pull_into_clot): a cell already
   chosen for the current step is marked distinctly from the highlighted picks. */
/* A chosen target: the sight stays lit and the cell fills with gold (02 §4.3). */
.cards-board-cell[data-target-selected="true"] {
  background: color-mix(in srgb, var(--c-selection) 18%, transparent);
}
/* Floating overlay pinned to the bottom of the board stage: starting a
   targeting session must never reflow/shift the battlefield itself. */
.cards-targeting-bar {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  z-index: 60;
  box-sizing: border-box;
  width: calc(100% - 16px);
  max-width: calc(100% - 16px);
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 6px;
  /* A slate panel over the hand (02 §4.4): the step hint, the running count
     and the two actions. */
  padding: 5px 7px;
  color: var(--c-text-on-dark);
  font: var(--type-text-s);
}
.cards-targeting-bar-label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cards-targeting-bar .cards-kit-button {
  flex: 0 0 auto;
  white-space: nowrap;
}
.cards-targeting-bar-count { color: var(--c-text-muted-on-dark); font-variant-numeric: tabular-nums; }

/* The card skin (design system 02 §3, 01 §2.2): ONE set of variables every
   card dresses from — the hand, the board, the plan, the drag preview, both
   flight faces, the large card L and MiniCard. Look A «Тёмный корпус» (Q14):
   every card is a dark slate body — indigo for a spell, a lighter slate for a
   token — with cream text, in an ink outline over a solid ink step and a light
   bevel along its top edge; a structure adds a dark gold inner rim; the art
   sits in a warm light window framed in ink. A state swaps only the variable
   it changes, and a new look is a change here, not in each card. Surfaces that are not cards (a deck passive, the cell
   state, a location effect) opt out explicitly: .cards-mini-card[data-card-kind=
   "passive"] and the untyped/EFFECT .cards-card-detail-panel (kit.css.ts). */
.cards-card-detail-panel,
.cards-mini-card,
.cards-hand-card,
.cards-touch-drag-preview,
.cards-return-face-hand,
.cards-return-face-compact,
.cards-board-card,
.cards-staged-card {
  --cards-card-face: var(--c-card-face);
  --cards-card-ink: var(--c-text-on-card-face);
  --cards-card-muted: var(--c-text-muted-on-card-face);
  --cards-card-edge: var(--c-outline);
  --cards-card-rim: 0 0 transparent;
  --cards-card-ring: 0 0 transparent;
  /* Depth on the night table: a solid ink step (ink at 40% vanished into the
     indigo) and a 2px light bevel on the face's top edge (01 §7, Q14). */
  --cards-card-step-color: var(--c-step-shadow);
  --cards-card-bevel: inset 0 var(--stroke-m) 0 color-mix(in srgb, var(--c-text-on-card-face) 14%, transparent);
  /* The art window's ink frame and its sunken top edge (01 §10). */
  --cards-card-window-inset: inset 0 0 0 var(--stroke-m) var(--c-outline), inset 0 var(--step-2) 0 color-mix(in srgb, var(--c-step-shadow) 14%, transparent);
}
:is(.cards-card-detail-panel, .cards-mini-card, .cards-hand-card, .cards-touch-drag-preview, .cards-return-face-hand, .cards-return-face-compact, .cards-board-card, .cards-staged-card)[data-card-type="SPELL"] {
  --cards-card-face: var(--c-card-face-spell);
  --cards-card-ink: var(--c-text-on-spell);
  --cards-card-muted: var(--c-text-on-spell);
}
/* The rim is as thick as the card's outline (--cards-card-rim-width: L 3px). */
:is(.cards-card-detail-panel, .cards-mini-card, .cards-hand-card, .cards-touch-drag-preview, .cards-return-face-hand, .cards-return-face-compact, .cards-board-card, .cards-staged-card)[data-card-type="STRUCTURE"] {
  --cards-card-rim: inset 0 0 0 var(--cards-card-rim-width, var(--stroke-m)) var(--c-card-rim-structure);
}
/* A token (02 §3.2): the paler face, a dashed inner ring and the token mark by
   its name — on every card that can be one: the board, the hand and the large
   card. The ring gives way to the keyboard focus ring. */
:is(.cards-board-card, .cards-hand-card, .cards-card-detail-panel)[data-token="true"] {
  --cards-card-face: var(--c-card-face-token);
}
:is(.cards-board-card, .cards-hand-card, .cards-card-detail-panel)[data-token="true"]:not(:focus-visible) {
  outline: var(--stroke-m) dashed color-mix(in srgb, var(--c-text-on-card-face) 35%, transparent);
  outline-offset: calc(var(--stroke-m) * -3);
}
:is(.cards-board-card[data-token="true"] .cards-board-card-name, .cards-hand-card[data-token="true"] .cards-hand-name, .cards-card-detail-panel[data-token="true"] .cards-card-detail-title)::before {
  content: "";
  display: inline-block;
  flex: none;
  width: 7px;
  height: 7px;
  margin-right: 3px;
  background: currentColor;
  -webkit-mask: ${pixelIconDataUri('token', COLORS.outline)} center / 100% 100% no-repeat;
  mask: ${pixelIconDataUri('token', COLORS.outline)} center / 100% 100% no-repeat;
}
.cards-card-detail-panel[data-token="true"] .cards-card-detail-title::before { width: 14px; height: 14px; margin-right: var(--space-1); }
/* A token appearing draws its dashed ring in four held frames (03 §4.10): the
   static outline waits, a pseudo-ring in the same place is uncovered top-down. */
.cards-board-card[data-token="true"][data-animation="token-enter"] { outline-color: transparent; }
.cards-board-card[data-token="true"][data-animation="token-enter"]::after {
  content: "";
  position: absolute;
  z-index: 5;
  inset: var(--stroke-m);
  border: var(--stroke-m) dashed color-mix(in srgb, var(--c-text-on-card-face) 35%, transparent);
  pointer-events: none;
  animation: cards-token-ring var(--beat-token-create) step-end forwards;
}
@keyframes cards-token-ring {
  0% { clip-path: inset(0 0 100% 0); }
  25% { clip-path: inset(0 0 66% 0); }
  50% { clip-path: inset(0 0 33% 0); }
  75%, 100% { clip-path: inset(0); }
}
/* The rival's cards wear the rival's colour on their outline and name strip
   (01 §2.3); the location half carries the side. */
.cards-board-location-half[data-side="opponent"] :is(.cards-board-card, .cards-staged-card) {
  --cards-card-edge: var(--c-rival);
}
.cards-board-card,
.cards-staged-card {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  box-sizing: border-box;
  border: var(--stroke-m) solid var(--cards-card-edge);
  border-radius: var(--radius-m);
  padding: 2px;
  overflow: hidden;
  /* Board cards wear the same face as the hand card, so pressing "Ready"
     reveals a card in the colour it was placed in, never a dark→ flip. */
  background: var(--cards-card-face);
  color: var(--cards-card-ink);
}
.cards-board-card {
  --cards-state-shadow: 0 0 transparent;
  --cards-hack-shadow: 0 0 transparent;
  box-shadow:
    var(--cards-card-ring), var(--cards-state-shadow), var(--cards-hack-shadow),
    var(--cards-card-bevel), 0 var(--step-3) 0 var(--cards-card-step-color), var(--cards-card-rim);
}
/* Board cards carry cards-tilt for the hand/catalog hover feel. This
   box-shadow transition and the tilt transform transition target the same
   property slot at equal-or-lower specificity, so restate both together —
   otherwise whichever rule comes later in KIT_CSS silently drops the other. */
.cards-board-card.cards-tilt { transition: transform var(--dur-move) var(--ease-overshoot); }
.cards-board-card.cards-tilt[data-tilting="true"] { transition: transform var(--dur-tap) linear; }
.cards-board-card.cards-tilt[data-tilt-input="tap"] { transition: transform var(--dur-quick) var(--ease-out); }
/* The same depth as a hand card's (see .cards-hand-glyph): every tilting card's
   art drifts toward the pointer while it tilts, and only then — at rest the
   glyph carries no transform at all, which is what keeps the pixel art crisp.
   --cards-tilt-nx/ny are written by useCardTilt next to the rotation. */
.cards-tilt .cards-card-glyph {
  transition: transform var(--dur-base) var(--ease-out);
}
.cards-tilt[data-tilting="true"] .cards-card-glyph {
  transform:
    translate(calc(var(--cards-tilt-nx, 0) * 4%), calc(var(--cards-tilt-ny, 0) * 4%))
    scale(1.04);
  transition-duration: var(--dur-tap);
}
@media (prefers-reduced-motion: reduce) {
  .cards-tilt[data-tilting="true"] .cards-card-glyph { transform: none; }
}
/* The face-down back paints at z-index 9; the sheen must sit above it so a
   hovered/pressed hidden card still glints. */
.cards-board-card > .cards-tilt-glare { z-index: 10; }
.cards-board-card[role="button"] { cursor: pointer; }
/* A unit that moved cells slides there from its old one (03 §4.10, unitMove):
   Board pins it at the old cell's offset ("from"), then lets it go ("to"). */
.cards-board-cell .cards-board-card[data-unit-move] {
  z-index: 7;
  will-change: transform;
}
.cards-board-cell .cards-board-card[data-unit-move="from"] {
  transform: translate(var(--cards-move-x, 0px), var(--cards-move-y, 0px));
  transition: none;
}
.cards-board-cell .cards-board-card[data-unit-move="to"] {
  transform: translate(0, 0);
  transition: transform var(--beat-unit-move) var(--ease-out);
}
/* Preparation (not_ready) has no ring of its own any more: its «delay» label
   tells it (04 §1.2), and the gold ring belongs only to a card in your plan. */
/* Random-pick and Hack candidates: a sight ring around the card; the chosen one
   fills it — gold for a random pick, the rival's pink for a Hack (04 §1.2). */
.cards-board-card[data-hack-candidate="true"],
.cards-board-card[data-random-pick-candidate="true"] {
  --cards-hack-shadow: 0 0 0 var(--stroke-m) var(--c-selection);
}
.cards-board-card[data-hack-selected="true"] {
  --cards-hack-shadow: 0 0 0 var(--stroke-l) var(--c-rival);
}
.cards-board-card[data-random-pick-selected="true"] {
  --cards-hack-shadow: 0 0 0 var(--stroke-l) var(--c-selection);
}
.cards-board-card-playback-delta {
  position: absolute;
  z-index: 6;
  left: 50%;
  top: 18%;
  color: var(--c-positive);
  font: var(--type-display-s);
  line-height: 1;
  text-shadow: var(--text-outline-2);
  pointer-events: none;
  animation: cards-playback-delta var(--beat-status-change) var(--ease-out) forwards;
}
/* A loss drops instead of rising (03 §4.4). */
.cards-board-card-playback-delta[data-negative="true"] {
  color: var(--c-negative);
  animation-name: cards-playback-delta-down;
}
@media (prefers-reduced-motion: reduce) {
  /* Still and readable for the whole beat: the global rule would end it on frame one. */
  .cards-board-card-playback-delta,
  .cards-card-stat-delta { animation: none; transform: translate(-50%, 0); }
}
.cards-board-card[data-animation="consuming"] {
  animation: cards-card-consume var(--beat-consume) var(--ease-in) forwards;
}
.cards-board-card[data-animation="consuming"].cards-board-card-absorb-donor {
  /* A consumed card is pulled into its receiver instead of reading as a beam. */
  z-index: 8;
  transform-origin: center;
  will-change: transform, opacity, filter;
  animation: cards-card-absorb-donor var(--beat-consume) var(--ease-out) forwards;
}
/* box-shadow is deliberately absent from will-change here and on the devourer
   below. It is a paint property: no amount of hinting moves it to the compositor,
   and naming it only tells the browser to keep a layer primed for a change it
   will have to repaint anyway. Removing it is hygiene rather than a speed-up —
   the layer still gets created by the transform/filter listed beside it. The glow
   keyframes do still animate box-shadow, and it stays on the card instead of
   moving to an opacity-faded pseudo-element: at the mobile breakpoint
   .cards-board-card clips its overflow, so an outer ring on a child would lose
   its spread there. Only one or two cards devour at a time, so the repaint stays
   bounded. */
.cards-board-card-absorb-receiver {
  z-index: 7;
  transform-origin: center;
  will-change: transform, filter;
  animation: cards-card-absorb-receiver var(--beat-consume) var(--ease-out) both;
}
/* Both halves of the devour run linear with the beats placed by waypoint
   spacing: an eased curve would smear the snap and the struggle into a slide. */
.cards-board-card-devour-prey {
  z-index: 8;
  transform-origin: center;
  will-change: transform, opacity, filter;
  animation: cards-card-devour-prey var(--beat-devour) linear forwards;
}
.cards-board-card-devourer {
  z-index: 7;
  transform-origin: center;
  will-change: transform, filter;
  animation: cards-card-devourer var(--beat-devour) linear both;
}
/* The z-index above only orders a card inside its own cell: cells isolate their
   stacking and locations both isolate and CLIP (overflow: hidden). A donor
   absorbed across locations would be cut off at the arena border and pass under
   the slots it crosses. Board lifts the two cells and their locations for the
   length of the cue (the same outside-React contract as the attack lunge) and
   this class is what lets the flight leave the frame. */
.cards-board-location-flight { overflow: visible; }
.cards-board-card[data-animation="transforming"] {
  animation:
    cards-card-transform var(--beat-transform) linear,
    cards-card-transform-tone var(--beat-transform) step-end;
}
.cards-board-card[data-animation="timed-destroy"] {
  animation: cards-card-timed-destroy var(--beat-timed-destroy) steps(2, end) forwards;
}
.cards-board-card[data-death-phase="fractured"] {
  opacity: .68;
  filter: grayscale(.78) brightness(.58) contrast(1.2);
  box-shadow: var(--cards-card-rim);
}
.cards-board-card[data-death-phase="fractured"]::after {
  content: '';
  position: absolute;
  z-index: 7;
  inset: 5px;
  border-radius: var(--radius-xs);
  pointer-events: none;
  opacity: .9;
  background:
    linear-gradient(117deg, transparent 0 37%, var(--cards-card-muted) 38% 39%, transparent 40% 100%),
    linear-gradient(63deg, transparent 0 54%, var(--cards-card-muted) 55% 56%, transparent 57% 100%),
    linear-gradient(142deg, transparent 0 68%, var(--cards-card-muted) 69% 70%, transparent 71% 100%);
  clip-path: polygon(48% 0, 55% 30%, 86% 45%, 58% 52%, 70% 100%, 45% 62%, 16% 79%, 39% 48%, 10% 31%, 43% 34%);
}
/* The dissolve below eats the card away through a dither mask; this is the tile
   that mask repeats at, i.e. the size of one eaten pixel block. */
.cards-board-card[data-death-phase="fractured"] {
  -webkit-mask-size: ${DITHER_TILE} ${DITHER_TILE};
  mask-size: ${DITHER_TILE} ${DITHER_TILE};
}
/* Cracking without a blow (a death by effect, waiting for the sweep): the
   colour drains and the crack spreads in three hard steps rather than snapping
   in on one frame. Only while nothing else animates the card — a transition
   outranks an animation in the cascade, so on a lethal HIT it would hold the
   card grey through the very hit-stop flash that is meant to be white. */
.cards-board-card[data-death-phase="fractured"]:not([data-animation]) {
  transition: filter var(--dur-move) steps(3, end), opacity var(--dur-move) steps(3, end);
}
.cards-board-card[data-death-phase="fractured"]::after {
  transform-origin: 48% 42%;
  animation: cards-crack-spread var(--dur-base) steps(3, end) both;
}
/* Killed by the blow itself: the crack waits out the hit-stop, so the frozen
   white frame is the hit and the crack is what it did. */
.cards-board-card[data-death-phase="fractured"][data-animation]::after {
  animation-delay: var(--beat-hit-stop);
}
@keyframes cards-crack-spread {
  from { opacity: 0; transform: scale(.3); }
  to { opacity: .9; transform: scale(1); }
}
.cards-board-card[data-death-phase="fractured"][data-animation="hit"] {
  animation:
    cards-card-shatter var(--beat-attack-impact) linear forwards,
    cards-card-shatter-tone var(--beat-attack-impact) step-end forwards;
}
/* Killed in a clash: the blow finds it at the end of its own lunge. */
.cards-board-card[data-death-phase="fractured"][data-animation="attack-recover"] {
  animation:
    cards-card-shatter-recoil var(--beat-attack-impact) linear forwards,
    cards-card-shatter-recoil-tone var(--beat-attack-impact) step-end forwards;
}
.cards-board-card[data-death-phase="fractured"][data-animation="damage-impact"] {
  animation:
    cards-card-shatter-cast var(--beat-cast-impact) linear forwards,
    cards-card-shatter-cast-tone var(--beat-cast-impact) step-end forwards;
}
/* Every other death (an effect, the death sweep): it is already cracked and
   grey, so it only shivers and crumbles. Same dissolve, same shards. */
.cards-board-card[data-animation="dying"] {
  animation: cards-card-crumble var(--beat-death) linear forwards;
}
/* Meltdown (Перегрев 4, 03 §4.6) instead of a crumble: the card sinks three
   3px steps while its art window floods orange through the dither, then it is
   eaten from the bottom up as hot drips fall. No crack, no grey: it melted. */
.cards-board-card[data-death-style="melt"][data-animation="dying"] {
  filter: none;
  -webkit-mask-size: 100% 200%;
  mask-size: 100% 200%;
  -webkit-mask-image: linear-gradient(to top, transparent 50%, var(--c-outline) 50%);
  mask-image: linear-gradient(to top, transparent 50%, var(--c-outline) 50%);
  animation: cards-card-melt var(--beat-death) step-end forwards;
}
.cards-board-card[data-death-style="melt"] .cards-board-card-art::before {
  content: "";
  position: absolute;
  z-index: 2;
  inset: 0;
  background: var(--c-heat);
  pointer-events: none;
  animation: cards-melt-flood var(--beat-death) step-end forwards;
}
.cards-board-card[data-death-style="melt"][data-animation="dying"]::after {
  content: "";
  inset: auto;
  left: 25%;
  bottom: 30%;
  width: 3px;
  height: 3px;
  border-radius: 0;
  clip-path: none;
  background: var(--c-heat);
  box-shadow: 12px 3px 0 var(--c-heat), 24px -2px 0 var(--c-heat), 36px 2px 0 var(--c-heat);
  opacity: 0;
  animation: cards-melt-drip var(--beat-death) step-end forwards;
}
@keyframes cards-card-melt {
  0% { transform: translateY(0); -webkit-mask-position: 0 0; mask-position: 0 0; }
  10% { transform: translateY(3px); }
  20% { transform: translateY(6px); }
  30% { transform: translateY(9px); -webkit-mask-position: 0 0; mask-position: 0 0; }
  40% { transform: translateY(9px); -webkit-mask-position: 0 20%; mask-position: 0 20%; }
  52% { transform: translateY(9px); -webkit-mask-position: 0 40%; mask-position: 0 40%; }
  64% { transform: translateY(9px); -webkit-mask-position: 0 60%; mask-position: 0 60%; }
  76% { transform: translateY(9px); -webkit-mask-position: 0 80%; mask-position: 0 80%; }
  88%, 100% { transform: translateY(9px); -webkit-mask-position: 0 100%; mask-position: 0 100%; }
}
@keyframes cards-melt-flood {
  0% { ${maskStop(16)} }
  8% { ${maskStop(12)} }
  16% { ${maskStop(8)} }
  24% { ${maskStop(4)} }
  32%, 100% { ${maskStop(0)} }
}
@keyframes cards-melt-drip {
  0%, 30% { opacity: 0; transform: translateY(0); }
  36% { opacity: 1; transform: translateY(0); }
  52% { opacity: 1; transform: translateY(4px); }
  68% { opacity: 1; transform: translateY(8px); }
  84% { opacity: 1; transform: translateY(12px); }
  92%, 100% { opacity: 0; transform: translateY(12px); }
}
${shatterKeyframes('cards-card-shatter', TURN_PLAYBACK_TIMING.attackImpact, 'rest')}
${shatterKeyframes('cards-card-shatter-recoil', TURN_PLAYBACK_TIMING.attackImpact, 'contact')}
${shatterKeyframes('cards-card-shatter-cast', TURN_PLAYBACK_TIMING.castImpact, 'rest')}
@keyframes cards-card-crumble {
  /* Held poses, not a slide: a shiver is a few hard frames. */
  0% { transform: translate(0, 0); animation-timing-function: step-end; }
  5% { transform: translate(-1.5px, 0); animation-timing-function: step-end; }
  10% { transform: translate(1.5px, 0); animation-timing-function: step-end; }
  15% { transform: translate(-1px, 0); animation-timing-function: step-end; }
  20% { transform: translate(1px, 0); animation-timing-function: step-end; }
  24% { transform: translate(0, 0); ${maskStop(0)} }
  34% { transform: translate(0, 1px) scale(.995); ${maskStop(4)} }
  46% { transform: translate(0, 2px) scale(.99); ${maskStop(8)} }
  58% { transform: translate(0, 3px) scale(.98); ${maskStop(12)} }
  70% { transform: translate(0, 4px) scale(.975); ${maskStop(14)} }
  80% { opacity: 1; transform: translate(0, 5px) scale(.97); ${maskStop(16)} }
  100% { opacity: 0; transform: translate(0, 5px) scale(.97); ${maskStop(16)} }
}
/* The pieces a dying card drops: square chips in its own colours — face, art
   window and outline — popped loose and falling as the dissolve eats the card. They
   live inside the card's box (container units), because the location clips
   anything that strays out of it. Mounted with the crack, invisible until the
   card's death animation starts them through the sibling rules below. */
.cards-death-shards {
  --cards-shard-frame: var(--c-card-face);
  position: absolute;
  inset: var(--cards-cell-padding, 4px);
  z-index: 8;
  pointer-events: none;
  container-type: size;
}
.cards-death-shards[data-card-type="SPELL"] { --cards-shard-frame: var(--c-card-face-spell); }
.cards-death-shards > span {
  position: absolute;
  width: 5px;
  height: 5px;
  width: max(3px, 7cqw);
  height: max(3px, 7cqw);
  background: var(--c-card-art-window);
  box-shadow: 0 0 0 var(--stroke-s) var(--c-outline);
  opacity: 0;
}
.cards-death-shards > span:nth-child(3n + 1) { background: var(--cards-shard-frame); }
.cards-death-shards > span:nth-child(3n) { background: var(--c-outline); }
.cards-death-shards > span:nth-child(1) { left: 16%; top: 18%; --cards-shard-x: -12cqw; --cards-shard-y: 44cqh; }
.cards-death-shards > span:nth-child(2) { left: 44%; top: 10%; --cards-shard-x: 4cqw; --cards-shard-y: 52cqh; --cards-shard-lag: 30ms; }
.cards-death-shards > span:nth-child(3) { left: 72%; top: 20%; --cards-shard-x: 12cqw; --cards-shard-y: 40cqh; --cards-shard-lag: 10ms; }
.cards-death-shards > span:nth-child(4) { left: 28%; top: 36%; --cards-shard-x: -8cqw; --cards-shard-y: 36cqh; --cards-shard-lag: 40ms; }
.cards-death-shards > span:nth-child(5) { left: 58%; top: 34%; --cards-shard-x: 9cqw; --cards-shard-y: 42cqh; --cards-shard-lag: 20ms; }
.cards-death-shards > span:nth-child(6) { left: 12%; top: 52%; --cards-shard-x: -6cqw; --cards-shard-y: 30cqh; --cards-shard-lag: 15ms; }
.cards-death-shards > span:nth-child(7) { left: 80%; top: 50%; --cards-shard-x: 6cqw; --cards-shard-y: 28cqh; --cards-shard-lag: 35ms; }
.cards-death-shards > span:nth-child(8) { left: 40%; top: 58%; --cards-shard-x: -3cqw; --cards-shard-y: 26cqh; --cards-shard-lag: 5ms; }
.cards-death-shards > span:nth-child(9) { left: 64%; top: 64%; --cards-shard-x: 4cqw; --cards-shard-y: 22cqh; --cards-shard-lag: 25ms; }
.cards-death-shards > span:nth-child(10) { left: 50%; top: 26%; --cards-shard-x: 0cqw; --cards-shard-y: 48cqh; --cards-shard-lag: 45ms; }
/* Released as the dissolve starts eating the card (40% of a shatter, 24% of a
   crumble — see the keyframes above). forwards, not both: before its delay a
   chip must stay at its invisible resting style, not at the opaque 0% frame. */
.cards-board-card[data-death-phase="fractured"][data-animation="hit"] ~ .cards-death-shards > span,
.cards-board-card[data-death-phase="fractured"][data-animation="attack-recover"] ~ .cards-death-shards > span {
  animation: cards-death-shard calc(var(--beat-attack-impact) * .52) linear forwards;
  animation-delay: calc(calc(var(--beat-attack-impact) * .4) + var(--cards-shard-lag, 0ms));
}
.cards-board-card[data-death-phase="fractured"][data-animation="damage-impact"] ~ .cards-death-shards > span {
  animation: cards-death-shard calc(var(--beat-cast-impact) * .5) linear forwards;
  animation-delay: calc(calc(var(--beat-cast-impact) * .4) + var(--cards-shard-lag, 0ms));
}
.cards-board-card[data-animation="dying"] ~ .cards-death-shards > span {
  animation: cards-death-shard calc(var(--beat-death) * .66) linear forwards;
  animation-delay: calc(calc(var(--beat-death) * .24) + var(--cards-shard-lag, 0ms));
}
@keyframes cards-death-shard {
  /* A hop up and out as it comes loose, then a fall that gathers speed — the
     waypoint spacing is the gravity, the timing stays linear. */
  0% { opacity: 1; transform: translate(0, 0); }
  22% { transform: translate(calc(var(--cards-shard-x, 0cqw) * .3), -6cqh); }
  55% { transform: translate(calc(var(--cards-shard-x, 0cqw) * .7), calc(var(--cards-shard-y, 30cqh) * .25)); }
  80% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--cards-shard-x, 0cqw), var(--cards-shard-y, 30cqh)); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-death-shards { display: none; }
}
/* The cell's paint order, stated once because four rules depend on it and only
   one of them used to work: staged plans and lineup backs at 5, the overlay
   host ghost at 6, the card currently flipping up at 7, its gulp flash at 11.
   A flipping card MUST outrank the pile it is emerging from — a back left on
   top of it hides the whole reveal. z-index needs a positioned box, and the
   base .cards-board-card is static, so the position: relative here is
   load-bearing for this rule AND for the devouring one below (which carried a
   z-index that was silently inert until this line existed). Do not drop it. */
.cards-board-card[data-animation="entering"] {
  /* backwards fill applies the 0% frame before the first paint so the card
     never flashes at full size/opacity for a frame before animating in;
     will-change promotes it to its own layer so the transform pop composites. */
  animation: cards-reveal-card var(--beat-card-reveal) linear backwards;
  position: relative;
  z-index: 7;
  transform-origin: 50% 60%;
  will-change: transform;
}
/* The back is unmounted by a JS timer aimed at the edge-on frame, but the turn
   runs on the CSS clock and the two drift by a frame or so. Fading the back out
   on the CSS clock too means a late timer removes something already invisible
   — never a readable sliver of back on a card that is already turning face-up.
   (The devouring reveal has its own clock and its own cover below.) */
.cards-board-card[data-animation="entering"][data-face-down="true"]:not([data-devouring="true"])
  .cards-board-card-back {
  animation: cards-reveal-back-cover var(--beat-card-reveal) linear both;
}
@keyframes cards-reveal-back-cover {
  0%, 59% { opacity: 1; }
  60%, 100% { opacity: 0; }
}
/* A light catching the face as it turns toward the player: one stepped band
   across it, on the way down — so the first thing the eye gets of the new card
   is a glint, not a pop. */
.cards-board-card[data-animation="entering"]:not([data-devouring="true"])::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 10;
  border-radius: inherit;
  pointer-events: none;
  /* The band is the mask, the paint is flat: it sweeps through mask-position,
     which may animate (03 §7), where background-position may not. */
  --cards-reveal-band: linear-gradient(
    110deg,
    transparent 38%,
    color-mix(in srgb, var(--c-glare) 18%, transparent) 38% 44%,
    color-mix(in srgb, var(--c-glare) 42%, transparent) 44% 50%,
    color-mix(in srgb, var(--c-glare) 18%, transparent) 50% 56%,
    transparent 56%
  );
  /* Light, not a combat flash: the glare role (white is only for chip digits
     and the core of a hit, 01 §1). */
  background: var(--c-glare);
  -webkit-mask-image: var(--cards-reveal-band);
  mask-image: var(--cards-reveal-band);
  -webkit-mask-size: 260% 100%;
  mask-size: 260% 100%;
  -webkit-mask-position: 100% 0;
  mask-position: 100% 0;
  mix-blend-mode: screen;
  opacity: 0;
  animation: cards-reveal-sheen var(--beat-card-reveal) linear backwards;
}
@keyframes cards-reveal-sheen {
  0%, 62% { opacity: 0; -webkit-mask-position: 100% 0; mask-position: 100% 0; }
  64% { opacity: 1; -webkit-mask-position: 92% 0; mask-position: 92% 0; }
  86% { opacity: 1; -webkit-mask-position: 0 0; mask-position: 0 0; }
  92%, 100% { opacity: 0; -webkit-mask-position: 0 0; mask-position: 0 0; }
}
/* The puff where it lands: a few grey chips kicked out sideways from under its
   bottom edge on the slam (82% of the reveal), inside the cell like the shards. */
.cards-reveal-dust {
  position: absolute;
  inset: var(--cards-cell-padding, 4px);
  z-index: 8;
  pointer-events: none;
  container-type: size;
}
.cards-reveal-dust > span {
  position: absolute;
  bottom: 0;
  width: 3px;
  height: 3px;
  width: max(2px, 4cqw);
  height: max(2px, 4cqw);
  background: var(--c-text-muted-on-table);
  opacity: 0;
}
.cards-reveal-dust > span:nth-child(1) { left: 4%; --cards-dust-x: -9cqw; }
.cards-reveal-dust > span:nth-child(2) { left: 16%; --cards-dust-x: -6cqw; --cards-dust-y: -7cqh; }
.cards-reveal-dust > span:nth-child(3) { left: 28%; --cards-dust-x: -3cqw; --cards-dust-y: -4cqh; }
.cards-reveal-dust > span:nth-child(4) { left: 66%; --cards-dust-x: 3cqw; --cards-dust-y: -4cqh; }
.cards-reveal-dust > span:nth-child(5) { left: 78%; --cards-dust-x: 6cqw; --cards-dust-y: -7cqh; }
.cards-reveal-dust > span:nth-child(6) { left: 90%; --cards-dust-x: 9cqw; }
.cards-board-card[data-animation="entering"]:not([data-devouring="true"]) ~ .cards-reveal-dust > span {
  animation: cards-reveal-dust calc(var(--beat-card-reveal) * .18) var(--ease-out) calc(var(--beat-card-reveal) * .8) forwards;
}
@keyframes cards-reveal-dust {
  0% { opacity: .9; transform: translate(0, 0); }
  100% { opacity: 0; transform: translate(var(--cards-dust-x, 0cqw), var(--cards-dust-y, -3cqh)); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-reveal-dust { display: none; }
  .cards-board-card[data-animation="entering"]::before { display: none; }
}
/* A receiver arriving in the cell of the host it just ate does two things where
   a plain reveal does one: it flips up lying ON that host (which is why the
   keyframe starts shifted up — see [data-overlay-stacked]), and then it eats it,
   dropping into the slot as the ghost beneath is swallowed. Only the
   keyframe changes — one animation per element, since the reveal already owns
   this element's transform.
   It sits ABOVE the ghost (z-index over cards-overlay-host-ghost): the eater is
   the card lying on top, and a ghost covering it would hide the whole flip —
   which is exactly how this read before: a back that simply vanished.
   Timing is linear because this keyframe has stops all the way to 100%: the
   reveal's snappy cubic-bezier is applied to EVERY segment, so each pose was
   reached in the first third of its slot and then held — five little stutters
   instead of a gulp. The wind-up into the flip keeps its ease per-stop inside
   the keyframe, where it belongs. */
.cards-board-card[data-animation="entering"][data-devouring="true"] {
  animation-name: cards-reveal-card-devouring;
  animation-duration: var(--beat-overlay-reveal);
  animation-timing-function: linear;
  /* Restated rather than inherited from the rule above: this z-index is the
     whole reason the eater is visible over its ghost, and it is only ever
     honoured because that rule also sets position. */
  position: relative;
  z-index: 7;
  /* It also inherits the shadow the back was casting down onto the host's
     exposed name and stat strip (see cards-board-card-lineup[data-overlay-stacked]) — without
     it the strip brightens on the handoff frame, which is the same one-frame
     twitch as a fade would be. It fades out through the card's own box-shadow
     transition once the animation ends and there is nothing left underneath. */
  --cards-state-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
}
/* The card is taking over a lineup back that was already fully visible. Keep
   its first frame opaque, so the handoff is one continuous card back followed
   by the flip rather than a second back fading in. */
.cards-board-card[data-animation="entering"][data-lineup-handoff="true"]:not([data-devouring="true"]) {
  animation-name: cards-reveal-card-from-lineup;
}
/* The gulp's flash. It used to be a brightness filter on the card itself —
   which forces the ~400-<rect> glyph SVG (see AuthoredGlyph) to re-rasterise
   every frame, on both cards at once, while they are also being scaled. An
   overlay animating only opacity stays on the compositor. */
.cards-board-card[data-animation="entering"][data-devouring="true"]::after,
.cards-overlay-host-ghost::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 11;
  border-radius: inherit;
  pointer-events: none;
  background: radial-gradient(circle at 50% 44%,
    color-mix(in srgb, var(--c-positive) 42%, var(--c-glare)) 0%,
    color-mix(in srgb, var(--c-positive) 40%, transparent) 48%,
    transparent 74%);
  opacity: 0;
  will-change: opacity;
  /* Both elements mount on the same frame (the ghost at CardConsumed, the card
     at the UnitEnteredPlay right behind it), so one shared clock — the whole
     overlay reveal — keeps the two halves of the flash in step. */
  animation: cards-devour-gulp-flash var(--beat-overlay-reveal) linear both;
}
@keyframes cards-devour-gulp-flash {
  /* Dark through the flip and the grab, brightest on the shut (87%). */
  0%, 71% { opacity: 0; }
  80% { opacity: .14; }
  87% { opacity: .4; }
  100% { opacity: 0; }
}
/* The back is unmounted by a JS timer aimed at the edge-on frame
   (turnPlayback's UnitEnteredPlay), but the flip runs on the CSS clock, and the
   two drift: a couple of frames late and you see the back at a readable width
   snapping to the face. So the back also fades itself out ON that clock — what
   the timer then removes is already invisible. */
.cards-board-card[data-animation="entering"][data-devouring="true"][data-face-down="true"]
  .cards-board-card-back {
  animation: cards-reveal-back-cover-devouring var(--beat-overlay-reveal) linear both;
}
@keyframes cards-reveal-back-cover-devouring {
  /* The edge-on frame is at 25% of this longer clock — same 370ms in as a plain
     reveal, since the flip in front of the gulp is the plain reveal's own. */
  0%, 24% { opacity: 1; }
  25%, 100% { opacity: 0; }
}
.cards-board-card[data-animation="token-enter"] {
  /* Tokens are never face-down — they appear face-up with a fade + scale accent,
     no flip (they materialize mid-play, they were never "placed" hidden). */
  animation: cards-token-enter var(--beat-token-create) var(--ease-out) backwards;
  will-change: transform;
}
.cards-board-card[data-animation="assembling"].cards-board-card-assembly-donor {
  z-index: 8;
  will-change: transform, opacity, filter;
  animation:
    cards-card-assembly-donor var(--beat-consume) var(--ease-out) forwards,
    cards-card-assembly-donor-tone var(--beat-consume) step-end forwards;
}
.cards-board-card[data-animation="token-enter"].cards-board-card-death-spawn {
  z-index: 8;
  will-change: transform, opacity, filter;
  animation:
    cards-token-death-spawn var(--beat-token-create) var(--ease-out) backwards,
    cards-token-death-spawn-tone var(--beat-token-create) step-end backwards;
}
.cards-board-card[data-animation="token-enter"].cards-board-card-death-spawn.cards-board-card-decay-fragment-spawn {
  /* Linear on purpose: eased curves apply per keyframe segment, which briefly
     stalled the card at every waypoint. Speed is shaped by the waypoint
     spacing inside the keyframes instead. The glow beside it steps. */
  animation:
    cards-decay-fragment-flight var(--beat-decay-fragment) linear backwards,
    cards-decay-fragment-flight-tone var(--beat-decay-fragment) step-end backwards;
}
.cards-decay-debris {
  position: fixed;
  z-index: 111;
  width: 0;
  height: 0;
  pointer-events: none;
}
.cards-decay-debris > span {
  /* Pixel chips: hard-edged squares, matching the board's pixel-art idiom. */
  position: absolute;
  left: -3px;
  top: -3px;
  width: 6px;
  height: 6px;
  background: var(--c-selection);
  opacity: 0;
  animation: cards-decay-debris-chip calc(var(--beat-decay-fragment) * .42) var(--ease-out) forwards;
}
.cards-decay-debris > span:nth-child(2n) { background: var(--c-text-on-dark); }
/* Radial "crack" chips stay tight around the source — they only sell the
   break-up moment; the courier chips below carry the decay to its cells. */
.cards-decay-debris > span:nth-child(1) { --cards-debris-dx: -28px; --cards-debris-dy: -18px; }
.cards-decay-debris > span:nth-child(2) { --cards-debris-dx: 8px; --cards-debris-dy: -32px; width: 8px; height: 8px; animation-delay: 30ms; }
.cards-decay-debris > span:nth-child(3) { --cards-debris-dx: 32px; --cards-debris-dy: -10px; }
.cards-decay-debris > span:nth-child(4) { --cards-debris-dx: 24px; --cards-debris-dy: 20px; width: 5px; height: 5px; animation-delay: 50ms; }
.cards-decay-debris > span:nth-child(5) { --cards-debris-dx: -8px; --cards-debris-dy: 28px; animation-delay: 20ms; }
.cards-decay-debris > span:nth-child(6) { --cards-debris-dx: -34px; --cards-debris-dy: 12px; width: 5px; height: 5px; animation-delay: 40ms; }
.cards-decay-debris > span[data-kind="courier"] {
  /* Couriers make the decay causal: each chip flies the measured source→cell
     vector (inline --cards-debris-dx/dy, size and stagger from Board.tsx) and
     lands right as its fragment card condenses — the eye follows the chips
     instead of the card popping out of nowhere. Placed after the nth-child
     rules above so the courier background wins at equal specificity. */
  background: var(--c-selection);
  animation: cards-decay-courier-chip calc(var(--beat-decay-fragment) * .58) linear forwards;
}
.cards-board-card[data-animation="attacking"] {
  /* Wind-up + strike; forwards keeps the card at contact until the impact
     phase swaps this for the recover animation. The glow steps beside it (see
     the hit below for why a filter is never interpolated). */
  animation:
    cards-card-lunge var(--beat-attack-travel) linear forwards,
    cards-card-lunge-flash var(--beat-attack-travel) step-end forwards;
  will-change: transform;
}
/* The attacker takes the same white-hot hit-stop as what it struck — the frame
   is the collision, not one card's — then bounces home. */
.cards-board-card[data-animation="attack-recover"] {
  animation:
    cards-card-lunge-return var(--beat-attack-impact) linear,
    cards-card-hit-flash var(--beat-attack-impact) step-end;
}
.cards-board-card[data-animation="hit"] {
  /* Two animations, two timing functions on purpose: the knockback moves on the
     compositor (linear overall, eased per stop), the flash steps so the card
     rasterises once per keyframe instead of once per frame. step-end is
     per-interval, so each brightness holds until the next stop — steps(n)
     would subdivide inside every interval and undo it. */
  animation:
    cards-card-hit var(--beat-attack-impact) linear,
    cards-card-hit-flash var(--beat-attack-impact) step-end;
}
.cards-board-card[data-animation="capture-departure"] {
  animation:
    cards-effect-departure var(--beat-capture-transfer) var(--ease-in) forwards,
    cards-effect-departure-tone var(--beat-capture-transfer) step-end forwards;
}
.cards-board-card[data-animation="capture-arrival"] {
  animation:
    cards-effect-arrival var(--beat-status-change) var(--ease-out),
    cards-effect-arrival-tone var(--beat-status-change) step-end;
}
.cards-board-card[data-animation="virus-attach"] {
  animation:
    cards-virus-attach var(--beat-explanation) var(--ease-out),
    cards-virus-attach-tone var(--beat-explanation) step-end;
}
.cards-board-card[data-animation="virus-trigger"],
.cards-board-card[data-animation="hack-attach"] {
  animation: cards-effect-glitch var(--beat-status-change) steps(4, end);
}
.cards-board-card[data-animation="virus-remove"],
.cards-board-card[data-animation="hack-remove"],
.cards-board-card[data-animation="effect-remove"] {
  animation:
    cards-effect-remove var(--beat-status-change) var(--ease-out),
    cards-effect-remove-tone var(--beat-status-change) step-end;
}
.cards-board-card[data-animation="shield-impact"] {
  animation:
    cards-shield-impact var(--beat-status-change) var(--ease-out),
    cards-shield-impact-tone var(--beat-status-change) step-end;
}
.cards-board-card[data-animation="shield-deplete"] {
  animation:
    cards-shield-deplete var(--beat-status-change) var(--ease-out),
    cards-shield-deplete-tone var(--beat-status-change) step-end;
}
.cards-board-card[data-animation="counter-positive"],
.cards-board-card[data-animation="sun-positive"],
.cards-board-card[data-animation="effect-positive"] {
  animation:
    cards-effect-positive var(--beat-status-change) var(--ease-out),
    cards-effect-positive-tone var(--beat-status-change) step-end;
}
/* A heal plays in the status window the playback waits for it (03 §4.9): a
   flash cut at the stat beat's 260ms left the card idle for the rest. */
.cards-board-card[data-animation="heal-positive"] {
  animation:
    cards-effect-positive var(--beat-status-change) var(--ease-out),
    cards-effect-positive-tone var(--beat-status-change) step-end;
}
/* Stat beats run on their own shorter window: the card pulse has to end with
   the chip and the number roll it belongs to, not 160 ms after them. */
.cards-board-card[data-animation="stat-positive"] {
  animation:
    cards-effect-positive var(--beat-stat-buff) var(--ease-out),
    cards-effect-positive-tone var(--beat-stat-buff) step-end;
}
.cards-board-card[data-animation="stat-negative"] {
  animation:
    cards-effect-negative var(--beat-stat-buff) var(--ease-out),
    cards-effect-negative-tone var(--beat-stat-buff) step-end;
}
.cards-board-card[data-animation="avoid-positive"] {
  animation:
    cards-card-avoid var(--beat-explanation) var(--ease-out),
    cards-card-avoid-flash var(--beat-explanation) step-end;
}
.cards-board-card[data-animation="counter-negative"],
.cards-board-card[data-animation="sun-negative"],
.cards-board-card[data-animation="effect-negative"],
.cards-board-card[data-animation="damage-impact"],
.cards-board-card[data-animation="capture-negative"] {
  animation:
    cards-effect-negative var(--beat-status-change) var(--ease-out),
    cards-effect-negative-tone var(--beat-status-change) step-end;
}
/* A failed dodge: the same shudder as a hit by an effect, held for the
   explanation the playback gives it. */
.cards-board-card[data-animation="avoid-negative"] {
  animation:
    cards-effect-negative var(--beat-explanation) var(--ease-out),
    cards-effect-negative-tone var(--beat-explanation) step-end;
}
/* A lasting effect ticked on the card (its duration changed): a hop on the
   overshoot and the gold ring below, no tone — the effect is not good or bad. */
.cards-board-card[data-animation="effect-trigger"] {
  animation: cards-effect-trigger var(--beat-status-change) linear;
}
@keyframes cards-effect-trigger {
  0% { transform: translateY(0); animation-timing-function: ${MOTION_EASINGS.out}; }
  35% { transform: translateY(-4px); animation-timing-function: ${MOTION_EASINGS.overshoot}; }
  100% { transform: translateY(0); }
}
/* An effect landing on a card flashes a pixel ring in its colour for two held
   frames (03 §4.9, §7): opacity steps on a pseudo-element instead of an
   animated box-shadow glow. Drawn inside the card's edge — the compact board
   clips a card's overflow, and an outer ring would lose its spread there. */
.cards-board-card:is(
  [data-animation$="-positive"],
  [data-animation$="-negative"],
  [data-animation="damage-impact"],
  [data-animation^="shield-"],
  [data-animation="virus-attach"],
  [data-animation="effect-trigger"],
  [data-animation$="-remove"]
)::before {
  content: "";
  position: absolute;
  z-index: 11;
  inset: 0;
  border: var(--stroke-l) solid var(--cards-cue-ring, var(--c-selection));
  border-radius: inherit;
  pointer-events: none;
  opacity: 0;
  animation: cards-cue-ring var(--cards-cue-ring-duration, var(--beat-status-change)) step-end;
}
.cards-board-card[data-animation$="-positive"] { --cards-cue-ring: var(--c-positive); }
.cards-board-card:is([data-animation$="-negative"], [data-animation="damage-impact"], [data-animation="virus-attach"]) {
  --cards-cue-ring: var(--c-negative);
}
.cards-board-card[data-animation^="shield-"] { --cards-cue-ring: var(--c-health); }
.cards-board-card[data-animation$="-remove"] { --cards-cue-ring: var(--c-text-on-dark); }
.cards-board-card:is([data-animation="stat-positive"], [data-animation="stat-negative"]) {
  --cards-cue-ring-duration: var(--beat-stat-buff);
}
.cards-board-card:is([data-animation="virus-attach"], [data-animation^="avoid-"]) {
  --cards-cue-ring-duration: var(--beat-explanation);
}
@keyframes cards-cue-ring {
  0% { opacity: 1; }
  20% { opacity: 0; }
  40% { opacity: 1; }
  60%, 100% { opacity: 0; }
}
/* Reduced motion: the ring stays lit for the whole beat — the same news, still. */
@media (prefers-reduced-motion: reduce) {
  .cards-board-card[data-animation]::before { animation: none; opacity: 1; }
  .cards-board-card[data-animation="entering"]::before { opacity: 0; }
}
/* A shield takes the blow (03 §4.9): a ring of eight blue pixels around the
   card closes in and goes out in held frames; the plate's number drops. */
.cards-board-card[data-animation="shield-impact"]::before {
  inset: var(--stroke-m);
  border: 0;
  background:
    linear-gradient(var(--c-health), var(--c-health)) left top / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) center top / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) right top / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) left center / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) right center / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) left bottom / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) center bottom / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) right bottom / 4px 4px no-repeat;
  animation: cards-shield-ring var(--beat-status-change) step-end;
}
@keyframes cards-shield-ring {
  0% { opacity: 1; transform: scale(1.06); }
  25% { opacity: 1; transform: scale(1); }
  50% { opacity: 1; transform: scale(.94); }
  75%, 100% { opacity: 0; transform: scale(.94); }
}
/* The shield is spent: its plate breaks into four pixels that fly apart. */
.cards-board-card[data-animation="shield-deplete"]::before {
  inset: 4px auto auto 4px;
  width: 16px;
  height: 16px;
  border: 0;
  background:
    linear-gradient(var(--c-health), var(--c-health)) left top / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) right top / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) left bottom / 4px 4px no-repeat,
    linear-gradient(var(--c-health), var(--c-health)) right bottom / 4px 4px no-repeat;
  animation: cards-shield-break var(--beat-status-change) step-end forwards;
}
@keyframes cards-shield-break {
  0% { opacity: 1; transform: scale(1); }
  25% { opacity: 1; transform: scale(1.6); }
  50% { opacity: 1; transform: scale(2.2); }
  75%, 100% { opacity: 0; transform: scale(2.6); }
}
/* The chips answer a blow (03 §4.3): the struck card's health chip hops, the
   striker's attack chip nods 4px on contact. */
.cards-board-card:is([data-animation="hit"], [data-animation="damage-impact"]) .cards-board-card-stats > .cards-card-stat:last-child {
  animation: cards-chip-bump var(--beat-attack-impact) linear;
}
.cards-board-card[data-animation="attack-recover"] .cards-board-card-stats > .cards-card-stat:first-child {
  animation: cards-chip-nod var(--beat-attack-impact) step-end;
}
@keyframes cards-chip-nod {
  0% { transform: translateY(-4px); }
  15%, 100% { transform: translateY(0); }
}
/* A dying card's chips drop 6px and go with it instead of hanging (03 §4.5). */
.cards-board-card[data-animation="dying"] .cards-board-card-stats {
  animation: cards-chips-fall var(--beat-death) steps(3, end) forwards;
}
@keyframes cards-chips-fall {
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(6px); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-board-card[data-animation^="shield-"]::before { animation: none; opacity: 1; transform: none; }
}
/* Перегрев and Заморозка flash their own family's ring (03 §4.6–4.7). */
.cards-board-card[data-animation^="heat-"] { --cards-cue-ring: var(--c-heat); }
.cards-board-card[data-animation^="freeze-"] { --cards-cue-ring: var(--c-freeze); }
/* Перегрев N wears a four-step scale under its number (02 §3.4); at 3 — the
   step before it melts — the plate blinks. */
/* Keyed on data-level as well, so it outranks the plain plate rule after it. */
.cards-board-card-statuses [data-tone="heat"][data-level] {
  --cards-heat-fill: 25%;
  padding-bottom: calc(var(--stroke-m) + 1px);
  background:
    repeating-linear-gradient(to right, transparent 0 calc(25% - 1px), var(--cards-tone-face) calc(25% - 1px) 25%) left bottom / 100% var(--stroke-m) no-repeat,
    linear-gradient(to right, var(--c-outline) 0 var(--cards-heat-fill), color-mix(in srgb, var(--c-outline) 30%, transparent) var(--cards-heat-fill)) left bottom / 100% var(--stroke-m) no-repeat,
    var(--cards-tone-face);
}
.cards-board-card-statuses [data-tone="heat"][data-level="2"] { --cards-heat-fill: 50%; }
.cards-board-card-statuses [data-tone="heat"][data-level="3"] {
  --cards-heat-fill: 75%;
  animation: cards-board-aim-blink var(--dur-blink) var(--steps-blink) infinite alternate;
}
/* Frozen: four pixel ice triangles in the corners of the art window. */
.cards-board-card:is([data-frozen="true"], [data-animation="freeze-positive"]) .cards-board-card-art::before {
  content: "";
  position: absolute;
  z-index: 2;
  inset: 0;
  background:
    ${ICE_CORNERS};
  pointer-events: none;
}
/* A frozen card does not tilt (02 §3.4). */
.cards-board-card[data-frozen="true"].cards-tilt[data-tilting="true"] { transform: none; }
/* Frozen +N: the corners snap on one by one, the snowflake hops. */
.cards-board-card[data-animation="freeze-negative"] .cards-board-card-art::before {
  animation: cards-ice-snap var(--beat-status-change) step-end;
}
@keyframes cards-ice-snap {
  0% { clip-path: polygon(0 0, 50% 0, 50% 50%, 0 50%, 0 50%, 0 50%); }
  25% { clip-path: polygon(0 0, 100% 0, 100% 50%, 50% 50%, 0 50%, 0 50%); }
  50% { clip-path: polygon(0 0, 100% 0, 100% 50%, 50% 50%, 50% 100%, 0 100%); }
  75%, 100% { clip-path: polygon(0 0, 100% 0, 100% 100%, 50% 100%, 50% 100%, 0 100%); }
}
/* Thawed −N: the corners drop 6px and go out in steps. */
.cards-board-card[data-animation="freeze-positive"] .cards-board-card-art::before {
  animation: cards-ice-fall var(--beat-status-change) steps(3, end) forwards;
}
@keyframes cards-ice-fall {
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(6px); }
}
/* The family's plate hops when its number moves (03 §4.4). */
.cards-board-card[data-animation^="heat-"] .cards-board-card-statuses [data-tone="heat"],
.cards-board-card[data-animation^="freeze-"] .cards-board-card-statuses [data-tone="freeze"] {
  animation: cards-chip-bump var(--beat-status-change) linear;
}
/* A frozen card's attack spent standing still (03 §4.7): it tries, and sticks —
   two held ±2px frames under the −1. */
.cards-board-card[data-animation="freeze-skip"] {
  animation: cards-freeze-stuck var(--beat-status-change) step-end;
}
@keyframes cards-freeze-stuck {
  0% { transform: translateX(-2px); }
  20% { transform: translateX(2px); }
  40%, 100% { transform: translateX(0); }
}
.cards-board-card-playback-delta[data-tone="heat"] { color: var(--c-heat); }
.cards-board-card-playback-delta[data-tone="freeze"] { color: var(--c-freeze); }
@media (prefers-reduced-motion: reduce) {
  .cards-board-card-statuses [data-tone="heat"][data-level="3"] { animation: none; }
}
.cards-board-card-art,
.cards-staged-card-art {
  position: absolute;
  /* The bottom clearance leaves room for the name row and the stat strip.
     Staged cards and flight ghosts share it so a landing/reveal never shifts
     the glyph. */
  inset: 3px 3px 34px;
  display: grid;
  place-items: center;
  /* The art window (01 §10): light on every face, so authored palettes read
     the same on a cream unit and an indigo spell. */
  border-radius: var(--radius-s);
  background: var(--c-card-art-window);
  box-shadow: var(--cards-card-window-inset);
}
/* The name strip (02 §3.2 S): ink with cream text on your cards, the rival's
   pink with ink text on theirs. It stays above the chips, where the hand card
   keeps its name, so the laydown flight does not rebuild the face. */
.cards-board-card-name {
  position: absolute;
  z-index: 4;
  /* The paired insets define the clipping width; override the shared 100%. */
  width: auto;
  inset-inline: 3px;
  bottom: 20px;
  box-sizing: border-box;
  padding: 1px var(--space-1);
  border-radius: var(--radius-xs);
  background: var(--cards-card-edge);
  color: var(--c-text-on-dark);
  font: var(--type-text-xs);
  line-height: 1.1;
  text-align: left;
}
.cards-board-location-half[data-side="opponent"] .cards-board-card-name { color: var(--c-text-on-card); }
/* Spells have no attack/health pair: use that stat strip for their name, so
   the label lands on the same visual row as a unit's combat values. */
.cards-board-card[data-card-type="SPELL"] .cards-board-card-name,
.cards-staged-card[data-card-type="SPELL"] .cards-board-card-name {
  bottom: 5px;
}
/* Face-down "card back": a dark themed cover with the centered pixel emblem,
   drawn over the (light) face during the first half of the reveal flip. */
.cards-board-card-back {
  position: absolute;
  inset: 0;
  z-index: 9;
  display: grid;
  place-items: center;
  border-radius: inherit;
  /* The card back (01 §10): opaque dark felt, so the face is fully hidden, its
     diamond weave and a gold frame around the gold mark. */
  background: var(--c-card-back);
}
.cards-board-card-back::after {
  content: '';
  position: absolute;
  inset: var(--space-1);
  border: var(--stroke-m) solid var(--c-card-back-mark);
  border-radius: var(--radius-xs);
  pointer-events: none;
}
.cards-board-card-back-pattern {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.cards-board-card-back > [data-testid="card-back"] {
  position: relative;
  z-index: 1;
  /* Five pixels per cell of the 9×9 mark; a compact cell shrinks it by whole
     cells (01 §10). */
  width: max(9px, round(down, min(45px, 70%), 9px));
  height: max(9px, round(down, min(45px, 70%), 9px));
}
/* Round-start lineup placeholder (03 §4.2): the backs are dealt one after
   another, a thirteenth of the lineup beat apart (40ms at ×1), at most six
   steps deep so the last one lands inside the beat at either speed. */
.cards-board-card-lineup {
  cursor: default;
  animation: cards-facedown-in calc(var(--beat-reveal-lineup) / 2) var(--ease-overshoot) backwards;
  animation-delay: calc(min(var(--cards-lineup-order, 0), 6) * var(--beat-reveal-lineup) / 13);
}
@keyframes cards-facedown-in {
  from { opacity: 0; transform: scale(.82) translateY(-6px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-board-card-lineup { animation: none; }
}
.cards-board-card-statuses,
.cards-board-card-m14-markers {
  position: absolute;
  z-index: 2;
  top: 4px;
  display: flex;
  gap: 2px;
  font: var(--type-display-2xs);
  line-height: 1;
}
/* A column down the art window's left edge; control and virus markers take
   the right one (02 §3.2 S). */
.cards-board-card-statuses {
  left: 4px;
  flex-direction: column;
  align-items: flex-start;
  max-height: calc(100% - 40px);
  overflow: hidden;
}
.cards-board-card-m14-markers { right: 4px; flex-direction: column; align-items: flex-end; }
/* Each status is a plate in its family's colour (04 §2.1); the icon names it. */
.cards-board-card-statuses [data-tone],
.cards-board-card-m14-markers [data-tone] {
  --cards-tone-face: ${STATUS_FAMILY_TONES.neutral.face};
  --cards-tone-ink: ${STATUS_FAMILY_TONES.neutral.ink};
  display: inline-flex;
  align-items: center;
  gap: 1px;
  padding: 1px;
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  background: var(--cards-tone-face);
  color: var(--cards-tone-ink);
}
:is(.cards-board-card-statuses, .cards-board-card-m14-markers) .cards-pixel-icon {
  width: 14px;
  height: 14px;
  vertical-align: 0;
}
/* Each family's colours come from STATUS_FAMILY_TONES (cardStatus.ts), the table
   the large card's status rows read too. */
${statusFamilyToneRules()}
.cards-board-card-stats,
.cards-staged-card-stats {
  position: absolute;
  z-index: 4;
  /* Board cards, staged cards and flight ghosts share the exact offsets so a
     landing cross-dissolve or a reveal never nudges the stat strip. */
  inset-inline: 2px;
  bottom: 2px;
  display: flex;
  /* Never wrap. The strip is anchored to the card's bottom edge, so a second
     line grows upward — straight into the name row 14px above it. On a compact
     phone cell (~33px wide) two two-digit numbers did not fit on one line and
     the wrapped strip printed over the card's name. */
  flex-wrap: nowrap;
  justify-content: space-between;
  gap: 1px;
  line-height: 1;
  white-space: nowrap;
}
.cards-board-card-stats .cards-card-stat,
.cards-staged-card-stats .cards-card-stat {
  /* Anchor attack to the left and health to the right edge of the field card. */
  flex: 0 0 auto;
}
.cards-card-stat {
  min-width: 0;
  display: inline-flex;
  align-items: baseline;
}
/* A stat change rises out of the badge it moved, so which stat grew is read
   from position — no room for a label at the field-card scale. The anchor is
   the badge itself; the chip clears it upward into the card art. */
.cards-board-card-stats .cards-card-stat { position: relative; }
.cards-card-stat-delta {
  position: absolute;
  left: 50%;
  bottom: 100%;
  z-index: 6;
  margin-bottom: 2px;
  color: var(--c-positive);
  font: var(--type-display-xs);
  line-height: 1;
  text-shadow: var(--text-outline-1);
  pointer-events: none;
  animation: cards-stat-delta var(--beat-stat-buff) var(--ease-out) forwards;
}
.cards-card-stat-delta[data-negative="true"] {
  color: var(--c-negative);
  animation-name: cards-stat-delta-down;
}
/* The value itself pops: the chip says how much, the bump says the number
   under it is the one that just changed. Scale only — colour does not animate. */
.cards-board-card-stats .cards-card-stat[data-delta] .cards-card-stat-value {
  display: inline-block;
  animation: cards-stat-bump var(--beat-stat-buff) var(--ease-out);
}
/* A stat's icon is a 1em pixel icon at 1× (01 §9); the value's base size is display-s. */
.cards-card-stat-icon { font-size: var(--icon-s); }
.cards-card-stat-value { font-size: var(--type-display-s-size); }
/* Combat chips on the hand, the board and the plan (02 §3.1; the recipe is
   kit.css.ts). The board's are a size smaller. The icons stay in the markup for
   screen readers' neighbours and come back on the large card only. */
:is(.cards-hand-stats, .cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat {
  justify-content: center;
  align-items: center;
  font-size: 0;
}
:is(.cards-hand-stats, .cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat-icon { display: none; }
/* A step larger than the first cut (Q14): the hand's numbers are display-xs
   (12px) and the board's display-2xs (10px) — display-3xs stays for compact
   cards of 74px and less (01 §3.2). */
.cards-hand-stats .cards-card-stat { min-width: 28px; height: 20px; padding: 0 3px; }
.cards-hand-stats .cards-card-stat-value { font: var(--type-display-xs); line-height: 1; }
.cards-board-card-stats .cards-card-stat,
.cards-staged-card-stats .cards-card-stat { min-width: 22px; height: 18px; padding: 0 2px; }
.cards-board-card-stats .cards-card-stat-value,
.cards-staged-card-stats .cards-card-stat-value { font: var(--type-display-2xs); line-height: 1; }
/* Persistent marks (02 §3.3). A wounded card: a red bar along the bottom of its
   health chip and a crack in the corner of its art window. A value off the
   printed one: a corner on its chip — gold at the top above it, ink at the
   bottom below it. */
.cards-board-card[data-wounded="true"] .cards-board-card-stats .cards-card-stat:last-child {
  --cards-chip-mark: inset 0 calc(var(--stroke-l) * -1) 0 var(--c-negative);
}
.cards-board-card[data-wounded="true"] .cards-board-card-art::after {
  content: '';
  position: absolute;
  right: var(--space-1);
  bottom: var(--space-1);
  width: 14px;
  height: 14px;
  background: ${pixelIconDataUri('crack', COLORS.negative)} center / contain no-repeat;
  image-rendering: pixelated;
}
.cards-board-card-stats .cards-card-stat[data-stat-change]::before {
  content: '';
  position: absolute;
  right: 0;
  width: 7px;
  height: 7px;
  pointer-events: none;
}
.cards-board-card-stats .cards-card-stat[data-stat-change="up"]::before {
  top: 0;
  background: ${pixelIconDataUri('statUp', COLORS.selection)} center / contain no-repeat;
}
.cards-board-card-stats .cards-card-stat[data-stat-change="down"]::before {
  bottom: 0;
  background: ${pixelIconDataUri('statDown', COLORS.outline)} center / contain no-repeat;
}
/* Code-native pixel glyphs (PixelIcon): inherit the surrounding text colour via
   currentColor and size to the local font so they drop in where text glyphs sat. */
.cards-pixel-icon {
  display: inline-block;
  width: 1em;
  height: 1em;
  vertical-align: -0.1em;
}
.cards-card-stat-icon .cards-pixel-icon { width: 1.2em; height: 1.2em; }

/* Staged cards share the light board-card surface (set on the base rule above);
   the warning ring just marks them as "preparing", and the art shows at full
   opacity like the hand card it flew in from. */
/* A card in your plan lies face up (04 §1.3): a gold dashed ring around it and
   a «ПЛАН» tag on the top card of the stack. */
.cards-staged-card {
  outline: var(--stroke-m) dashed var(--c-selection);
  outline-offset: var(--stroke-m);
  box-shadow: var(--cards-card-bevel), 0 var(--step-3) 0 var(--cards-card-step-color), var(--cards-card-rim);
  animation: cards-stage-land var(--dur-move) linear;
  cursor: pointer;
  transition: transform var(--dur-move) var(--ease-out);
}
/* The plan tag (04 §1.1, §1.3): one gold label for a planned card — «ПЛАН» on
   its board card, «В ПЛАНЕ» on its hand card. Each sets only its text and place. */
.cards-staged-card:not([data-buried="true"])::before,
.cards-hand-card[data-staged="true"]::before {
  position: absolute;
  z-index: 5;
  padding: 2px var(--space-1);
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  background: var(--c-energy);
  color: var(--c-text-on-energy);
  font: var(--type-display-3xs);
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
}
.cards-staged-card:not([data-buried="true"])::before {
  content: 'ПЛАН';
  top: var(--space-1);
  right: var(--space-1);
}
/* A staged (preparing) card tilts and glints under the pointer like the hand
   and the sandbox catalog: the same stepped sheen, positioned by the
   pointer-motion vars written by applyCardPointerMotion (Cell.tsx). */
.cards-staged-card::after {
  --cards-sheen-at: var(--cards-motion-light-x, 50%) var(--cards-motion-light-y, 30%);
  content: "";
  z-index: 6;
  opacity: 0;
  transition: opacity var(--dur-base) var(--ease-out), -webkit-mask-position var(--dur-base) var(--ease-out), mask-position var(--dur-base) var(--ease-out);
}
.cards-staged-card[data-motion-active="true"] {
  transform:
    perspective(720px)
    rotateX(var(--cards-motion-rotate-x, 0deg))
    rotateY(var(--cards-motion-rotate-y, 0deg));
  transition-duration: var(--dur-tap);
  will-change: transform;
}
.cards-staged-card[data-motion-active="true"]::after { opacity: 1; }
.cards-staged-card[data-drag-enabled="true"] {
  cursor: grab;
  touch-action: none;
}
.cards-cell-reservation {
  position: absolute;
  right: 3px;
  top: 3px;
  padding: 2px 4px;
  font: var(--type-display-3xs);
  line-height: 1;
}
.cards-cell-reservation[data-has-effect-badges="true"] { top: 22px; }
.cards-cell-effect-badges {
  position: absolute;
  z-index: 6;
  /* Constrained to the cell on both sides so a badge can never grow wider
     than the (narrow mobile) cell underneath it. */
  left: 3px;
  right: 3px;
  top: 3px;
  display: grid;
  justify-items: end;
  gap: 2px;
  /* Like the badges themselves: taps must fall through to the card/cell. */
  pointer-events: none;
}
/* Doubled class: GLOBAL_BASE_CSS is assembled BEFORE the kit rules, so a bare
   .cards-cell-effect-badge loses to .cards-kit-badge (same specificity, later
   in the sheet) and the badge silently renders at the 10px pill defaults. */
/* A cell effect is a cream plate (02 §4.3): the effect's icon in its family's
   colour — the slime's residue green, thorns red — and the count in ink. */
.cards-kit-badge.cards-cell-effect-badge {
  gap: 1px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  border: var(--stroke-m) solid var(--c-outline);
  border-radius: var(--radius-xs);
  padding: 2px 3px;
  background: var(--c-plate-light);
  color: var(--c-text-on-card);
  font: var(--type-display-3xs);
  line-height: 1;
  pointer-events: none;
}
.cards-cell-effect-badge .cards-pixel-icon[data-icon="droplet"] { color: var(--c-positive); }
.cards-cell-effect-badge .cards-pixel-icon[data-icon="thorns"] { color: var(--c-negative); }

/* Stable-height hand row. It shrinks before the board does when space is tight,
   and extra cards scroll rather than pushing the battlefield. */
.cards-hand-area {
  display: flex;
  flex: 0 1 300px;
  min-height: 0;
  align-items: stretch;
  gap: 10px;
  min-width: 0;
}
.cards-hand-area > .cards-hand {
  order: 0;
  flex: 1 1 0;
  min-width: 0;
}
.cards-hand {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-top: var(--stroke-s) solid color-mix(in srgb, var(--c-text-on-dark) 12%, transparent);
  padding: 8px;
}
/* Carrying a staged card back: the hand lights up as the place to drop it. */
.cards-hand[data-return-active="true"][data-pointer-drag-over="true"] {
  border-top-color: var(--c-selection);
  background: color-mix(in srgb, var(--c-selection) 12%, transparent);
  box-shadow: inset 0 0 0 var(--stroke-m) var(--c-selection);
}
.cards-hand-bar {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}
/* The hand's hint (05 §2.1): plain muted text, not a caps heading. */
.cards-hand-title {
  color: var(--c-text-muted-on-table);
  font: var(--type-text-s);
  line-height: 14px;
}
.cards-hand-list {
  /* Anchors the out-of-flow carried card while its placeholder stays in flow. */
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-wrap: wrap;
  /* Centre the rows in the region; when they outgrow it, scroll from the top
     (safe center). Top padding keeps the hover lift from being clipped. */
  align-content: safe center;
  gap: 8px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px 2px 4px;
}
.cards-hand-card {
  --cards-motion-rotate-x: 0deg;
  --cards-motion-rotate-y: 0deg;
  --cards-motion-rotate-z: 0deg;
  --cards-motion-lift: 0px;
  --cards-motion-scale: 1;
  --cards-motion-light-x: 50%;
  --cards-motion-light-y: 30%;
  position: relative;
  /* The FLIP pass in Hand.tsx rewrites every card's CSS order and then reads
     offsetLeft/offsetTop, so each carry frame forces a synchronous layout of the
     whole strip. Layout containment cannot remove that read — the card's position
     depends on its ancestors either way — but it makes each card's interior its
     own scope, so re-flowing the strip positions the cards without laying out the
     face inside them (glyph, name track, stat strip) again because a sibling
     moved. Layout only: not size, so the box keeps being sized by the rules below
     and by the mobile breakpoint that replaces them, and not paint, because the
     hover lift and the frame glow deliberately spill outside the box. */
  contain: layout;
  width: 112px;
  min-width: 112px;
  height: 138px;
  flex: 0 0 112px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  border: var(--stroke-l) solid var(--cards-card-edge);
  border-radius: var(--radius-l);
  padding: 25px 6px 6px;
  background: var(--cards-card-face);
  color: var(--cards-card-ink);
  --cards-card-step: var(--step-4);
  box-shadow:
    var(--cards-card-ring), var(--cards-card-bevel), 0 var(--cards-card-step) 0 var(--cards-card-step-color), var(--cards-card-rim);
  cursor: grab;
  touch-action: pan-x;
  backface-visibility: hidden;
  transform-origin: 50% 68%;
  /* FLIP slide (Balatro-style): when the hand re-lays out (a card is carried
     away, returns, or is drawn/played), Hand.tsx sets the inverse offset in
     these variables and releases it, so cards glide to their new slots. The
     draw-in offset composes on top from its own pair, never shares this one. */
  /* The selection lift rides its own variable instead of a transform of its
     own, so the hover and tilt chains below can carry it: a rule that restated
     transform without it dropped a selected card back into the row the moment
     the pointer touched it. */
  transform:
    translate(var(--cards-flip-x, 0px), var(--cards-flip-y, 0px))
    translate(var(--cards-draw-x, 0px), var(--cards-draw-y, 0px))
    translateY(var(--cards-select-lift, 0px));
  transition:
    transform var(--dur-move) var(--ease-overshoot),
    opacity var(--dur-quick) var(--ease-out),
    rotate var(--dur-move) var(--ease-overshoot),
    translate var(--dur-move) var(--ease-out);
}
/* Fallback arrival for a drawn card, used only where its own slot cannot be
   measured (tests, an unlaid-out hand). The real trip in from off-screen is an
   overlay flight ghost — see Hand.tsx: this region clips its overflow and paints
   below the board, so nothing that crosses the screen can happen inside it.
   The movement animates registered variables, so it survives the mobile
   breakpoint's !important transform chain.
   Its own pair, not the FLIP one: an animation outranks inline style in the
   cascade, so sharing the variables meant the arriving card silently dropped
   out of any FLIP glide running at the same time — most visibly the re-flow
   into a second row that the fourth card itself triggers. */
@property --cards-flip-x { syntax: '<length>'; inherits: false; initial-value: 0px; }
@property --cards-flip-y { syntax: '<length>'; inherits: false; initial-value: 0px; }
@property --cards-draw-x { syntax: '<length>'; inherits: false; initial-value: 0px; }
@property --cards-draw-y { syntax: '<length>'; inherits: false; initial-value: 0px; }
@keyframes cards-hand-card-draw-in {
  from {
    opacity: 0;
    --cards-draw-x: 110px;
    --cards-draw-y: -18px;
  }
  30% { opacity: 1; }
}
/* More specific than the resting UNIT/SPELL frame pulse declared in the
   sandbox surface below: a real draw must temporarily own its animation. */
.cards-hand-card[data-card-type][data-drawn="true"] {
  /* Paired with TURN_PLAYBACK_TIMING.cardDraw: playback waits exactly this long,
     so the card is home before the next event moves. */
  animation: cards-hand-card-draw-in var(--beat-card-draw) var(--ease-out) backwards;
  /* The resting transform transition is what the FLIP slide rides on: every
     change to these variables eases over 260ms. A card arriving from the deck
     has no FLIP of its own, and letting the transition chase an offset that the
     animation already moves every frame only smears the deal a quarter second
     behind itself. Opacity keeps its transition. */
  transition: opacity var(--dur-quick) var(--ease-out);
}
/* A card just dealt into the hand marks itself once as it settles, so the new
   card is the one the eye finds among the old: a ring springs off its frame and
   a glint crosses its face. Specific enough to outrank both the resting frame
   pulse and the no-energy rule, which would otherwise silence it. */
.cards-hand-card[data-fresh="true"]:not([data-staged="true"])::before {
  content: "";
  position: absolute;
  inset: calc(var(--stroke-l) * -2);
  border: var(--stroke-l) solid var(--c-selection);
  border-radius: calc(var(--radius-l) + var(--stroke-l));
  pointer-events: none;
  opacity: 0;
  animation: cards-hand-card-fresh-ring var(--dur-emphasis) step-end;
}
.cards-hand-card[data-fresh="true"]::after {
  animation: cards-hand-card-fresh-glint var(--dur-emphasis) linear;
}
/* Two frames of the selection ring (03 §3.1), held, not faded. */
@keyframes cards-hand-card-fresh-ring {
  0% { opacity: 1; }
  25% { opacity: 0; }
  50% { opacity: 1; }
  75%, 100% { opacity: 0; }
}
@keyframes cards-hand-card-fresh-glint {
  0% { opacity: 0; -webkit-mask-position: 100% 100%; mask-position: 100% 100%; }
  15% { opacity: 1; }
  70% { opacity: 1; -webkit-mask-position: 0% 0%; mask-position: 0% 0%; }
  100% { opacity: 0; -webkit-mask-position: 0% 0%; mask-position: 0% 0%; }
}
.cards-hand-reorder-placeholder {
  width: 112px;
  min-width: 112px;
  height: 138px;
  flex: 0 0 112px;
  box-sizing: border-box;
  border: var(--stroke-m) dashed color-mix(in srgb, var(--c-selection) 60%, transparent);
  border-radius: var(--radius-l);
  background: color-mix(in srgb, var(--c-selection) 8%, transparent);
  pointer-events: none;
  transform: translate(var(--cards-flip-x, 0px), var(--cards-flip-y, 0px));
  transition: transform var(--dur-move) var(--ease-overshoot);
}
.cards-hand-card::after {
  /* The shared sheen (kit.css.ts), positioned by the pointer-motion vars
     instead of the tilt vars. */
  --cards-sheen-at: var(--cards-motion-light-x) var(--cards-motion-light-y);
  content: "";
  z-index: 5;
  opacity: 0;
  transition: opacity var(--dur-base) var(--ease-out), -webkit-mask-position var(--dur-base) var(--ease-out), mask-position var(--dur-base) var(--ease-out);
}
/* Hover and tilt keep the card's resting footprint (no lift, no scale — the
   card stays exactly where the pointer found it), but they restate the whole
   resting chain in front of the 3D part: dropping the FLIP pair made the card
   under the pointer jump instead of gliding while the hand re-flowed. */
@media (hover: hover) and (pointer: fine) {
  .cards-hand-card[data-drag-enabled="true"]:hover {
    z-index: 6;
    transform:
      translate(var(--cards-flip-x, 0px), var(--cards-flip-y, 0px))
      translate(var(--cards-draw-x, 0px), var(--cards-draw-y, 0px))
      translateY(var(--cards-select-lift, 0px))
      perspective(720px);
  }
  .cards-hand-card[data-drag-enabled="true"]:hover::after { opacity: .5; }
}
/* Scoped through .cards-hand-list to match the [data-drag-enabled]:hover
   specificity (0,3,0) and sit after it, so the pointer-tilt transform wins.
   With the bare [data-motion-active] selector (0,2,0) the hover rule above
   overrode it and rotateX/Y/Z never applied on desktop. The sandbox catalog
   reuses the same cards + motion vars, so it needs the same scoped rule. */
.lab-sandbox-catalog-grid .cards-hand-card[data-motion-active="true"],
.cards-hand-list .cards-hand-card[data-motion-active="true"] {
  z-index: 6;
  transform:
    translate(var(--cards-flip-x, 0px), var(--cards-flip-y, 0px))
    translate(var(--cards-draw-x, 0px), var(--cards-draw-y, 0px))
    translateY(var(--cards-select-lift, 0px))
    perspective(720px)
    rotateX(var(--cards-motion-rotate-x))
    rotateY(var(--cards-motion-rotate-y))
    rotateZ(var(--cards-motion-rotate-z));
  transition-duration: var(--dur-tap), var(--dur-quick);
  will-change: transform;
}
/* Depth without growing the card: while it tilts, the art drifts toward the
   pointer and swells a touch, as if it stood off the face — the side the card
   dips toward is the side a raised layer slides to. --cards-motion-nx/ny are the
   pointer's -1..1 position (applyCardPointerMotion); a percentage of the art's
   own box keeps the drift proportional on every card size. */
.cards-hand-glyph {
  transition: transform var(--dur-base) var(--ease-out);
}
.lab-sandbox-catalog-grid .cards-hand-card[data-motion-active="true"] .cards-hand-glyph,
.cards-hand-list .cards-hand-card[data-motion-active="true"] .cards-hand-glyph {
  transform:
    translate(calc(var(--cards-motion-nx, 0) * 3%), calc(var(--cards-motion-ny, 0) * 3%))
    scale(1.04);
  transition-duration: var(--dur-tap);
}
.lab-sandbox-catalog-grid .cards-hand-card[data-motion-active="true"]::after,
.cards-hand-list .cards-hand-card[data-motion-active="true"]::after { opacity: 1; }
.cards-hand-card[data-drag-enabled="true"] { touch-action: none; }
/* Selected: a gold ring outside the outline (04 §1.1), lifted 12px and turned
   2° on the overshoot (03 §3.1). The turn rides the standalone rotate property,
   so the transform chain (FLIP, draw, lift) keeps its own transition. */
.cards-hand-card[data-selected="true"] {
  --cards-card-ring: 0 0 0 var(--stroke-l) var(--c-selection);
  --cards-select-lift: -12px;
  rotate: -2deg;
}
/* The resting sway (03 §3.1), the table's one background motion: a 1.2° rock
   with a 3px rise mid-cycle, every third slot a third of a cycle apart so the
   hand never moves in step. On rotate/translate, so it composes with the
   transform chain. Only a card at rest in a live hand on a desktop sways: not
   hovered (it holds still under the pointer), chosen, carried, planned, being
   dealt, unaffordable, or while the hand is locked for playback or waiting. */
@media (hover: hover) and (pointer: fine) and (min-width: 901px) and (prefers-reduced-motion: no-preference) {
  .cards-hand-list > .cards-hand-card:not(
    [data-selected="true"], [data-staged="true"], [data-dragging="true"], [data-carried="true"],
    [data-drawing="true"], [data-drawn="true"], [data-landing="true"], [data-denied="true"],
    [data-insufficient-energy="true"], [data-planning-disabled="true"]
  ) {
    animation: cards-hand-sway var(--dur-sway) var(--ease-in-out) infinite;
  }
  .cards-hand-list > .cards-hand-card:nth-child(3n + 2) { animation-delay: calc(var(--dur-sway) / -3); }
  .cards-hand-list > .cards-hand-card:nth-child(3n) { animation-delay: calc(var(--dur-sway) * -2 / 3); }
  .cards-hand-list > .cards-hand-card:hover { animation-play-state: paused; }
}
@keyframes cards-hand-sway {
  0%, 100% { rotate: -1.2deg; translate: 0 0; }
  50% { rotate: 1.2deg; translate: 0 -3px; }
}
/* A card taken without the energy for it (03 §3.1): a ±3px shiver in held
   frames and its red coin blinking twice. */
.cards-hand-card[data-denied="true"] {
  animation: cards-hand-deny var(--dur-base) step-end;
}
.cards-hand-card[data-denied="true"] .cards-hand-cost {
  animation: cards-hand-coin-deny var(--dur-base) step-end;
}
@keyframes cards-hand-deny {
  0% { translate: -3px 0; }
  17% { translate: 3px 0; }
  33% { translate: -3px 0; }
  50% { translate: 3px 0; }
  67% { translate: -3px 0; }
  83% { translate: 3px 0; }
  100% { translate: 0 0; }
}
@keyframes cards-hand-coin-deny {
  0%, 50% { opacity: .3; }
  25%, 75%, 100% { opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .cards-hand-card[data-denied="true"] { animation: none; }
}
/* In your plan: the card loses its step, its art is hatched over, the coin goes
   out and a «В ПЛАНЕ» tag sits on its top edge (04 §1.1). */
.cards-hand-card[data-staged="true"] {
  --cards-card-step: 0px;
  cursor: default;
}
.cards-hand-card[data-staged="true"] .cards-hand-glyph::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: repeating-linear-gradient(
    135deg,
    color-mix(in srgb, var(--c-outline) 25%, transparent) 0 var(--stroke-m),
    transparent var(--stroke-m) var(--space-1)
  );
}
.cards-hand-card[data-staged="true"]::before {
  content: 'В ПЛАНЕ';
  top: calc(var(--stroke-l) * -1 - var(--space-1));
  inset-inline: 0;
  width: fit-content;
  margin-inline: auto;
}
/* An unaffordable card keeps its frame; the coin turns red and the art goes
   grey in one step, no transition (04 §1.1). */
.cards-hand-card[data-insufficient-energy="true"] {
  cursor: default;
  animation: none;
}
.cards-hand-card[data-insufficient-energy="true"] .cards-hand-glyph svg { filter: grayscale(1); }
.cards-hand-card[data-insufficient-energy="true"][data-drag-enabled="true"] { cursor: grab; }
.cards-hand-card[data-planning-disabled="true"] { cursor: default; }
.cards-hand-card[data-dragging="true"] {
  /* The carried card leaves the hand entirely — the floating preview IS the
     card. On release its movable placeholder becomes the real card beneath
     the landing ghost; the pickup itself fades out quickly. */
  opacity: 0;
  transition:
    transform var(--dur-move) var(--ease-overshoot),
    opacity var(--dur-tap) var(--ease-out);
  cursor: grabbing;
}
/* Once the drag is real (not a tap), the card leaves the layout but stays
   mounted for pointer capture. A separate placeholder occupies and moves
   between slots, making the remaining cards FLIP-glide apart around it. */
.cards-hand-card[data-carried="true"] {
  position: absolute !important;
  top: -200px !important;
  left: 0 !important;
  transition: none;
}
/* A card the playback is drawing right now: its ghost is still crossing the
   screen from beyond its edge, so the slot is empty space that only becomes the
   card when the flight lands. Hidden with no opacity transition and from the
   very commit that mounts it (Hand.tsx sets this in render, not in the layout
   effect) — a fade would show the card in the hand before it flew. */
.cards-hand-card[data-drawing="true"] {
  opacity: 0;
  transition: none;
}
/* While the parked ghost dissolves on top, the slot must already be fully
   opaque underneath — a concurrent fade-in would dip the combined brightness
   and read as a grey blink at the moment of landing. */
.cards-hand-card[data-landing="true"] {
  opacity: 1;
  transition: transform var(--dur-move) var(--ease-overshoot);
}
/* A staged cell card whose ghost is airborne: hidden (still measurable as the
   flight target), then revealed instantly beneath the dissolving ghost. The
   normal stage-land keyframes animate opacity, so they must stay disabled in
   both phases or they override the hiding rule and cause a grey landing blink. */
.cards-staged-card[data-flight-hidden="true"],
.cards-staged-card[data-flight-landing="true"],
/* Cards that flew in already animated their entrance — never replay the landing
   bounce when the flight finishes and the flight-state attributes clear. */
.cards-staged-card[data-flight-landed="true"] { animation: none; }
.cards-staged-card[data-flight-hidden="true"] { opacity: 0; }
/* A staged card carried by the pointer: the floating preview IS the card, so
   the source hides in place (still layout-measurable) instead of doubling up. */
.cards-staged-card[data-carried="true"] {
  opacity: 0;
  animation: none;
}
.cards-staged-card[data-flight-landing="true"] { opacity: 1; }
/* The hand's cost coin (02 §3.1 M; the recipe is kit.css.ts). It sits inside
   the top-left corner rather than over it — the hand strip clips anything that
   leaves the card. Red with a cream number when you cannot pay; slate once the
   card is in your plan. */
.cards-hand-cost {
  position: absolute;
  z-index: 4;
  top: 2px;
  left: 2px;
  width: 24px;
  height: 24px;
}
.cards-hand-cost .cards-card-stat { font-size: 0; }
.cards-hand-cost .cards-card-stat-icon { display: none; }
.cards-hand-cost .cards-card-stat-value { font: var(--type-display-2xs); line-height: 1; }
/* Spent on the plan: a muted cream coin — slate vanished into the dark face (Q14). */
.cards-hand-card[data-staged="true"] .cards-hand-cost {
  --cards-coin-face: var(--c-text-muted-on-card-face);
  --cards-coin-ink: var(--c-text-on-energy);
}
.cards-hand-card[data-insufficient-energy="true"] .cards-hand-cost {
  --cards-coin-face: var(--c-negative);
  --cards-coin-ink: var(--c-text-on-color);
  text-shadow: var(--text-outline-1);
}
/* The hand face mirrors the field-card geometry (raised glyph, bottom name
   row, edge-anchored stat strip) so the laydown flight lands on an almost
   identical face instead of morphing a different layout. */
.cards-hand-glyph {
  position: absolute;
  inset: 3px 3px 40px;
  display: grid;
  place-items: center;
  /* The art window (01 §10), the same as the board card's. */
  border-radius: var(--radius-s);
  background: var(--c-card-art-window);
  box-shadow: var(--cards-card-window-inset);
}
.cards-hand-name {
  position: absolute;
  z-index: 4;
  width: auto;
  inset-inline: 4px;
  bottom: 25px;
  overflow: hidden;
  color: var(--cards-card-ink);
  font: var(--type-text-xs);
  line-height: 1.2;
  text-align: left;
  white-space: nowrap;
}
/* Spells do not render a stat pair, so their name occupies its otherwise
   empty strip at the bottom of a hand card. */
.cards-hand-card[data-card-type="SPELL"] .cards-hand-name {
  bottom: 5px;
}
.cards-hand-stats {
  position: absolute;
  z-index: 4;
  inset-inline: 3px;
  bottom: 3px;
  display: flex;
  /* Same bottom anchor and the same name row above it as the field card, so the
     same no-wrap rule applies — see .cards-board-card-stats. */
  flex-wrap: nowrap;
  justify-content: space-between;
  gap: 1px;
  line-height: 1;
  white-space: nowrap;
}
.cards-hand-stats .cards-card-stat { flex: 0 0 auto; }
.cards-touch-drag-preview {
  --cards-drag-rotate-x: 0deg;
  --cards-drag-rotate-y: 0deg;
  --cards-drag-rotate-z: 2deg;
  --cards-drag-skew-x: 0deg;
  --cards-drag-skew-y: 0deg;
  --cards-drag-scale-x: 1;
  --cards-drag-scale-y: 1;
  --cards-drag-origin-x: 50%;
  --cards-drag-origin-y: 70%;
  --cards-drag-glare-shift-x: 0%;
  --cards-drag-glare-shift-y: -10%;
  --cards-drag-glare-opacity: .08;
  /* The preview's gap above the pointer: 18px, 12px on a phone. One variable so
     reduced motion keeps the same gap (03 §6). */
  --cards-drag-gap: 18px;
  position: fixed;
  z-index: 75;
  /* The pointer position lives in --cards-drag-x/y consumed by the transform
     chain below: transforms stay on the compositor, while per-frame left/top
     writes forced a relayout of the whole card face every frame. */
  left: 0;
  top: 0;
  /* Matches the resting hand card so a dragged card is the same card, full size,
     not a shrunken mini layout with clipped text. */
  width: 112px;
  height: 138px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  /* The hand card it lifted, in the air (03 §3.2): the same skin, and the step
     grown to step-lift instead of a soft fading shadow. */
  border: var(--stroke-l) solid var(--cards-card-edge);
  border-radius: var(--radius-l);
  padding: 25px 6px 6px;
  background: var(--cards-card-face);
  color: var(--cards-card-ink);
  box-shadow: var(--cards-card-bevel), var(--cards-card-rim), 0 var(--step-lift) 0 var(--c-step-lift);
  overflow: hidden;
  pointer-events: none;
  backface-visibility: hidden;
  transform:
    translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
    translate(-50%, calc(-100% - var(--cards-drag-gap)))
    perspective(620px)
    rotateX(var(--cards-drag-rotate-x))
    rotateY(var(--cards-drag-rotate-y))
    rotateZ(var(--cards-drag-rotate-z))
    skewX(var(--cards-drag-skew-x))
    skewY(var(--cards-drag-skew-y))
    scale(
      calc(var(--cards-drag-scale-x) + .04),
      calc(var(--cards-drag-scale-y) + .04)
    );
  transform-origin: var(--cards-drag-origin-x) var(--cards-drag-origin-y);
  will-change: transform;
}
/* Fly-home ghost: its box is the measured release footprint rather than the
   breakpoint's fixed preview size (mobile slots differ), so the JS-driven custom
   properties must win. The transform drops the drag preview's +.04 scale bonus
   (baked into the motion instead, which relaxes to exactly 1) and uses the
   measured anchor gap; the airborne shadow fades along the travel — at rest the
   ghost is pixel-identical to the slot card, so the landing cross-dissolve is
   invisible. The sandbox's passive flight resizes this box per frame; the battle
   flight does not — it overrides everything visual here and morphs three
   fixed-size layers by transform instead (see data-return-morph below). */
.cards-touch-drag-preview[data-return-flight="true"] {
  width: var(--cards-return-w, 112px) !important;
  height: var(--cards-return-h, 138px) !important;
  min-width: 0 !important;
  /* The lifted card's own depth (card skin): bevel, rim and the step-lift. */
  box-shadow: var(--cards-card-bevel), var(--cards-card-rim), 0 var(--step-lift) 0 var(--c-step-lift) !important;
  transform:
    translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
    translate(-50%, calc(-100% - var(--cards-return-gap, 18px)))
    perspective(620px)
    rotateX(var(--cards-drag-rotate-x))
    rotateY(var(--cards-drag-rotate-y))
    rotateZ(var(--cards-drag-rotate-z))
    skewX(var(--cards-drag-skew-x))
    skewY(var(--cards-drag-skew-y))
    scale(var(--cards-drag-scale-x), var(--cards-drag-scale-y)) !important;
}
/* A watched flight should remain visible across the battle chrome just like the
   live preview. The sandbox has an open bottom sheet at z-index 40, so battle
   uses a layer that clears it without changing the sandbox's own stacking. */
.cards-game-view .cards-touch-drag-preview[data-return-flight="true"] { z-index: 39; }
.cards-touch-drag-preview::after {
  /* The one stepped sheen (kit.css.ts) on a double-size layer that slides with
     the tilt: moving the layer keeps it on the compositor, where moving the mask
     would repaint every frame. The band crosses the layer's centre. */
  --cards-sheen-at: 50% 50%;
  content: "";
  z-index: 3;
  inset: -50%;
  width: 200%;
  height: 200%;
  border-radius: 0;
  -webkit-mask-size: 100% 100%;
  mask-size: 100% 100%;
  /* At full tilt the band is as strong as a hovered hand card's. */
  opacity: min(1, calc(var(--cards-drag-glare-opacity) * 2.4));
  transform: translate3d(
    var(--cards-drag-glare-shift-x),
    var(--cards-drag-glare-shift-y),
    0
  );
  will-change: transform;
}
/* Transform-only morph — the battle card flight (data-return-morph).
   The ghost used to BE the box that resized: the rAF loop wrote its width and
   height every frame, and width/height is layout, not compositing. A layout of
   this element is a layout of a whole hand card plus the compact staged face it
   cross-fades into — and that face is a size container, so its cqh-driven stat
   sizes were re-resolved on every frame of the flight. That is the hitch a card
   flying into a cell had on a phone.
   So the box stops changing. The ghost is a fixed-size transform carrier: its
   layout box is the release footprint and it keeps the drag chain verbatim, so
   translate(-50%, calc(-100% - gap)) and the grab-point transform-origin resolve
   against exactly the box the live preview had and the handover is continuous.
   Under it are three layers of FIXED size, each laid out once and thereafter
   moved only by transform and faded only by opacity: the drop-shadow plate and
   the hand face at the source size, the compact staged face at the destination's.
   cardFlight.tsx writes those transforms and opacities inline, per frame. */
.cards-touch-drag-preview[data-return-flight="true"][data-return-morph="true"] {
  width: var(--cards-return-src-w, 112px) !important;
  height: var(--cards-return-src-h, 138px) !important;
  padding: 0 !important;
  border: none !important;
  background: none !important;
  box-shadow: none !important;
  /* The faces are the card now, and a destination cell is wider than a hand card
     on a big phone — clipping to the carrier would cut the compact face in half.
     Each face clips its own contents instead, exactly as the element it lands on
     does. */
  overflow: visible !important;
}
/* …which is also why the lifted card's shine cannot stay: it is a pseudo twice
   the card's size, kept in bounds only by the overflow the carrier just gave up.
   The live preview keeps it; over a 300ms flight at .08 alpha it is not what the
   eye is following anyway, and this drops a full-size gradient layer from every
   frame of one. */
.cards-touch-drag-preview[data-return-morph="true"]::after { content: none; }
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-shadow,
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-hand,
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-compact {
  position: absolute;
  bottom: 0;
  left: 50%;
  box-sizing: border-box;
  border-radius: var(--radius-s);
  /* Bottom centre: a flight anchors its card by the bottom edge plus the measured
     preview gap, so that is the one point which must not move while the box grows
     or shrinks. translateX sits to the LEFT of the scale on purpose — it is
     applied in the carrier's space, unscaled, so it keeps centring the face at
     whatever morph scale the frame hands it. */
  transform-origin: 50% 100%;
  transform: translateX(-50%) scale(1);
  will-change: transform, opacity;
  backface-visibility: hidden;
}
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-shadow,
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-hand {
  width: var(--cards-return-src-w, 112px);
  height: var(--cards-return-src-h, 138px);
}
/* The airborne step is a layer of its own: it rasterises once and the flight
   only fades it (opacity), so the step lands with the card instead of being
   repainted every frame. Card-faced, so the cross-fade above can never let the
   step's colour show through the box it belongs to. */
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-shadow {
  z-index: 0;
  border-radius: var(--radius-l);
  background: var(--cards-card-face);
  box-shadow: 0 var(--step-lift) 0 var(--c-step-lift);
}
/* The chrome the carrier gave up, now on each face at its own fixed size. The
   hand face is the drag preview's box down to the padding; the compact face is
   the destination's border box, so its padding box is the landed card's border
   box less that frame — which is the geometry the staged face documents below. */
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-hand {
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 25px 6px 6px;
  overflow: hidden;
}
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-compact {
  z-index: 2;
  width: var(--cards-return-dst-w, 112px);
  height: var(--cards-return-dst-h, 138px);
  /* Overridden inline from the first frame on; 0 here so a compact face cannot
     flash over the hand face on the commit that mounts it. */
  opacity: 0;
}
/* Each face wears the skin of the card it stands for: the hand card's 3px frame
   and radius, the field card's 2px ones (02 §3). */
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-hand,
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-compact {
  border: var(--stroke-l) solid var(--cards-card-edge);
  border-radius: var(--radius-l);
  background: var(--cards-card-face);
  color: var(--cards-card-ink);
  box-shadow: var(--cards-card-bevel), var(--cards-card-rim);
}
.cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-compact {
  border-width: var(--stroke-m);
  border-radius: var(--radius-m);
}
.cards-card-detail-select.cards-kit-button { flex: 1 1 0; }

/* Button (design system 02 §2.1): a raised plaque — dark rim and a solid step
   below it. Hover lifts it onto a taller step, a press sinks it onto its step
   (01 §7). Only transform is transitioned; the step itself changes in one
   frame, the pixel way (03 §7). */
.cards-kit-button {
  --cards-button-step: var(--step-3);
  --cards-button-step-color: var(--c-step-shadow);
  --cards-button-face: var(--c-panel-raised);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  box-sizing: border-box;
  max-width: 100%;
  border: var(--stroke-l) solid var(--c-outline);
  border-radius: var(--radius-xl);
  background: var(--cards-button-face);
  box-shadow: 0 var(--cards-button-step) 0 var(--cards-button-step-color);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: transform var(--dur-quick) var(--ease-out);
}
.cards-kit-button--md { min-height: 48px; padding: 0 var(--space-6); }
.cards-kit-button--sm {
  min-height: 36px;
  padding: 0 var(--space-4);
  font: var(--type-text-s);
  font-weight: 600;
}
.cards-kit-button--compact,
.cards-card-detail-actions-inline .cards-kit-button {
  min-height: 32px;
  border-radius: var(--radius-m);
  padding: 0 var(--space-3);
  font: var(--type-text-s);
  font-weight: 600;
}
/* An icon-only plaque (02 §2.1): a 22px round button for a corner action on a
   card — remove from a slot, add or take one. One size instead of a round
   button invented per place. */
.cards-kit-button--icon {
  --cards-button-step: var(--step-1);
  width: 22px;
  height: 22px;
  min-height: 0;
  border-width: var(--stroke-m);
  border-radius: var(--radius-round);
  padding: 0;
  font: var(--type-display-2xs);
  line-height: 1;
}
.cards-kit-button--icon .cards-pixel-icon { width: 14px; height: 14px; }
/* Short plain-text labels (Button marks them --display) take the pixel face,
   upper-cased by CSS so the markup keeps its natural case (01 §3.3). */
.cards-kit-button--md.cards-kit-button--display { font: var(--type-display-s); text-transform: uppercase; }
.cards-kit-button--sm.cards-kit-button--display { font: var(--type-display-xs); text-transform: uppercase; }
.cards-kit-button--primary {
  --cards-button-step: var(--step-4);
  --cards-button-step-color: var(--c-action-step);
  --cards-button-face: var(--c-action);
  color: var(--c-text-on-action);
}
.cards-kit-button--secondary { --cards-button-face: var(--c-panel-raised); }
.cards-kit-button--danger {
  --cards-button-step-color: var(--c-danger-step);
  --cards-button-face: var(--c-negative);
  color: var(--c-text-on-color);
  text-shadow: var(--text-outline-1);
}
/* The 2px outline is drawn for the pixel face; on the reading face it would
   swallow 13–15px letters. */
.cards-kit-button--danger.cards-kit-button--display { text-shadow: var(--text-outline-2); }
.cards-kit-button--ghost {
  --cards-button-step: 0px;
  border-color: transparent;
  background: transparent;
  box-shadow: none;
}
/* Hover: lighter face, lifted 2px onto a step 2px taller (02 §1). */
@media (hover: hover) and (pointer: fine) {
  .cards-kit-button:not(.cards-kit-button--ghost):not(:disabled):not([aria-disabled="true"]):hover {
    background: color-mix(in srgb, var(--cards-button-face) 85%, var(--c-text-on-color));
    box-shadow: 0 calc(var(--cards-button-step) + var(--step-1)) 0 var(--cards-button-step-color);
    transform: translateY(calc(var(--step-1) * -1));
  }
  .cards-kit-button--ghost:not(:disabled):not([aria-disabled="true"]):hover {
    color: var(--c-selection);
    text-decoration: underline var(--stroke-l);
    text-underline-offset: var(--space-1);
  }
}
/* Same weight as the hover rule above and later, so a press wins over hover. */
.cards-kit-button:not(.cards-kit-button--ghost):not(:disabled):not([aria-disabled="true"]):active {
  box-shadow: none;
  transform: translateY(var(--cards-button-step));
  transition-duration: var(--dur-tap);
}
.cards-kit-button:disabled,
.cards-kit-button[aria-disabled="true"] {
  --cards-button-step: 0px;
  --cards-button-face: var(--c-panel-raised);
  box-shadow: none;
  color: var(--c-text-disabled);
  text-shadow: none;
  transform: none;
  cursor: not-allowed;
}
.cards-kit-button--ghost:disabled,
.cards-kit-button--ghost[aria-disabled="true"] { background: transparent; }
/* Screen (02 §2.8): every user screen lies on the felt (01 §8, Q9). */
.cards-kit-screen {
  min-height: calc(100vh - var(--cards-beta-ticker-reserve));
  box-sizing: border-box;
  /* The shell clamps a screen to the viewport row (.auth-app-content > *), so a
     taller screen scrolls itself — like the deck pages — and the felt behind it
     stays whole instead of ending at the first viewport. */
  overflow-y: auto;
  background: var(--cards-felt);
  color: var(--c-text-on-dark);
  font: var(--type-text-m);
}
.cards-kit-screen--dynamic { min-height: 0; height: calc(100dvh - var(--cards-beta-ticker-reserve)); }
.cards-kit-screen--centered {
  display: grid;
  place-items: center;
  padding:
    max(var(--space-6), env(safe-area-inset-top))
    max(var(--space-6), env(safe-area-inset-right))
    max(var(--space-6), env(safe-area-inset-bottom))
    max(var(--space-6), env(safe-area-inset-left));
}
.cards-kit-screen--flow { display: block; }
/* Screen-level swaps (lobby ↔ search, deck pages, status screens, the board)
   fade in instead of popping — the shared "no hard cuts" layer. Opacity only:
   a transform here would break position:fixed descendants while it runs. */
@keyframes cards-screen-enter {
  from { opacity: 0; }
}
.cards-kit-screen,
.cards-status {
  animation: cards-screen-enter var(--dur-base) var(--ease-out);
}
/* StatusScreen (02 §2.8): one centred panel — the variant's pixel icon, a pixel
   heading, a line of text and the action. */
.cards-kit-status-panel {
  width: min(480px, 100%);
  box-sizing: border-box;
  display: grid;
  justify-items: center;
  gap: var(--space-3);
  padding: var(--space-6);
  --cards-panel-step: var(--step-4);
  color: var(--c-text-on-dark);
  text-align: center;
}
.cards-kit-status-panel--compact { width: min(460px, 100%); padding: var(--space-5); }
.cards-kit-status-icon { display: grid; place-items: center; min-height: 42px; }
.cards-kit-status-icon > .cards-pixel-icon { width: 42px; height: 42px; }
.cards-kit-status-panel--error .cards-kit-status-icon { color: var(--c-negative); }
.cards-kit-status-panel--reconnecting .cards-kit-status-icon { color: var(--c-selection); }
.cards-kit-status-panel--info .cards-kit-status-icon { color: var(--c-freeze); }
.cards-kit-status-heading {
  display: block;
  color: var(--c-text-on-dark);
  font: var(--type-display-s);
  text-transform: uppercase;
}
.cards-kit-status-message {
  margin: 0;
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-m);
}
.cards-kit-status-panel--with-action .cards-kit-status-message { margin-bottom: var(--space-2); }
.cards-kit-status-reconnecting-copy { color: var(--c-text-on-dark); font: var(--type-text-m); }
.cards-kit-status-detail {
  color: var(--c-text-muted-on-dark);
  font: var(--type-text-s);
}
.cards-deck-selection.cards-kit-screen {
  padding:
    max(clamp(18px, 4vw, 48px), env(safe-area-inset-top))
    max(clamp(18px, 4vw, 48px), env(safe-area-inset-right))
    max(clamp(18px, 4vw, 48px), env(safe-area-inset-bottom))
    max(clamp(18px, 4vw, 48px), env(safe-area-inset-left));
}
/* DeckSelection already switches primary/secondary by state; these keep only
   the layout. */
.cards-deck-select-button { width: 100%; }
.cards-deck-lock-button { min-width: 150px; }
/* ReadyButton (02 §5.2): the screen's one orange button. It keeps the battle
   column's 36px slot (the field divides the height left around it until DS4b),
   so the 3px rim and the pixel label are fitted inside that box. */
.cards-ready-button {
  position: relative;
  z-index: 40;
  align-self: flex-start;
  width: 112px;
  height: 36px;
  min-height: 36px;
  margin-top: 0;
  margin-left: 0;
  padding: 0 var(--space-3);
  text-transform: uppercase;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
/* Doubled class: the kit's --md --display size rule has the same weight. */
.cards-ready-button.cards-kit-button { font: var(--type-display-s); line-height: 1; }
/* Ready and waiting for the opponent: no un-ready exists (Q8), so the button
   rests on the table in a gold rim with three stepped dots. */
/* «Пора» (02 §5.2): on a desktop the button nudges its step twice every
   cycle — a 3px hop in held frames — while it waits to be pressed. Never under
   the pointer or the press, which own the transform. */
@media (hover: hover) and (pointer: fine) and (min-width: 901px) and (prefers-reduced-motion: no-preference) {
  .cards-ready-button[data-nudge="true"]:not(:hover, :active, :disabled) {
    animation: cards-ready-nudge var(--dur-nudge) step-end infinite;
  }
}
@keyframes cards-ready-nudge {
  0%, 88% { transform: translateY(0); }
  90% { transform: translateY(calc(var(--step-2) * -1)); }
  93% { transform: translateY(0); }
  95% { transform: translateY(calc(var(--step-2) * -1)); }
  98%, 100% { transform: translateY(0); }
}
.cards-ready-button[data-waiting="true"] {
  --cards-button-face: var(--c-panel-raised);
  border-color: var(--c-selection);
  color: var(--c-text-on-dark);
  cursor: default;
}
.cards-ready-button-dots { display: inline-flex; gap: 2px; margin-left: var(--space-1); }
.cards-ready-button-dots > span {
  width: 3px;
  height: 3px;
  background: currentColor;
  animation: cards-ready-dot var(--dur-loader) var(--steps-flash) infinite;
}
.cards-ready-button-dots > span:nth-child(2) { animation-delay: calc(var(--dur-loader) / 3); }
.cards-ready-button-dots > span:nth-child(3) { animation-delay: calc(var(--dur-loader) * 2 / 3); }
@keyframes cards-ready-dot {
  0% { opacity: 1; }
  33.3%, 100% { opacity: .3; }
}
.cards-lobby.cards-kit-screen {
  min-height: 0;
  height: calc(100dvh - var(--cards-beta-ticker-reserve));
  padding: 24px;
  overflow: hidden;
}
.lobby-play-bot { margin-top: 0; }
@media (prefers-reduced-motion: reduce) {
  .cards-kit-button { transition: none; }
}
/* Dropped into the cell (03 §3.2): it falls in, squashes flat on contact
   (scaleY .92), springs a touch tall and settles. Linear overall, a curve per
   stop. */
@keyframes cards-stage-land {
  from { opacity: 0; transform: translateY(-18px) scale(1.08); animation-timing-function: ${MOTION_EASINGS.in}; }
  55% { opacity: 1; transform: translateY(0) scale(1.04, .92); animation-timing-function: ${MOTION_EASINGS.out}; }
  80% { opacity: 1; transform: translateY(0) scale(.98, 1.04); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
@keyframes cards-hover-popover-in {
  from { opacity: 0; transform: translateY(0) scale(calc(var(--cards-hover-popover-scale, 1) * .92)); }
  to { opacity: 1; transform: translateY(0) scale(var(--cards-hover-popover-scale, 1)); }
}
@keyframes cards-hover-popover-out {
  from { opacity: 1; transform: translateY(0) scale(var(--cards-hover-popover-scale, 1)); }
  to { opacity: 0; transform: translateY(0) scale(calc(var(--cards-hover-popover-scale, 1) * .92)); }
}
/* Drops in from above on the overshoot (03 §4.14). */
@keyframes cards-match-result {
  from { opacity: 0; transform: translateY(-64px); }
  to { opacity: 1; transform: translateY(0); }
}
${revealKeyframes('cards-reveal-card', true)}
${revealKeyframes('cards-reveal-card-from-lineup', false)}
@keyframes cards-reveal-card-devouring {
  /* Two beats end to end on one clock (overlayReveal = cardReveal + hostSwallow),
     because the flip and the gulp are things the player must read in order —
     played at the same time, under the ghost, the flip was invisible and the
     card looked like it just vanished.
     0–42% is cards-reveal-card exactly, moment for moment (hold, the same turn
     in depth, edge-on at 25% = 370ms, slam, settle), played where this card has been lying since it was
     dealt: ON the host, shifted UP off it by the overlay stack (see
     [data-overlay-stacked]) — so the swap frame from the face-down back is
     pixel-identical, and the host's name and stat row stay readable below it
     throughout. Same --cards-reveal-from as the plain reveal, so a receiver that
     was not the top of its pile starts at ITS layer rather than at the full
     shift; the fallback of 1 is the lone-card case this began as.
     42–100% is the meal, on the ghost's own clock (it waits out the flip via
     animation-delay): the eater DROPS out of the offset into the slot it is
     taking over while the prey is dragged down under it, jaws WIDE as it arrives
     (77%), snapped narrow across the axis (87%) — the flytrap fold of
     cards-card-devourer — then a decaying settle. The ghost is crushed at 91%,
     just after the shut: cause, then effect.
     No filter here: the flash is a compositor-only ::after overlay.
     And NO fade-in, unlike cards-reveal-card: this card does not arrive out of
     nothing, it takes over from a face-down back already standing on this exact
     spot, drawn identically — so it has to be fully opaque on its first frame.
     The reveal's 0%→5% opacity ramp punched a ~74ms hole through that back,
     showing the bright host through it at full size and then letting the back
     "come back": the flicker that read as a flip and a flip back. */
${overlayRevealStops()}
}
@keyframes cards-spell-orb-reveal {
  /* The same turn in depth as a card's reveal (see revealKeyframes): edge-on at
     58%, where the back's step-end cover hands over, and on round through the
     far side with scaleX(-1) instead of jumping back to 0°. */
  0%, 34% { transform: perspective(240px) rotateY(0deg) scaleX(1) scale(1); animation-timing-function: ${MOTION_EASINGS.in}; }
  53% { transform: perspective(240px) rotateY(72deg) scaleX(1) scale(1.08); animation-timing-function: linear; }
  58% { transform: perspective(240px) rotateY(90deg) scaleX(0) scale(1.12); animation-timing-function: linear; }
  63% { transform: perspective(240px) rotateY(108deg) scaleX(-1) scale(1.14); animation-timing-function: ${MOTION_EASINGS.out}; }
  80% { transform: perspective(240px) rotateY(180deg) scaleX(-1) scale(1.18); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  100% { transform: perspective(240px) rotateY(180deg) scaleX(-1) scale(1); }
}
@keyframes cards-spell-orb-back-reveal {
  0%, 58% { opacity: 1; }
  59%, 100% { opacity: 0; }
}
/* Overshoot carries the 0 → 1.15 → 1 of 03 §3.3 on two stops. */
@keyframes cards-spell-orb-fade-in {
  from { opacity: 0; transform: scale(0); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes cards-spell-orb-fade-out {
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0); }
}
/* 36 is a whole number of every dash period above (6, 9, 12), so the flow loops seamlessly. */
@keyframes cards-spell-fiber-flow {
  to { stroke-dashoffset: -36; }
}
/* Grows by scale from its 4-unit radius, not by animating r (03 §7). */
@keyframes cards-cast-fiber-spark {
  0% { transform: scale(1); opacity: 1; }
  40% { transform: scale(3.25); opacity: .5; }
  100% { transform: scale(4.5); opacity: 0; }
}
@keyframes cards-devour-fiber-glow {
  /* Brightest across the shut, not at the end: the flash IS the snap. */
  0%, 14% { opacity: 0; }
  26% { opacity: .12; }
  42%, 70% { opacity: .26; }
  100% { opacity: 0; }
}
@keyframes cards-devour-fiber-pull {
  /* Offset 1→0 draws the vines out of the devourer towards the prey; going
     back 0→1 erases them from the prey end so they retract INTO the
     devourer — matching the direction the prey itself is being pulled.
     They must be all the way out and open BEFORE the geometry snaps them
     shut at 28%, or the trap closes on a thread that has not arrived. */
  0% { opacity: 0; stroke-dashoffset: 1; }
  10% { opacity: 1; }
  26%, 76% { opacity: 1; stroke-dashoffset: 0; }
  94%, 100% { opacity: 0; stroke-dashoffset: 1; }
}
@keyframes cards-devour-fiber-tendon {
  /* Slack until the shut, then it snaps taut and stays loaded through the pull. */
  0%, 24% { opacity: 0; stroke-dashoffset: 1; }
  30% { opacity: 1; stroke-dashoffset: 0; }
  76% { opacity: 1; stroke-dashoffset: 0; }
  94%, 100% { opacity: 0; stroke-dashoffset: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .cards-spell-orb[data-revealing="true"],
  .cards-spell-orb[data-revealing="true"] .cards-spell-orb-back,
  .cards-spell-orb,
  .cards-spell-orb[data-exiting="true"] { animation-duration: 1ms; }
  .cards-spell-fiber path[data-strand="thread"] { animation: none; }
  .cards-spell-fiber g[data-strand="knot"] { display: none; }
  .cards-spell-fiber[data-variant="devour"] path[data-strand="glow"] { opacity: .18; animation: none; }
  .cards-spell-fiber[data-variant="devour"] path[data-strand="thread"] { opacity: .9; stroke-dasharray: none; }
}
@keyframes cards-token-enter {
  /* Face-up fade-in + a scale accent, no flip. */
  0% { opacity: 0; transform: scale(.7); }
  55% { opacity: 1; transform: scale(1.24); }
  100% { opacity: 1; transform: scale(1); }
}
@keyframes cards-token-death-spawn {
  0% { opacity: 0; transform: translate(var(--cards-spawn-dx), var(--cards-spawn-dy)) scale(.28) rotate(-12deg); }
  62% { opacity: 1; transform: translate(calc(var(--cards-spawn-dx) * -.08), calc(var(--cards-spawn-dy) * -.08)) scale(1.12) rotate(2deg); }
  100% { opacity: 1; transform: translate(0, 0) scale(1) rotate(0); }
}
@keyframes cards-token-death-spawn-tone {
  0% { filter: brightness(1.9) saturate(.4); }
  62% { filter: brightness(1.35) saturate(1.2); }
  100% { filter: brightness(1) saturate(1); }
}
@keyframes cards-decay-fragment-flight {
  /* Decay reads as one carried motion: the crack chips pop at the dying
     source, the courier chips fly the source→cell path over the first ~60%
     of this window, and the card condenses AT its cell right as they land —
     it stays invisible until then so the chip flight is what the eye follows.
     Per-segment linear easing; waypoint spacing shapes the deceleration. */
  0%, 55% { opacity: 0; transform: translate(calc(var(--cards-spawn-dx) * .16), calc(var(--cards-spawn-dy) * .16)) scale(.5) rotate(-4deg); }
  74% { opacity: 1; transform: translate(0, 0) scale(1.07) rotate(-1deg); }
  86% { opacity: 1; transform: translate(0, 0) scale(.98) rotate(0deg); }
  100% { opacity: 1; transform: translate(0, 0) scale(1) rotate(0deg); }
}
@keyframes cards-decay-fragment-flight-tone {
  0%, 55% { filter: brightness(1.8) saturate(.7); }
  74% { filter: brightness(1.35) saturate(1.05); }
  86% { filter: brightness(1.1) saturate(1); }
  100% { filter: brightness(1) saturate(1); }
}
@keyframes cards-decay-courier-chip {
  /* Directed flight along the measured vector with a slight mid-path lift;
     the chip burns out exactly on arrival, handing over to the fragment
     card's condensation. Linear + waypoint spacing, same as the flight. */
  0% { opacity: 0; transform: translate(calc(var(--cards-debris-dx) * .04), calc(var(--cards-debris-dy) * .04)) scale(.7); }
  12% { opacity: 1; transform: translate(calc(var(--cards-debris-dx) * .12), calc(var(--cards-debris-dy) * .12 - 8px)) scale(1); }
  60% { opacity: 1; transform: translate(calc(var(--cards-debris-dx) * .62), calc(var(--cards-debris-dy) * .62 - 12px)) scale(1); }
  88% { opacity: 1; transform: translate(calc(var(--cards-debris-dx) * .96), calc(var(--cards-debris-dy) * .96)) scale(.85); }
  100% { opacity: 0; transform: translate(var(--cards-debris-dx), var(--cards-debris-dy)) scale(.55); }
}
@keyframes cards-decay-debris-chip {
  /* Radial one-shot: each chip flies out to its --cards-debris-* offset,
     tumbling and burning out. Single segment, so the ease-out never stalls. */
  0% { opacity: 0; transform: translate(0, 0) scale(.6) rotate(0deg); }
  12% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--cards-debris-dx, 0px), var(--cards-debris-dy, 0px)) scale(1) rotate(160deg); }
}
@keyframes cards-card-assembly-donor {
  0% { opacity: 1; transform: translate(0, 0) scale(1) rotate(0); }
  35% { opacity: 1; transform: translate(calc(var(--cards-assembly-dx) * .28), calc(var(--cards-assembly-dy) * .28)) scale(.9) rotate(3deg); }
  100% { opacity: 0; transform: translate(var(--cards-assembly-dx), var(--cards-assembly-dy)) scale(.16) rotate(-5deg); }
}
@keyframes cards-card-assembly-donor-tone {
  0% { filter: brightness(1) saturate(1); }
  35% { filter: brightness(1.3) saturate(1.3); }
  100% { filter: brightness(2) saturate(.35); }
}
@keyframes cards-combat-impact {
  from { opacity: 1; scale: 1 1; }
  to { opacity: 0; scale: 1.04 3; }
}
@keyframes cards-effect-link-travel {
  from { opacity: 0; scale: 0 1; }
  30% { opacity: 1; }
  to { opacity: 1; scale: 1 1; }
}
@keyframes cards-departed-death-fall {
  0% { opacity: 0; transform: translate(0, -88px) rotate(-7deg); }
  20% { opacity: 1; }
  100% { opacity: 1; transform: translate(0, 0) rotate(4deg); }
}
@keyframes cards-departed-death-fall-tone {
  0% { filter: brightness(.85) saturate(.55); }
  100% { filter: brightness(1.45) saturate(.65); }
}
@keyframes cards-departed-death-fall-impact {
  0% { opacity: 1; transform: translate(0, 0) rotate(4deg); }
  20% { opacity: 1; transform: translate(0, 7px) rotate(0); }
  100% { opacity: 0; transform: translate(0, 16px) rotate(0) scale(.78); }
}
@keyframes cards-departed-death-fall-impact-tone {
  0% { filter: brightness(1.45) saturate(.65); }
  20% { filter: brightness(2.15) saturate(.35); }
  100% { filter: brightness(.7) saturate(.2); }
}
@keyframes cards-card-absorb-donor {
  0% { opacity: 1; transform: translate(0, 0) scale(1); }
  28% { opacity: 1; transform: translate(calc(var(--cards-absorb-dx) * .12), calc(var(--cards-absorb-dy) * .12)) scale(.91); }
  100% { opacity: 0; transform: translate(var(--cards-absorb-dx), var(--cards-absorb-dy)) scale(.18); }
}
/* An overlay card (overgulper/colony_devourer) lies ON the card it will eat
   instead of replacing it in the slot: shifted UP, so what stays visible of the
   host is its BOTTOM strip — the name and the attack/health row, the only part
   of a board card that carries information. It can be hovered there like any
   other board card. Absolutely positioned so the host keeps the flex slot and
   the two are card-on-card rather than side by side; the box keeps its exact
   size — top and bottom move by the same amount in opposite directions —
   because this box has to stay pixel-identical to the cards that take over from
   it (the lineup back, then the flipping receiver).
   18px is the room there is above a cell: its own 4px top padding plus the 14px
   front line, stopping at the opposite row's cell border. That clears the stat
   row (bottom: 5px, 16px digits) and no more — the name sits above it at
   bottom: 19px and would need 32px, a third of the way into the other player's
   card. The card may not shrink to make room. Mobile gets 10px against a 16px
   front line and 1px padding.
   --cards-stack-step is a LAYER INDEX, not a fraction: every card in a pile
   lies a full 18px above the one below it, so each one shows its whole strip no
   matter how deep the pile is. A pile of four therefore stands 54px up and runs
   out over the row above — accepted, and the alternative is worse. Dividing the
   18px between the layers kept the pile inside the cell but left 6px slivers
   that said nothing, and made a layer's offset depend on the pile's depth: the
   whole stack re-spaced itself every time a card was added or revealed.
   Layer 0 is the slot itself, which is why this is not gated on being stacked at
   all: at step 0 the calc collapses to a plain inset of the cell padding, the
   same box the base rule gives as a flex child. Positioning unconditionally costs nothing and
   removes an un-transitionable position: relative → absolute flip on the frame a
   pile first grew past one card.
   Two cards use it, on the same spot, one after the other: the placing player's
   staged plan all round, and then — on BOTH sides of the table — the reveal
   lineup's face-down back standing in for it. The opponent's overlay card is
   dealt onto the host face-down exactly where the plan lay, and the flip that
   follows is the one that eats it. */
.cards-staged-card[data-stack-layer],
.cards-board-card-lineup[data-stack-layer] {
  position: absolute;
  inset: var(--cards-cell-padding, 4px);
  width: auto;
  height: auto;
  z-index: 5;
  translate: 0 calc(var(--cards-overlay-stack-shift, 18px) * var(--cards-stack-step, 0) * -1);
}
/* Since a layer's step is its own index, neither pile re-spaces when it grows:
   a card is added on top and everything under it stays where it was, and the
   lineup drains from the top for the same reason (see Cell). What is left for
   this transition is a card pulled out from the MIDDLE — returning the second of
   three staged cards to hand drops the third down a layer. Snapped, that is a
   card teleporting; over a quick beat it reads as the pile settling. The offset
   rides the standalone translate property, never top/bottom (layout, 03 §7) or
   width/height — the box may not resize — and never transform: the staged
   card's pointer tilt and the buried card's peek both live on transform, and
   translate composes in front of it without either one dropping the other.
   A card's own arrival keyframe wins on the frame it mounts (no previous value
   to transition from), so landing and dealing are unaffected. transform is
   restated because these selectors outrank .cards-staged-card's own tilt
   transition. */
.cards-staged-card[data-stack-layer],
.cards-board-card-lineup[data-stack-layer] {
  transition:
    translate var(--dur-quick) var(--ease-out),
    transform var(--dur-move) var(--ease-out);
}
@media (prefers-reduced-motion: reduce) {
  .cards-staged-card[data-stack-layer],
  .cards-board-card-lineup[data-stack-layer] { transition: none; }
}
/* A card buried in a staged pile is a whole card, not a plate: the step above it
   is a full one precisely so its name and stat row stay readable, and a blank
   rectangle in that space says nothing about what is in the pile. So it keeps the
   .cards-staged-card face and the pile geometry of [data-stack-layer] above; its
   own class only marks it for the peek below. */
/* Pointed at: the card slides its corner out of the pile — a hair right and down,
   the two directions that are not covered by the cards lying on it — so the one
   the preview is describing is unmistakable. It deliberately stays IN the pile
   (no z-index climb): the pile's order is information too. */
.cards-staged-stack-layer[data-peek="true"] {
  transform: translate(7px, 3px);
}
@media (prefers-reduced-motion: reduce) {
  .cards-staged-stack-layer[data-peek="true"] { transform: none; }
}
/* A FIELD card that could not have the cell flips face-up on it anyway, lying on
   whatever denied it — the pile pose, on its own variable rather than on
   --cards-stack-step, because here the WRAPPER holds the offset for good while
   the card inside it still runs the reveal flip. Sharing one variable would apply
   the same shift twice and lift the card a full card-height off the cell.
   Then it leaves. It used to be unmounted between two frames: a card the player
   had just been shown, and been told about, blinking out of existence — which
   reads as a rendering fault, not as a play failing. The fade is the departure.
   Kept under reduced motion: nothing moves, only opacity. */
.cards-field-fizzle-card {
  position: absolute;
  top: calc(var(--cards-cell-padding, 4px) - var(--cards-overlay-stack-shift, 18px) * var(--cards-fizzle-step, 0));
  right: var(--cards-cell-padding, 4px);
  bottom: calc(var(--cards-cell-padding, 4px) + var(--cards-overlay-stack-shift, 18px) * var(--cards-fizzle-step, 0));
  left: var(--cards-cell-padding, 4px);
  z-index: 7;
}
.cards-field-fizzle-card[data-leaving="true"] {
  -webkit-mask-size: ${DITHER_TILE} ${DITHER_TILE};
  mask-size: ${DITHER_TILE} ${DITHER_TILE};
  animation: cards-field-fizzle-leave var(--beat-field-fizzle-fade) step-end forwards;
}
/* A card that could not have the cell crumbles into pixel blocks and goes
   (03 §4.2): the same Bayer dither as a death, in held steps. */
@keyframes cards-field-fizzle-leave {
  0% { opacity: 1; ${maskStop(0)} }
  25% { opacity: 1; ${maskStop(4)} }
  50% { opacity: 1; ${maskStop(8)} }
  75% { opacity: 1; ${maskStop(12)} }
  100% { opacity: 0; ${maskStop(16)} }
}
.cards-field-fizzle-card > .cards-board-card {
  width: 100%;
  height: 100%;
}
/* A shadow cast DOWN onto the strip it exposes, so the offset reads as one card
   lying on another rather than as a card that missed its slot. It points the
   same way the pile grows: the cards climb, the shadow falls onto the name and
   stat row left uncovered below them. The staged card restates its warning ring
   with it (that rule lands earlier in this sheet at equal specificity) and the
   unit tint with it, since overlay cards are always units. */
.cards-staged-card[data-overlay-stacked="true"] {
  /* Lying on a card: a hard step down onto it, no soft glow (01 §7). */
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
}
.cards-board-card-lineup[data-overlay-stacked="true"] {
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
}
/* Overlay host being swallowed: a ghost takes over the consumed host's exact
   box, waits out the flip of the card lying on it (animation-delay, fill both,
   so it stands there as the host did) and is then eaten under that card — the
   flytrap language of cards-card-devour-prey, with the travel pointing into the
   cell itself instead of at a neighbouring devourer.
   It sits BELOW the flipping receiver, since the receiver is the card lying on
   top of it; what is visible of it during the gulp is its name and stat strip
   below that card, which is where all of this animation's vertical travel
   reads. */
.cards-overlay-host-ghost {
  position: absolute;
  inset: var(--cards-cell-padding, 4px);
  width: auto;
  height: auto;
  z-index: 6;
  pointer-events: none;
  will-change: transform, opacity;
  animation: cards-overlay-host-swallow var(--beat-host-swallow) linear
    var(--beat-card-reveal) both;
}
@keyframes cards-overlay-host-swallow {
  /* Nothing happens to the host until the card on it is face-up: this whole
     keyframe is delayed by the flip (cardReveal), and a ghost draining while the
     eater is still a card back reads as the host quietly fading out rather than
     as something eating it — and, worse, hides the flip. So: one long arc on the
     gulp's own clock, starting where the flip left off.
     ONE recoil, then a single direction of travel: down into the mouth. The
     previous pass alternated the rotation five times across 372ms — 2 to 4
     frames per pose — which is a vibration, not a struggle. Fewer poses, bigger
     amplitudes, more time each.
     Linear like cards-card-devour-prey: the beats are placed by waypoint
     spacing, and an eased curve smears the grab and the pull into a slide. */
  0% { opacity: 1; transform: translate(0, 0) scale(1) rotate(0); }
  /* Not a freeze: the host takes the weight of the card that has just turned
     face-up on it and settles. Anticipation, not draining — opacity stays at 1
     (a ghost losing opacity here reads as the host quietly fading out). The
     eater holds still through this (see the 42–60% rest in
     cards-reveal-card-devouring): the prey moves first. */
  29% { opacity: 1; transform: translate(0, 2%) scale(.985) rotate(0); }
  /* Gripped. The recoil goes UP, away from the mouth: the host is being held,
     not yet pulled. */
  50% { transform: translate(0, -7%) scale(.94) rotate(2.5deg); }
  /* Losing it — dragged back down over the jaws as they open. */
  68% { transform: translate(0, 5%) scale(.97) rotate(-2deg); }
  /* Squeezed through: crushed ACROSS the axis, just after the trap shuts. */
  84% { opacity: .9; transform: translate(0, 11%) scale(1.05, .5) rotate(-1.5deg); }
  94% { opacity: .34; transform: translate(0, 16%) scale(.55, .2) rotate(-3deg); }
  100% { opacity: 0; transform: translate(0, 19%) scale(.1) rotate(-5deg); }
}
/* The stats the swallow moved, floated out of the gulp: delayed to the frame
   the trap shuts so they read as coming OUT of the meal rather than as a label
   on the flip. The cue that owns them is held one statusChange beat past the
   flip (see the UnitEnteredPlay branch) so there is time to read them. */
.cards-overlay-host-gain {
  position: absolute;
  inset-inline: 0;
  top: 30%;
  /* Above the risen receiver too (z-index 7), which by then covers the ghost. */
  z-index: 8;
  display: flex;
  gap: 7px;
  justify-content: center;
  pointer-events: none;
  opacity: 0;
  color: var(--c-positive);
  animation: cards-overlay-host-gain-rise var(--beat-status-change) var(--ease-out)
    calc(var(--beat-card-reveal) + var(--beat-host-swallow) * .84) forwards;
}
.cards-overlay-host-gain .cards-card-stat-icon { font-size: var(--icon-s); }
.cards-overlay-host-gain .cards-card-stat-value {
  font: var(--type-display-xs);
  line-height: 1;
  text-shadow: var(--text-outline-1);
}
@keyframes cards-overlay-host-gain-rise {
  0% { opacity: 0; transform: translateY(7px) scale(.7); }
  24% { opacity: 1; transform: translateY(0) scale(1.16); }
  40% { transform: translateY(-2px) scale(1); }
  74% { opacity: 1; }
  100% { opacity: 0; transform: translateY(-15px) scale(.96); }
}
@media (prefers-reduced-motion: reduce) {
  .cards-overlay-host-ghost { animation: cards-overlay-host-swallow 1ms forwards; }
  /* The ghost is already gone by frame two, so the numbers cannot wait for a
     gulp that will never be seen. */
  .cards-overlay-host-gain { animation-delay: 0ms; }
}
@keyframes cards-card-absorb-receiver {
  0%, 100% { transform: scale(1); }
  64% { transform: scale(1.13); }
}
@keyframes cards-card-devour-prey {
  /* Caught, then eaten. The prey holds still while the vines reach, recoils
     the WRONG way when the trap slams at 28-42% (it is being gripped, not yet
     pulled), thrashes twice against the hold, and only then is reeled in.
     Ends eaten and stays eaten (forwards fill): playback departs the prey's
     cell right when the cue clears, so there is no return trip and no second
     dying animation afterwards — one devour IS the death. */
  0%, 28% { opacity: 1; transform: translate(0, 0) scale(1) rotate(0); }
  36% { transform: translate(calc(var(--cards-devour-dx) * -.06), calc(var(--cards-devour-dy) * -.06)) scale(.93) rotate(2.5deg); }
  46% { transform: translate(calc(var(--cards-devour-dx) * .03), calc(var(--cards-devour-dy) * .03)) scale(.99) rotate(-3deg); }
  54% { transform: translate(0, 0) scale(.94) rotate(2deg); }
  62% { transform: translate(calc(var(--cards-devour-dx) * .06), calc(var(--cards-devour-dy) * .06)) scale(.97) rotate(-1.5deg); }
  80% { opacity: .84; transform: translate(calc(var(--cards-devour-dx) * .46), calc(var(--cards-devour-dy) * .46)) scale(.6) rotate(-5deg); }
  92%, 100% { opacity: 0; transform: translate(var(--cards-devour-dx), var(--cards-devour-dy)) scale(.14) rotate(-9deg); }
}
@keyframes cards-card-devourer {
  /* Three beats of the trap: it loads open, slams shut on the catch (the lobes
     squash ACROSS the axis, the way a flytrap folds), then gulps it down. */
  0% { transform: scale(1); }
  24% { transform: scale(1.07, .96); }
  30% { transform: scale(.88, 1.15) rotate(-1.5deg); }
  40% { transform: scale(1.05, .94) rotate(.8deg); }
  52% { transform: scale(.98, 1.02); }
  86% { transform: scale(1.13, .84); }
  96%, 100% { transform: scale(1); }
}
@keyframes cards-cast-cue-travel {
  /* The spell's thread carries the blow to its target (the cast fiber's spark),
     so its emblem no longer drops in out of the sky on its own: it waits for the
     spark and bursts out of the target as it lands — cause, then emblem. */
  0%, 72% { opacity: 0; transform: scale(.5); }
  88% { opacity: 1; transform: scale(1.18); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes cards-cast-cue-impact {
  from { opacity: 1; transform: scale(1); }
  45% { opacity: 1; transform: scale(1.2); }
  to { opacity: 0; transform: scale(.45); }
}
@keyframes cards-cast-cue-impact-tone {
  from { filter: brightness(1); }
  45% { filter: brightness(2); }
  to { filter: brightness(2); }
}
/* Knockback along the measured attack vector (--cards-hit-dx/dy, stamped on
   the cell by the board), falling back to the old horizontal jolt.

   The flash rides a second animation rather than these keyframes, and the two
   are not interchangeable: transform is composited, so the knockback is free at
   any frame rate, while a filter is not — every distinct brightness value
   re-rasterises the whole card, glyph included. Interpolated over the 650ms
   impact window that is some forty rasters of the card being hit; stepped, it is
   five. What it is NOT is an opacity overlay, which is the usual advice and is
   wrong here: brightness multiplies, so an almost-black glyph outline at .1 goes
   to .18 and stays an outline, while a white wash at the same strength takes it
   to .8 and dissolves it. Pixel art cannot afford to lose its outlines for a
   flash. See the devour gulp for the case where an overlay IS right — a bloom
   over the whole card, where flattening is the point. */
@keyframes cards-card-hit {
  /* Hit-stop: dead still for the first frames of the impact, lit by the flash
     beside it — the freeze is what makes the blow land. Then the knockback. */
  0%, ${pctOf(TURN_PLAYBACK_TIMING.hitStop, TURN_PLAYBACK_TIMING.attackImpact)}% { transform: translate(0, 0); animation-timing-function: ${MOTION_EASINGS.out}; }
  28% { transform: translate(var(--cards-hit-dx, -5px), var(--cards-hit-dy, 0px)); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  55% { transform: translate(calc(var(--cards-hit-dx, -5px) * -0.35), calc(var(--cards-hit-dy, 0px) * -0.35)); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  78% { transform: translate(calc(var(--cards-hit-dx, -5px) * 0.12), calc(var(--cards-hit-dy, 0px) * 0.12)); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  100% { transform: translate(0, 0); }
}
/* Held values, not a ramp (step-end at the call site): the extra stops past 55%
   are what keeps the decay from reading as one long bright hold and a pop. */
@keyframes cards-card-hit-flash {
  0% { filter: brightness(2.1); }
  ${pctOf(TURN_PLAYBACK_TIMING.hitStop, TURN_PLAYBACK_TIMING.attackImpact)}% { filter: brightness(1.8); }
  30% { filter: brightness(1.35); }
  55% { filter: brightness(1.15); }
  78% { filter: brightness(1.05); }
  100% { filter: none; }
}
@keyframes cards-card-avoid {
  0%, 100% { transform: translateX(0); opacity: 1; }
  30% { transform: translateX(-12px) skewX(-4deg); opacity: .72; }
  62% { transform: translateX(7px) skewX(2deg); opacity: .9; }
}
/* The dodge used to carry blur(.5px) through its bright frame. Stepping the
   filter is what rules it out rather than the cost: a held half-pixel blur is
   210ms of a softened card, where an interpolated one read as a smear — and a
   smear is what it was for. The skew and the opacity dip already sell the
   sidestep, both on the compositor. */
@keyframes cards-card-avoid-flash {
  0% { filter: none; }
  30% { filter: brightness(1.8); }
  62% { filter: brightness(1.25); }
  80% { filter: brightness(1.1); }
  100% { filter: none; }
}
/* Melee lunge. The wind-up is a FIXED pull-back along the attack direction
   (--cards-lunge-ux/uy, a unit vector the board stamps beside the travel
   --cards-lunge-dx/dy): facing rows stand ~20px apart, so a wind-up worth a
   share of the travel was a pixel or two and never read. The card rears back,
   holds loaded, strikes accelerating, arrives a hair PAST first contact — the
   overlap is the collision — and holds contact into the impact phase. */
@keyframes cards-card-lunge {
  0% { transform: translate(0, 0) scale(1); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  44% { transform: translate(calc(var(--cards-lunge-ux, 0) * -9px), calc(var(--cards-lunge-uy, 0) * -9px)) scale(1.07); animation-timing-function: linear; }
  60% { transform: translate(calc(var(--cards-lunge-ux, 0) * -10px), calc(var(--cards-lunge-uy, 0) * -10px)) scale(1.08); animation-timing-function: ${MOTION_EASINGS.in}; }
  88% { transform: translate(calc(var(--cards-lunge-dx, 0px) + var(--cards-lunge-ux, 0) * 3px), calc(var(--cards-lunge-dy, 0px) + var(--cards-lunge-uy, 0) * 3px)) scale(1.04); animation-timing-function: ${MOTION_EASINGS.out}; }
  100% { transform: translate(var(--cards-lunge-dx, 0px), var(--cards-lunge-dy, 0px)) scale(1.04); }
}
/* Charging up: brighter as it loads and strikes, held at contact by forwards. */
@keyframes cards-card-lunge-flash {
  0% { filter: none; }
  44% { filter: brightness(1.15); }
  60% { filter: brightness(1.3); }
  88%, 100% { filter: brightness(1.45); }
}
/* Held at the contact point through the hit-stop, then the rebound: home and a
   little past it, the way it came. */
@keyframes cards-card-lunge-return {
  0%, ${pctOf(TURN_PLAYBACK_TIMING.hitStop, TURN_PLAYBACK_TIMING.attackImpact)}% { transform: translate(var(--cards-lunge-dx, 0px), var(--cards-lunge-dy, 0px)) scale(1.04); animation-timing-function: ${MOTION_EASINGS.out}; }
  52% { transform: translate(calc(var(--cards-lunge-ux, 0) * -4px), calc(var(--cards-lunge-uy, 0) * -4px)) scale(.985); animation-timing-function: ${MOTION_EASINGS.inOut}; }
  100% { transform: translate(0, 0) scale(1); }
}
@keyframes cards-player-hit {
  0%, ${pctOf(TURN_PLAYBACK_TIMING.hitStop, TURN_PLAYBACK_TIMING.attackImpact)}% { transform: translateX(0); }
  28% { transform: translateX(-4px); }
  50% { transform: translateX(3px); }
  72% { transform: translateX(-1px); }
  100% { transform: translateX(0); }
}
@keyframes cards-player-hit-tone {
  0%, ${pctOf(TURN_PLAYBACK_TIMING.hitStop, TURN_PLAYBACK_TIMING.attackImpact)}% { filter: brightness(1.7); }
  28% { filter: brightness(1.45); }
  50% { filter: brightness(1.2); }
  100% { filter: none; }
}
/* The contact flash: big on the hit-stop, a smaller after-image, gone. Held
   frames (step-end), like a sprite. */
@keyframes cards-impact-flash {
  0% { opacity: 1; transform: scale(1.25); }
  ${pctOf(TURN_PLAYBACK_TIMING.hitStop, TURN_PLAYBACK_TIMING.attackImpact)}% { opacity: 1; transform: scale(.8); }
  22% { opacity: .7; transform: scale(.5); }
  30%, 100% { opacity: 0; transform: scale(.5); }
}
@keyframes cards-impact-spark {
  0% { opacity: 1; transform: translate(0, 0); }
  70% { opacity: 1; }
  100% { opacity: 0; transform: translate(var(--cards-spark-x, 20px), var(--cards-spark-y, 0px)) scale(.5); }
}
/* The table takes the blow. Vertical only: the board scrolls sideways on narrow
   screens and the sandbox field has no side padding, so a horizontal jolt would
   push the field past its scroller and flash a scrollbar; the block padding
   (3px at its thinnest) absorbs these. Held frames, like the flash. */
.cards-board-locations[data-shake] {
  animation: cards-board-shake-light var(--dur-quick) step-end;
}
.cards-board-locations[data-shake="heavy"] {
  animation-name: cards-board-shake-heavy;
  animation-duration: var(--dur-base);
}
@keyframes cards-board-shake-light {
  0% { transform: translateY(2px); }
  33% { transform: translateY(-1px); }
  66% { transform: translateY(1px); }
  100% { transform: none; }
}
@keyframes cards-board-shake-heavy {
  0% { transform: translateY(3px); }
  20% { transform: translateY(-3px); }
  40% { transform: translateY(2px); }
  60% { transform: translateY(-1px); }
  80% { transform: translateY(1px); }
  100% { transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .cards-board-locations[data-shake] { animation: none; }
}
@keyframes cards-effect-departure {
  from { opacity: 1; transform: scale(1); }
  55% { opacity: .8; transform: scale(.82) rotate(-2deg); }
  to { opacity: .15; transform: scale(.55); }
}
@keyframes cards-effect-departure-tone {
  from { filter: brightness(1); }
  55% { filter: brightness(1.7) hue-rotate(35deg); }
  to { filter: brightness(2) blur(1px); }
}
@keyframes cards-effect-arrival {
  from { opacity: .15; transform: scale(.55); }
  65% { opacity: 1; transform: scale(1.08); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes cards-effect-arrival-tone {
  from { filter: brightness(2) blur(1px); }
  65% { filter: brightness(1.6); }
  to { filter: none; }
}
@keyframes cards-virus-attach {
  0% { transform: scale(1); }
  35% { transform: scale(.94); }
  65% { transform: scale(1.05); }
  100% { transform: scale(1); }
}
@keyframes cards-virus-attach-tone {
  0% { filter: none; }
  35% { filter: hue-rotate(45deg) brightness(1.45); }
  65% { filter: hue-rotate(-25deg) brightness(1.8); }
  100% { filter: none; }
}
@keyframes cards-effect-glitch {
  0%, 100% { transform: translate(0); filter: none; }
  20% { transform: translate(-3px, 1px); filter: hue-rotate(80deg) brightness(1.8); }
  40% { transform: translate(3px, -1px); filter: invert(.18) brightness(1.4); }
  60% { transform: translate(-2px, -1px); filter: hue-rotate(-70deg) brightness(1.9); }
  80% { transform: translate(2px, 1px); filter: saturate(1.8); }
}
@keyframes cards-effect-remove {
  0%, 100% { opacity: 1; }
  45% { opacity: .6; }
  70% { opacity: .85; }
}
@keyframes cards-effect-remove-tone {
  0%, 100% { filter: none; }
  45% { filter: grayscale(1) brightness(1.6); }
  70% { filter: grayscale(.5); }
}
@keyframes cards-shield-impact {
  0%, 100% { transform: scale(1); }
  35% { transform: scale(.96); }
  65% { transform: scale(1.04); }
}
@keyframes cards-shield-impact-tone {
  35% { filter: brightness(1.65); }
}
@keyframes cards-shield-deplete {
  0%, 100% { transform: scale(1); }
  25% { transform: scale(.96); }
  50% { transform: scale(1.05); }
  75% { transform: scale(.98); }
}
@keyframes cards-shield-deplete-tone {
  0%, 100% { filter: none; }
  25% { filter: brightness(2); }
  50% { filter: brightness(1.45) grayscale(.4); }
  75% { filter: grayscale(1); }
}
/* A gain rises 20px on out, a loss drops 8px and goes (03 §4.4). */
@keyframes cards-playback-delta {
  from { opacity: 0; transform: translate(-50%, 0) scale(.8); }
  20% { opacity: 1; transform: translate(-50%, -4px) scale(1.08); }
  80% { opacity: 1; }
  to { opacity: 0; transform: translate(-50%, -20px) scale(1); }
}
@keyframes cards-playback-delta-down {
  from { opacity: 0; transform: translate(-50%, 0) scale(1.2); }
  20% { opacity: 1; transform: translate(-50%, 0) scale(1); }
  70% { opacity: 1; transform: translate(-50%, 4px); }
  to { opacity: 0; transform: translate(-50%, 8px); }
}
@keyframes cards-stat-delta {
  from { opacity: 0; transform: translate(-50%, 6px) scale(.7); }
  35% { opacity: 1; transform: translate(-50%, -1px) scale(1.12); }
  to { opacity: 0; transform: translate(-50%, -15px) scale(1); }
}
@keyframes cards-stat-delta-down {
  from { opacity: 0; transform: translate(-50%, -4px) scale(1.12); }
  35% { opacity: 1; transform: translate(-50%, 0) scale(1); }
  to { opacity: 0; transform: translate(-50%, 8px) scale(1); }
}
@keyframes cards-stat-bump {
  0%, 100% { transform: scale(1); }
  40% { transform: scale(1.45); }
}
@keyframes cards-effect-positive {
  0%, 100% { transform: scale(1); }
  45% { transform: scale(1.06); }
}
@keyframes cards-effect-positive-tone {
  0%, 100% { filter: none; }
  45% { filter: brightness(1.65); }
}
@keyframes cards-effect-negative {
  0%, 100% { transform: translateX(0); }
  30% { transform: translateX(-3px) scale(.97); }
  60% { transform: translateX(3px) scale(1.02); }
}
@keyframes cards-effect-negative-tone {
  0%, 100% { filter: none; }
  30% { filter: brightness(1.6) saturate(1.5); }
  60% { filter: brightness(1.25); }
}
/* Deck-passive badge pulses. Colour comes from --cards-passive-glow so each
   passive glows in its own palette; motion carries the tone. */
@keyframes cards-passive-pulse {
  0%, 100% { transform: scale(1); }
  45% { transform: scale(1.06); }
}
@keyframes cards-passive-pulse-tone {
  0%, 100% { filter: none; }
  45% { filter: brightness(1.5); }
}
@keyframes cards-passive-pulse-strong {
  0%, 100% { transform: scale(1); }
  30% { transform: scale(1.1); }
  70% { transform: scale(1.02); }
}
@keyframes cards-passive-pulse-strong-tone {
  0%, 100% { filter: none; }
  30% { filter: brightness(1.9); }
  70% { filter: brightness(1.3); }
}
@keyframes cards-passive-reset {
  0%, 100% { transform: scale(1); }
  35% { transform: scale(.93); }
}
@keyframes cards-passive-reset-tone {
  0%, 100% { filter: none; }
  35% { filter: grayscale(.9) brightness(1.4); }
}
@keyframes cards-passive-delta {
  from { opacity: 0; transform: translateY(6px) scale(.8); }
  30% { opacity: 1; transform: translateY(0) scale(1.1); }
  to { opacity: 0; transform: translateY(-14px) scale(1); }
}

@keyframes cards-damage-number {
  /* Punched in oversized on the hit-stop, then tossed: up and off to one side
     (--cards-number-drift, so a clash's two numbers part), and down again as it
     fades. Linear with the arc in the waypoints. */
  0% { opacity: 1; transform: translate(-50%, -50%) scale(1.8); animation-timing-function: ${MOTION_EASINGS.out}; }
  ${pctOf(TURN_PLAYBACK_TIMING.hitStop, TURN_PLAYBACK_TIMING.attackImpact)}% { opacity: 1; transform: translate(-50%, -50%) scale(1.2); animation-timing-function: ${MOTION_EASINGS.out}; }
  26% { transform: translate(calc(-50% + var(--cards-number-drift, 1) * 4px), calc(-50% - 16px)) scale(1); }
  46% { transform: translate(calc(-50% + var(--cards-number-drift, 1) * 8px), calc(-50% - 21px)) scale(1); }
  70% { opacity: 1; transform: translate(calc(-50% + var(--cards-number-drift, 1) * 12px), calc(-50% - 17px)) scale(1); }
  100% { opacity: 0; transform: translate(calc(-50% + var(--cards-number-drift, 1) * 16px), calc(-50% - 6px)) scale(.9); }
}
@media (max-width: 900px), (max-height: 520px) and (pointer: coarse) {
  /* The browser's address bar changing visibility must not resize an active
     battle. dvh tracks that transient visual viewport; svh is the stable
     small viewport and leaves any newly exposed space below the game instead
     of relaying out the field and hand. :has keeps this policy battle-only. */
  .auth-app-shell:has(.cards-game-view) {
    height: calc(100svh - var(--cards-beta-ticker-reserve));
  }
  .cards-game-view {
    --cards-cell-width: calc((100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 64px) / 9);
    --cards-cell-gap: 2px;
    --cards-location-gap: 8px;
    --cards-board-padding: 3px;
    --cards-cell-padding: 1px;
    /* All the vertical slack a compact cell has for an overlay plan to sit lower
       than its host: 1px cell padding + the location's 3px padding. */
    --cards-overlay-stack-shift: 10px;
    --cards-cell-height: clamp(64px, 9.5svh, 82px);
    /* One hand card, and the fraction of a second row the hand is allowed to
       show. The cards themselves retain their fixed height below. */
    --cards-hand-card-height: 74px;
    --cards-hand-rows: 1.5;
    /* One inter-row gap (4px) plus the chrome before the cards: hand border
       (1px), hand padding-top (5px), list padding-top (3px). Bottom chrome is
       deliberately zero so the clipped row meets READY without a dark strip. */
    --cards-hand-chrome: 13px;
    /* Fill the auth shell's content row (below the 26px top menu) rather than
       covering the viewport with position:fixed, which used to slide the board
       under the menu and hide the opponent panel. */
    flex-direction: column !important;
    height: 100%;
    min-height: 0 !important;
    overflow: hidden;
    font-size: var(--type-text-xs-size);
  }
  .cards-game-main {
    flex: 1 1 auto !important;
    min-height: 0;
    gap: 8px !important;
    /* Снизу — 8px без safe-area: нижняя вырезка уже учтена в
       --cards-beta-ticker-reserve, который вычтен из высоты оболочки. Второй раз
       она отнимала у поля ещё 34px на айфоне (на десктопе env = 0, поэтому там
       перерасход не проявлялся). */
    padding: max(8px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right)) 8px max(8px, env(safe-area-inset-left)) !important;
    /* No page scroll on the battle screen: the compact hand (3 reduced cards
       per row) keeps the column within the viewport, so the whole board, both
       panels, the hand and READY stay on screen without vertical scrolling. */
    overflow-y: hidden;
    overscroll-behavior-y: contain;
    scrollbar-width: none;
  }
  .cards-board-stage {
    display: flex;
    /* The field owns the remainder after the fixed 1.5-card hand and READY.
       Shrinking it is preferable to clipping it out of the compact viewport. */
    flex: 1 1 0;
    min-height: 0;
    padding-block: 2px;
    box-sizing: border-box;
    flex-direction: column;
    /* Keep the field attached to the opponent panel; otherwise a viewport
       resize recentres it and makes the whole battle appear to jump. */
    justify-content: flex-start;
    overflow: hidden;
    gap: 4px;
  }
  .cards-game-main::-webkit-scrollbar,
  .cards-event-log::-webkit-scrollbar { display: none; }
  .cards-player-scoreboard { flex: 0 0 auto; }
  .cards-board-scroll {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 0;
    padding-bottom: 0 !important;
  }
  .cards-board-field {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 0;
  }
  .cards-board-locations {
    flex: 1 1 0;
    min-height: 0;
  }
  /* Cell-effect badges: the desktop size covers half a 36px mobile cell. */
  .cards-kit-badge.cards-cell-effect-badge {
    padding: 0 1px;
    border: 0;
  }
  /* Half the band it was. Both panels are single-line here — one seat glyph, a
     name, three stats — and the height was set by the 32px avatar plus 12px of
     padding, none of which the line needs. 20px avatar and 2px padding put the
     panel at ~24px instead of ~44px, and the ~40px that frees is the only
     remaining source of board column height: the card art on this layout is
     exactly the half's height + 6, so a taller half is the one thing that makes
     the glyph and its overhang bigger. */
  .cards-player-panel {
    grid-template-columns: 20px minmax(0, 1fr);
    gap: 7px !important;
    min-height: 0;
    box-sizing: border-box;
    padding: 2px 8px !important;
  }
  .cards-player-avatar {
    width: 20px;
    height: 20px;
    padding: 1px;
  }
  .cards-player-content {
    gap: 3px 10px;
    padding-right: 4px;
  }
  .cards-player-topline {
    gap: 4px 10px;
    align-items: center;
    justify-content: flex-start;
  }
  .cards-player-identity {
    flex: 0 1 auto;
    gap: 5px;
  }
  /* The passive keeps its glyph and name; its description is hidden for every
     battle viewport and remains available from the full hover/tap card. */
  .cards-player-panel .cards-deck-passive-details-compact { gap: 4px; }
  .cards-player-panel .cards-deck-passive-glyph {
    width: 14px;
    height: 14px;
  }
  .cards-player-panel .cards-deck-passive-heading strong { font-size: var(--type-text-xs-size); }
  .cards-player-passive {
    max-width: 110px;
  }
  /* A tappable badge, so it reads as one: the hit area is the whole pill and it
     gets the same 4px of slop the field cards have. */
  .cards-player-passive[role="button"] {
    cursor: pointer;
    margin: -3px;
    padding: 3px;
    -webkit-tap-highlight-color: transparent;
  }
  .cards-player-name-badge {
    max-width: 90px;
    padding: 1px 4px;
    font: var(--type-display-3xs);
  }
  .cards-player-stats {
    flex: 0 0 auto;
    justify-content: flex-start;
    flex-wrap: nowrap;
    gap: 3px 9px;
  }
  .cards-player-health,
  .cards-player-energy,
  .cards-player-deck.cards-kit-button,
  .cards-player-deck {
    min-width: 0 !important;
    font-size: var(--type-display-2xs-size) !important;
    white-space: nowrap;
  }
  .cards-player-secondary { display: none; }
  /* The ~24px panel (02 §5.1 compact): the chip drops to display-xs, the coins to 12px. */
  .cards-player-health > span { height: 18px; padding: 0 var(--space-1); font: var(--type-display-xs); line-height: 1; }
  .cards-player-health .cards-pixel-icon { width: 7px; height: 7px; }
  .cards-player-energy-coin { width: 12px; height: 12px; }
  .cards-board-scroll {
    flex: 1 1 0;
    width: calc(100% + 16px);
    max-width: none !important;
    margin-inline: -8px;
    padding: 0 8px !important;
    box-sizing: border-box;
    overflow-x: hidden !important;
    overflow-y: hidden !important;
    overscroll-behavior-y: none;
    touch-action: pan-y;
  }
  .cards-board-field {
    width: 100% !important;
    min-width: 0 !important;
    padding: var(--cards-board-padding) !important;
  }
  /* One height, always. The board and the two queues share a column with no
     scroll, so anything the band does to its own height moves every location and
     the hand under it — an empty queue that shrank to a stub and grew back on the
     first planned spell made the whole screen jump mid-drag. So the pill is
     exactly its orb (23px) plus its border and never measures its contents: 25px
     empty, 25px full. It stays faint and dashed while empty, which is what says
     "nothing here" now that the size no longer does. */
  .cards-spell-queue {
    height: 25px;
    gap: 3px;
    border-radius: var(--radius-s);
    padding: 0 3px;
  }
  .cards-spell-queue[data-side="opponent"] { margin-bottom: 3px; }
  .cards-spell-queue[data-side="viewer"] { margin-top: 3px; }
  .cards-spell-queue-list { gap: 4px; }
  .cards-spell-orb {
    width: 23px;
    height: 23px;
    flex-basis: 23px;
    padding: 1px;
  }
  .cards-spell-orb-mode { display: none; }
  .cards-board-locations { gap: var(--cards-location-gap) !important; }
  .cards-board-location {
    min-width: 0;
    flex: 1 1 0;
    padding: 3px !important;
    border-width: 1.5px;
    border-radius: var(--radius-s);
  }
  .cards-board-location-header {
    min-height: 21px;
    gap: 3px;
    margin-bottom: 5px;
    padding: 0 2px 4px;
    font-size: var(--type-display-3xs-size);
    letter-spacing: .35px;
  }
  /* Whose half is whose has to survive being read at arm's length. The desktop
     board says it with the occupied slot's border colour, which on a phone is a
     1.5px line around a 34px card — legible only if you go looking. A wash over
     the whole half says it in one glance and stays behind everything. 4%: three
     stacked locations mean six of these bands down the screen, so what reads as
     a hint on one half tints the whole phone six times over — and it has to stay
     far under the alpha the drop-target highlight uses (22%), or "these are your
     cells" gets mistaken for "you can drop here". */
  .cards-board-location-half {
    border-radius: var(--radius-xs);
  }
  .cards-board-location-half[data-side="opponent"] {
    background: color-mix(in srgb, var(--c-rival) 4%, transparent);
  }
  .cards-board-location-half[data-side="viewer"] {
    background: color-mix(in srgb, var(--c-text-on-dark) 4%, transparent);
  }
  .cards-board-front-line { height: 16px; }
  .cards-board-front-line > span { font-size: var(--type-display-3xs-size); }
  .cards-board-front-line::before,
  .cards-board-front-line::after {
    border-top-color: color-mix(in srgb, var(--c-text-muted-on-table) 70%, transparent);
  }
  .cards-board-group {
    min-width: 0;
    gap: var(--cards-cell-gap) !important;
  }
  .cards-board-cell {
    min-width: 0;
    height: var(--cards-cell-height) !important;
    padding: var(--cards-cell-padding) !important;
  }
  .cards-board-card {
    min-width: 0;
    box-sizing: border-box;
    overflow: hidden;
    padding: 1px !important;
  }
  .cards-board-card-art svg,
  .cards-staged-card-art svg {
    width: max(var(--cards-glyph-step), round(down, min(100%, 31px), var(--cards-glyph-step)));
    height: max(var(--cards-glyph-step), round(down, min(100%, 31px), var(--cards-glyph-step)));
  }
  /* A compact cell is only ~33px wide ((100vw - 64px) / 9), which leaves the
     strip ~27px for both numbers. Claim every pixel of it: the strip may not
     wrap (see the base rule), so whatever does not fit is clipped by the card
     instead of moving, and the numbers are what must survive. */
  .cards-board-card-stats,
  .cards-staged-card-stats {
    inset-inline: 1px !important;
    gap: 0 !important;
    letter-spacing: -1px;
  }
  /* The icons were rendering at 3px — decorative noise at this scale, and the
     ~4px each one took is exactly what pushed a two-digit pair over the line.
     Attack-left / health-right already carries the meaning here (the same
     convention the stat-delta chips rely on), and aria-label plus title keep
     it spelled out for assistive tech. */
  .cards-board-card-stats .cards-card-stat-icon,
  .cards-staged-card-stats .cards-card-stat-icon {
    display: none !important;
  }
  .cards-board-card-stats .cards-card-stat-value,
  .cards-staged-card-stats .cards-card-stat-value {
    font-size: var(--type-display-2xs-size) !important;
  }
  /* Three digits (only reachable by stacking buffs) need a smaller step to stay
     inside the card; the pair still reads at a glance because it is the only
     thing on the strip. */
  .cards-board-card-stats .cards-card-stat-value[data-len="3"],
  .cards-staged-card-stats .cards-card-stat-value[data-len="3"] {
    font-size: var(--type-display-3xs-size) !important;
  }
  /* Match the enlarged mobile stat figures, so titles remain equally legible
     without changing the shared desktop field-card scale. */
  .cards-board-card-name {
    font-size: var(--type-text-xs-size) !important;
  }
  /* An XS field card has no name (02 §3.1): on a 61×60 landscape card the strip
     took 14px off a 19px window. The art window comes down to the chips instead,
     and tap gives the full card with its name. A spell keeps its name, which
     stands where its missing numbers would be; the flight's compact face follows
     the card it lands as. The portrait row below repeats the hiding for itself. */
  .cards-board-card:not([data-card-type="SPELL"]) .cards-board-card-name,
  .cards-staged-card:not([data-card-type="SPELL"]) .cards-board-card-name,
  .cards-return-face-compact:not([data-card-type="SPELL"]) .cards-board-card-name {
    display: none;
  }
  .cards-board-card-art,
  .cards-staged-card-art { inset: 3px 3px 22px; }
  /* Nor a plan tag: on a 61px card «ПЛАН» covered the glyph wherever it stood,
     and the card clips anything past its edge. The gold dashed ring is the plan
     mark here, and only a planned card wears it. The portrait row below has a
     free corner and brings the tag back. */
  .cards-staged-card:not([data-buried="true"])::before { display: none; }
  .cards-board-card-statuses,
  .cards-board-card-m14-markers {
    gap: 1px !important;
    font-size: var(--icon-s) !important;
  }
  .cards-staged-card {
    padding: 1px !important;
  }
  /* Unwrap the desktop hand row inside the compact battle column. */
  .cards-hand-area { display: contents; }
  /* One fixed card row plus a visible half-row, regardless of hand size. The
     board never jumps on draw/play, and a one-row hand no longer stretches its
     cards to consume the scroll affordance. */
  .cards-hand-area > .cards-hand {
    order: 0;
    /* Mobile hand viewport: exactly one full fixed card plus half of the next.
       It must not shrink with the board, or a short phone makes the cards look
       incorrectly oversized relative to the hand. */
    flex: 0 0 calc(var(--cards-hand-card-height) * var(--cards-hand-rows) + var(--cards-hand-chrome));
    min-height: 0;
  }
  .cards-hand {
    height: auto;
    margin-inline: -8px;
    padding: 5px 8px 0 !important;
  }
  .cards-hand-title { display: none !important; }
  .cards-hand-bar { display: none; }
  .cards-hand-list {
    display: grid !important;
    /* Reserve the right quarter as a dependable strip where a thumb can start
       scrolling the hand without touching a draggable card. */
    grid-template-columns: repeat(3, minmax(0, 1fr));
    grid-auto-rows: var(--cards-hand-card-height);
    align-content: start;
    flex-wrap: initial !important;
    /* Three fixed-size cards per row; right padding reserves an empty scroll
       surface. Extra rows scroll vertically. */
    overflow-y: auto;
    overflow-x: hidden;
    gap: 4px !important;
    padding: 3px calc(25% + 4px) 0 0;
  }
  .cards-hand-card {
    width: 100% !important;
    min-width: 0 !important;
    height: var(--cards-hand-card-height) !important;
    box-sizing: border-box;
    padding: 14px 3px 4px !important;
    font-size: var(--icon-s) !important;
    /* Tilt effects stay disabled on mobile; the FLIP slide and draw-in
       variables must keep working so the hand re-flows smoothly around a
       carried card and a drawn card still arrives from the deck. The selection
       lift rides the same variable as on the desktop. */
    transform:
      translate(var(--cards-flip-x, 0px), var(--cards-flip-y, 0px))
      translate(var(--cards-draw-x, 0px), var(--cards-draw-y, 0px))
      translateY(var(--cards-select-lift, 0px)) !important;
  }
  .cards-hand-reorder-placeholder {
    width: 100% !important;
    min-width: 0 !important;
    height: var(--cards-hand-card-height) !important;
  }
  /* The phone's selection (03 §3.1): a 3px lift and no turn. The strip is one
     card tall and clips it, so the desktop's 12px would cut the card's top off. */
  .cards-hand-card[data-selected="true"] {
    --cards-select-lift: -3px;
    rotate: none;
  }
  /* The hand at XS (02 §3.1): a 20px coin, the art in its window, chips without
     a frame along the bottom and no name — at this size the art is the card's
     identity, and a tap opens the full card. */
  .cards-hand-card { border-width: var(--stroke-m); border-radius: var(--radius-m); }
  .cards-hand-cost {
    top: 1px;
    left: 1px;
    width: 20px;
    height: 20px;
  }
  .cards-hand-glyph { inset: 2px 2px 18px; }
  .cards-hand-glyph svg {
    width: max(var(--cards-glyph-step), round(down, min(100%, 40px), var(--cards-glyph-step)));
    height: max(var(--cards-glyph-step), round(down, min(100%, 40px), var(--cards-glyph-step)));
  }
  .cards-hand-name { display: none; }
  .cards-hand-stats {
    inset-inline: 2px !important;
    bottom: 1px !important;
    gap: 1px !important;
  }
  .cards-hand-stats .cards-card-stat {
    min-width: 18px;
    height: 15px;
    padding: 0 2px;
  }
  .cards-hand-cost .cards-card-stat-value,
  .cards-hand-stats .cards-card-stat-value {
    font: var(--type-display-3xs);
    line-height: 1;
  }
  .cards-touch-drag-preview {
    --cards-drag-gap: 12px;
    width: 84px !important;
    height: 96px !important;
    padding: 14px 3px 4px !important;
    transform:
      translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
      translate(-50%, calc(-100% - var(--cards-drag-gap)))
      perspective(620px)
      rotateX(var(--cards-drag-rotate-x))
      rotateY(var(--cards-drag-rotate-y))
      rotateZ(var(--cards-drag-rotate-z))
      skewX(var(--cards-drag-skew-x))
      skewY(var(--cards-drag-skew-y))
      scale(
        calc(var(--cards-drag-scale-x) + .035),
        calc(var(--cards-drag-scale-y) + .035)
      ) !important;
  }
  /* The flight ghost's hand face wears the preview's padding, so it needs the
     phone's figures too — its own box already comes from the measured preview
     (84×96 here), but the inset the cost badge and the stat strip sit at does not
     scale with it and would print through the frame at desktop values. */
  .cards-touch-drag-preview[data-return-morph="true"] > .cards-return-face-hand {
    padding: 14px 3px 4px;
  }
  /* READY stays in flow after the main column's deliberate 8px dark gap. The
     hand has no bottom chrome, so this is the only space between cards and the
     button and neither surface overlaps the other. */
  .cards-ready-button {
    position: relative;
    z-index: 40;
    align-self: flex-start !important;
    width: calc(100% - 34px);
    height: 24px;
    min-height: 24px;
    margin-top: 0;
    padding: 0 var(--space-3) !important;
    border-radius: var(--radius-m);
  }
  .cards-ready-button.cards-kit-button { font: var(--type-display-xs); line-height: 1; }
  .cards-event-log {
    display: none !important;
  }
  .cards-match-result-banner {
    box-sizing: border-box;
    width: calc(100vw - 32px);
    min-width: 0 !important;
    max-width: 420px;
    padding: 20px 18px !important;
  }
  .cards-match-result-title { font: var(--type-display-m); }
  .cards-choice-overlay { padding: 12px; box-sizing: border-box; }
  .cards-card-detail-overlay {
    padding: 12px max(12px, env(safe-area-inset-right)) max(12px, env(safe-area-inset-bottom)) max(12px, env(safe-area-inset-left)) !important;
  }
  .cards-card-detail-dialog {
    width: min(${CATALOG_CARD_WIDTH}px, calc((100vw - 48px) / ${CARD_DETAIL_DIALOG_MOBILE_SCALE}));
    max-height: calc((100dvh - 48px) / ${CARD_DETAIL_DIALOG_MOBILE_SCALE});
    /* The card owns its internal text scrolling. Keeping the dialog overflow
       visible gives the tilted card room to project above its resting box. */
    overflow: visible;
    transform: scale(${CARD_DETAIL_DIALOG_MOBILE_SCALE});
    animation: cards-card-detail-dialog-mobile-in var(--dur-base) var(--ease-overshoot);
  }
  .cards-card-detail-dialog-wide {
    width: min(${CATALOG_CARD_WIDTH * 2 + 8}px, calc((100vw - 48px) / ${CARD_DETAIL_DIALOG_MOBILE_SCALE}));
  }
  .cards-card-detail-panel {
    max-height: calc(100dvh - 24px);
    overflow-y: auto;
  }
  .cards-lobby {
    padding:
      max(16px, env(safe-area-inset-top))
      max(16px, env(safe-area-inset-right))
      max(16px, env(safe-area-inset-bottom))
      max(16px, env(safe-area-inset-left)) !important;
  }
  .cards-lobby-panel {
    box-sizing: border-box;
    padding: 18px !important;
  }
  .cards-status-shell {
    padding:
      max(12px, env(safe-area-inset-top))
      max(12px, env(safe-area-inset-right))
      max(12px, env(safe-area-inset-bottom))
      max(12px, env(safe-area-inset-left)) !important;
  }
  .cards-kit-status-panel {
    box-sizing: border-box;
    width: min(100%, 460px) !important;
    padding: 18px !important;
  }
}
/* A landscape phone keeps one complete, scrollable row. Its 9px chrome is the
   same top-only border/padding budget as above; there is no inter-row gap for a
   single row. Portrait phones retain the half-row peek. */
@media (max-width: 900px) and (max-height: 520px),
       (max-height: 520px) and (pointer: coarse) {
  .cards-game-view {
    --cards-hand-card-height: 82px;
    --cards-hand-rows: 1;
    --cards-hand-chrome: 9px;
  }
}
/* Desktop battle keeps the hand readable before spending height on the field.
   The field has the same no-scroll contract as mobile: its two cell rows divide
   whatever remains after the panels, compact spell queues and a fixed 1.5-row
   hand. A shorter hand card preserves the full face without making that
   reservation dominate a laptop-height viewport.

   Every board rule here is scoped to .cards-game-view, exactly as the compact
   breakpoint below scopes its own. The sandbox reuses the same board markup but
   hangs it in an auto-height grid row instead of a stretching stage: unscoped,
   this «divide the leftover column» chain has no column to divide and collapses
   every location, group and cell to zero height. */
@media (min-width: 901px) {
  /* The desktop hand card is 112×150 (Q7, DS4b). The strip shows one row and
     lets a wrapped second row peek out 22px, so a hand that wraps says so
     without spending half a row of the board's height on it. */
  .cards-game-view {
    --cards-desktop-hand-card-height: 150px;
    --cards-desktop-hand-rows: 1.15;
    --cards-hand-area-height: calc(var(--cards-desktop-hand-card-height) * var(--cards-desktop-hand-rows) + 60px);
    --cards-main-padding-bottom: 14px;
  }
  .cards-game-main { position: relative; }
  .cards-game-view .cards-board-stage {
    flex: 1 1 0;
    justify-content: stretch;
  }
  .cards-game-view .cards-board-scroll {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 0;
    padding-bottom: 0;
  }
  .cards-game-view .cards-board-field {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 0;
  }
  .cards-game-view .cards-board-locations {
    flex: 1 1 0;
    min-height: 0;
  }
  .cards-game-view .cards-board-location {
    display: flex;
    flex: 1 1 0;
    flex-direction: column;
    min-height: 0;
  }
  .cards-game-view .cards-board-location-half {
    display: flex;
    flex: 1 1 0;
    align-items: center;
    min-height: 0;
  }
  .cards-game-view .cards-board-group {
    width: 100%;
    height: 100%;
    max-height: 138px;
  }
  .cards-game-view .cards-board-cell { height: 100%; }
  /* Stable whether empty or populated: planning a spell must not move the
     field or the hand. 32px since DS4b: the turn HUD rides the opponent's rail
     at its own height (02 §5.3), and the 28px orbs keep a 20px glyph window. */
  .cards-game-view .cards-spell-queue {
    height: 32px;
    gap: 3px;
    border-radius: var(--radius-round);
    padding: 0 3px;
  }
  .cards-game-view .cards-spell-queue[data-side="opponent"] { margin-bottom: 3px; }
  .cards-game-view .cards-spell-queue[data-side="viewer"] { margin-top: 3px; }
  .cards-game-view .cards-spell-queue-list { gap: 4px; }
  .cards-game-view .cards-spell-orb {
    width: 28px;
    height: 28px;
    flex-basis: 28px;
    padding: 1px;
  }
  .cards-game-view .cards-spell-orb-glyph { inset: 2px; }
  .cards-game-view .cards-playback-hud { height: 32px; }
  /* «Готов» takes the kit's md height and stands to the right of the hand,
     level with its top (05 §2.1): the row it used to take under the hand goes
     to the field. */
  .cards-game-view .cards-ready-button {
    position: absolute;
    right: 16px;
    bottom: calc(var(--cards-main-padding-bottom) + var(--cards-hand-area-height) - 48px);
    height: 48px;
    min-height: 48px;
    margin: 0;
  }
  /* The location's own 3px frame and the 21px header icon (02 §4.1). */
  .cards-game-view .cards-board-location {
    border-width: var(--stroke-l);
    box-shadow: none;
  }
  .cards-game-view .cards-board-location[data-active="true"],
  .cards-game-view .cards-board-location[data-table-highlight="true"],
  .cards-game-view .cards-board-location[data-table-interactive="true"]:hover,
  .cards-game-view .cards-board-location[data-pointer-drag-over="true"] {
    box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
  }
  .cards-game-view .cards-board-location-header { min-height: 24px; }
  .cards-game-view .cards-location-symbol .cards-pixel-icon { width: 21px; height: 21px; vertical-align: -5px; }
  .cards-game-view .cards-spell-orb-mode { display: none; }
  /* 60px is all non-card chrome in the reservation:
     8px row gap + 14px list padding + 38px hand border/header/padding. */
  .cards-game-view .cards-hand-area { flex: 0 0 var(--cards-hand-area-height); }
  .cards-game-view .cards-hand-list {
    align-content: start;
    /* «Готов» stands at the hand's right edge (05 §2.1); the cards keep clear of it. */
    padding-right: calc(112px + var(--space-3));
  }
  .cards-game-view .cards-hand-card,
  .cards-game-view .cards-hand-reorder-placeholder,
  .cards-game-view .cards-touch-drag-preview:not([data-return-flight="true"]) {
    height: var(--cards-desktop-hand-card-height);
  }
  /* The tall card's art window holds the glyph four times over (01 §10). */
  .cards-game-view .cards-hand-glyph svg { width: max(var(--cards-glyph-step), round(down, 80px, var(--cards-glyph-step))); height: max(var(--cards-glyph-step), round(down, 80px, var(--cards-glyph-step))); }
}
/* A low desktop window (≤820px since DS4b) uses the width it has and gives up
   vertical decoration: the square 112px hand card, slimmer panels and rails.
   READY stays the same DOM sibling at the hand's right edge, as on any desktop.
   Scoped to battle for the same reason as the block above: the sandbox shares
   this markup but not this layout contract. */
@media (min-width: 901px) and (max-height: 820px) {
  .cards-game-view {
    --cards-desktop-hand-card-height: 112px;
    --cards-board-padding: 3px;
    --cards-cell-padding: 1px;
    --cards-cell-gap: 3px;
    --cards-location-gap: 8px;
    --cards-overlay-stack-shift: 10px;
  }
  /* A low window keeps the square 112px card, the 25px rails and the 36px
     «Готов» (it floats over the hand here), so the field keeps its height. */
  .cards-game-view .cards-hand-glyph svg { width: max(var(--cards-glyph-step), round(down, 60px, var(--cards-glyph-step))); height: max(var(--cards-glyph-step), round(down, 60px, var(--cards-glyph-step))); }
  .cards-game-view .cards-spell-queue { height: 25px; }
  .cards-game-view .cards-spell-orb { width: 23px; height: 23px; flex-basis: 23px; }
  .cards-game-view .cards-spell-orb-glyph { inset: 1px; }
  .cards-game-view .cards-playback-hud { height: 25px; }
  .cards-game-view {
    --cards-main-padding-bottom: 8px;
  }
  .cards-game-view .cards-ready-button {
    bottom: calc(var(--cards-main-padding-bottom) + var(--cards-hand-area-height) - 36px);
    height: 36px;
    min-height: 36px;
  }
  .cards-game-main {
    gap: 6px;
    padding: 8px 16px;
  }
  .cards-game-view .cards-player-panel {
    grid-template-columns: 22px minmax(0, 1fr);
    gap: 7px;
    min-height: 0;
    box-sizing: border-box;
    padding: 2px 8px;
  }
  .cards-game-view .cards-player-avatar {
    width: 22px;
    height: 22px;
    padding: 1px;
  }
  .cards-game-view .cards-player-content { gap: 3px 10px; }
  .cards-game-view .cards-player-topline { gap: 4px 10px; }
  .cards-game-view .cards-player-identity { gap: 5px; }
  .cards-game-view .cards-player-name-badge {
    max-width: 110px;
    padding: 1px 4px;
    font: var(--type-display-3xs);
  }
  .cards-game-view .cards-player-stats { gap: 3px 9px; }
  .cards-game-view .cards-player-health,
  .cards-game-view .cards-player-energy,
  .cards-game-view .cards-player-deck.cards-kit-button,
  .cards-game-view .cards-player-deck {
    font-size: var(--type-display-2xs-size);
  }
  .cards-game-view .cards-player-secondary { display: none; }
  /* The ~24px panel (02 §5.1 compact): the chip drops to display-xs, the coins to 12px. */
  .cards-game-view .cards-player-health > span { height: 18px; padding: 0 var(--space-1); font: var(--type-display-xs); line-height: 1; }
  .cards-game-view .cards-player-health .cards-pixel-icon { width: 7px; height: 7px; }
  .cards-game-view .cards-player-energy-coin { width: 12px; height: 12px; }
  .cards-game-view .cards-player-passive { max-width: 130px; }
  .cards-game-view .cards-player-panel .cards-deck-passive-details-compact { gap: 4px; }
  .cards-game-view .cards-player-panel .cards-deck-passive-glyph {
    width: 14px;
    height: 14px;
  }
  .cards-game-view .cards-player-panel .cards-deck-passive-heading strong { font-size: var(--type-text-xs-size); }
  .cards-game-view .cards-board-scroll {
    width: calc(100% + 16px);
    max-width: none;
    margin-inline: -8px;
    padding-inline: 8px;
    box-sizing: border-box;
  }
  .cards-game-view .cards-board-field {
    width: 100%;
    min-width: 0;
    padding: var(--cards-board-padding);
  }
  .cards-game-view .cards-board-location {
    padding: 3px;
    border-width: 1.5px;
    border-radius: var(--radius-s);
  }
  .cards-game-view .cards-board-location-header {
    min-height: 15px;
    gap: 3px;
    margin-bottom: 3px;
    padding: 0 2px 2px;
    font: var(--type-display-3xs);
    line-height: 1;
  }
  .cards-game-view .cards-location-symbol .cards-pixel-icon { width: 14px; height: 14px; vertical-align: -3px; }
  .cards-game-view .cards-board-front-line { height: 10px; }
  .cards-game-view .cards-board-card,
  .cards-game-view .cards-staged-card { padding: 1px; }
  .cards-game-view .cards-board-card-art,
  .cards-game-view .cards-staged-card-art { inset: 2px 2px 27px; }
  .cards-game-view .cards-board-card-name {
    inset-inline: 3px;
    bottom: 13px;
    font-size: var(--type-text-xs-size);
  }
  .cards-game-view .cards-board-card[data-card-type="SPELL"] .cards-board-card-name,
  .cards-game-view .cards-staged-card[data-card-type="SPELL"] .cards-board-card-name {
    bottom: 2px;
  }
  .cards-game-view .cards-board-card-stats,
  .cards-game-view .cards-staged-card-stats {
    inset-inline: 3px;
    bottom: 2px;
    gap: 0;
  }
  .cards-game-view .cards-board-card-stats .cards-card-stat-icon,
  .cards-game-view .cards-staged-card-stats .cards-card-stat-icon {
    display: none;
  }
  .cards-game-view .cards-board-card-stats .cards-card-stat-value,
  .cards-game-view .cards-staged-card-stats .cards-card-stat-value {
    font-size: var(--type-display-2xs-size);
  }
  /* The low window keeps the 16px chip: its name strip sits 13px up. */
  .cards-game-view .cards-board-card-stats .cards-card-stat,
  .cards-game-view .cards-staged-card-stats .cards-card-stat { min-width: 20px; height: 16px; }
  .cards-game-view .cards-kit-badge.cards-cell-effect-badge {
    padding: 0 1px;
    border: 0;
  }
}
/* Portrait phones stack the three locations instead of racing them across the
   width. Side by side, a slot is ~35px wide — an icon and nothing else. Stacked,
   it is ~113px, which fits the glyph next to a readable name and its stats. The
   trade is height: six cell rows instead of two, so a slot turns landscape.
   Gated on min-height because the trade only pays once there is enough column to
   split; short and landscape viewports keep the row layout above.

   Every step from the stage down to the cell grows/shrinks instead of carrying a
   fixed height, so the available column is divided rather than reserved: no
   arithmetic here has to predict the hand, the panels or the browser chrome, and
   a shorter phone gets shorter slots rather than a clipped board. */
@media (max-width: 900px) and (min-height: 700px) {
  .cards-game-view {
    /* Three lanes across the full width now, not nine. The 52px tallies the
       fixed horizontal chrome: game-main padding (16) + field padding (6) + the
       location's border (3) and padding (6) + the header rail (13) and its gap
       (4) + two cell gaps (4). */
    --cards-cell-width: calc((100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 52px) / 3);
    /* Bleed room. The card art hangs past the card's own frame here, and this ring
       is the first thing it hangs into (it passes clean through — the cell does
       not clip). Cell padding costs the card 4px of each dimension but no board
       height at all, because the cell itself is stretched to the half it sits in,
       and it buys the overhang its distance from the cell's own frame. */
    --cards-cell-padding: 3px;
  }
  /* The sandbox's own three lanes are set alongside its other metrics, in
     SURFACE_CSS: its compact block assigns --cards-cell-width for nine lanes and
     sits later in KIT_CSS, so an override here at the same specificity would lose.
     Everything below is scoped to the two surfaces that mount a Board, and has to
     stay scoped: it divides a column between three stacked locations, which only
     means anything where some ancestor has a definite height to divide. In the
     battle that column is the board stage; in the sandbox it is the middle row of
     .lab-sandbox-field, which this breakpoint pins to the leftover height for
     exactly this reason (unscoped, these rules once matched the sandbox's
     auto-height grid and collapsed its cells and locations to nothing). Each
     surface renders its own flight ghost inside itself, so the card faces further
     down stay reachable from both. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-stage { flex: 1 1 auto !important; }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-scroll {
    display: flex;
    flex: 1 1 auto !important;
    flex-direction: column;
    min-height: 0;
    padding-bottom: 0 !important;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-field {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-locations {
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
  }
  /* The location becomes a grid so its title can move to a vertical rail on the
     left: as a header row it cost ~15px of every location's height, which is a
     third of a slot, and horizontal room is the one thing this layout has. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-location {
    display: grid;
    flex: 1 1 0;
    grid-template-columns: 13px minmax(0, 1fr);
    grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr);
    column-gap: 4px;
    min-height: 0;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-location::after { inset: 0 0 0 13px; }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-location-header {
    grid-column: 1;
    grid-row: 1 / 4;
    /* Physical border: in vertical writing mode the logical inline axis runs
       down the rail, so border-inline-end would draw across the text. */
    border-right: var(--stroke-s) solid var(--c-panel-edge);
    border-bottom: 0;
    writing-mode: vertical-rl;
    justify-content: center;
    min-height: 0;
    margin: 0;
    padding: 2px 0;
    font-size: var(--type-display-3xs-size);
  }
  /* The same symbol already sits in the front line, and an inline SVG turns on
     its side in vertical writing mode. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-location-header .cards-location-symbol { display: none; }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-location-half {
    grid-column: 2;
    min-height: 0;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-location-half[data-side="opponent"] { grid-row: 1; }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-front-line {
    grid-column: 2;
    grid-row: 2;
    height: 9px !important;
    /* Above the cards, not among them. The glyphs of both halves now lean 3px
       into this band, and the divider plus the location's symbol are the two
       things in the location that must stay readable through them. A resting card
       is z-index auto, while every card in flight is lifted to 5+ by Board — so
       this raise keeps the line over the still board without ever cutting across
       a lunge or a card travelling between locations. */
    z-index: 3;
  }
  /* …but a pile is not the still board. Cards stack UPWARD out of the slot (see
     --cards-stack-step), so a plan two or three deep stands in the front line's
     band and well past it, and the raise above cut the line across the cards —
     the one thing in the location that must never be hidden hiding the thing the
     player is actually looking at. The cell isolates its own stacking context, so
     the pile cannot climb out on its own: the CELL has to, and only while it
     overhangs (Cell marks that). 4 clears the line and stays under every flight
     lift, which Board writes as inline z-index 5+. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-cell[data-stack-overhang="true"] {
    z-index: 4;
  }
  /* The symbol sits dead centre, which is exactly where the middle slot's glyph
     leans in, so it carries the field's own background as a chip: the art passes
     behind it instead of through it. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-front-line > span {
    padding: 0 3px;
    border-radius: var(--radius-xs);
    background: var(--c-attack);
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-location-half[data-side="viewer"] { grid-row: 3; }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-group { height: 100%; }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-cell {
    height: 100% !important;
    /* The cell is the card's measuring stick. Its height is whatever the column
       divided out — 42px on a 393x849 phone, 60px on a tall big one — and the stat
       pair is the one thing in the card that cannot be told to shrink, so it has
       to be told the number (see --cards-field-stat-size). Both dimensions here
       are definite (width from --cards-cell-instance-width, height from the row),
       which is what container-type: size requires; the containment it brings is
       already true of the cell, which isolates its own stacking context. */
    container-type: size;
  }

  /* A landscape slot needs a landscape face, and it cannot be built out of
     absolute halves: a 33px glyph column was a third of a phone card but an
     eighth of the 232px card a big phone hands out, so the middle of the slot
     went empty and the pair of numbers sat in a corner of it. Lay the face out
     as a flex row instead — glyph, then stats — and both halves centre
     themselves in whatever the slot actually is. Staged cards and flight ghosts
     share every rule, so a landing or reveal still cross-dissolves in place. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card,
  /* The return-flight ghost's compact face is not a card element but wears the
     card's own children, and it has to stay pixel-identical to the face it
     dissolves into — so it takes the row too. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-return-face-compact {
    display: flex;
    justify-content: center;
    /* Narrow phones are the other case — there the glyph is width-bound with
       height to spare, so every pixel between it and the numbers is a pixel off
       the art. Keep them apart, barely. */
    gap: 2px;
  }
  /* The height the numbers are allowed. A query length measures its container's
     CONTENT box, and the cell's content box is exactly the card's border box (the
     card fills it), so the two figures plus their 2px gap have to fit in
     100cqh - 6, the card's own border and padding coming off: f = 50cqh - 4. 19px
     is the size worth reading and the cap, so nothing changes on a tall phone; a
     42px cell hands out ~13px instead of printing the pair through the frame,
     which is what small phones were doing. The floor is the smallest that still
     fits a 24px card, and the clamp's upper bound means a missing container (cq
     falls back to the viewport) reads as the old flat 19px, not something absurd. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card {
    --cards-field-stat-size: clamp(8px, calc(50cqh - 4px), 19px);
  }
  /* The ghost's face has to end up on the landed card's content box, or the glyph
     and the numbers step at the cross-dissolve. Its box is the destination's
     BORDER box (--cards-return-dst-w/h, measured), it draws the card's own 2px
     type frame, and border-box sizing plus the card's own 1px padding then leave a
     content box of dst - 6 — which is the landed card's, art and stats included.
     The face is the container rather than the flight box, whose box is the release
     footprint and belongs to the hand face; a container cannot query itself, so
     the size is declared on the stats inside. It measures dst - 6 where the cell
     measures dst, hence the -1px against the card's -4px below: both come out at
     50% of the destination height less 4. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-return-face-compact {
    box-sizing: border-box;
    padding: 1px;
    container-type: size;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-return-face-compact .cards-staged-card-stats {
    --cards-field-stat-size: clamp(8px, calc(50cqh - 1px), 19px);
  }
  /* The art takes the leftover width in its own light window, like every card
     (look A, Q14): on the dark face a glyph needs its light ground, so it no
     longer bleeds past the card's edges. The glyph is the largest whole
     multiple of its grid that fits the window (01 §10). */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-art,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card-art {
    position: static;
    flex: 1 1 0;
    min-width: 0;
    container-type: size;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-art svg,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card-art svg {
    width: max(var(--cards-glyph-step), round(down, min(100cqw, 100cqh), var(--cards-glyph-step))) !important;
    height: max(var(--cards-glyph-step), round(down, min(100cqw, 100cqh), var(--cards-glyph-step))) !important;
  }
  /* A spell face (only reachable on the board while one fizzles) takes the same
     row, with its name where a unit keeps its numbers — the attack/health pair
     it renders is 0/0 and has never meant anything. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card[data-card-type="SPELL"] .cards-board-card-stats,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card[data-card-type="SPELL"] .cards-staged-card-stats {
    display: none;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-name {
    inset-inline: 37px 3px;
    top: 4px;
    bottom: auto;
    font-size: var(--type-text-xs-size) !important;
  }
  /* The name comes off the field card here. At this scale the 33px glyph IS the
     identity, and most unit names are too long for a 68px column — they became a
     scrolling marquee, which is motion the player has to wait out instead of
     read. Tap (or hover) still gives the full card with its name, and the column
     the label vacates goes to the stat pair below. Spells keep theirs: they have
     no attack/health, so the name is the only thing they can show. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card:not([data-card-type="SPELL"]) .cards-board-card-name,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card:not([data-card-type="SPELL"]) .cards-board-card-name,
  /* The flight ghost's compact face too, or the label the landed card does not
     have grows in over the glyph and the numbers while the card resizes onto its
     slot — the only place this name was still visible, and it read as a bug. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-return-face-compact:not([data-card-type="SPELL"]) .cards-board-card-name {
    display: none;
  }
  /* A spell's name is the row's second item, standing in for the numbers it does
     not have. Half the card, not its natural width: the label is what has to be
     clipped and scrolled when it is long, never the glyph. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card[data-card-type="SPELL"] .cards-board-card-name,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card[data-card-type="SPELL"] .cards-board-card-name,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-return-face-compact[data-card-type="SPELL"] .cards-board-card-name {
    position: static;
    flex: 0 1 50%;
    align-self: center;
    width: auto;
    inset: auto;
    text-align: center;
  }
  /* Stacked, not side by side, and the row's second item rather than a strip
     pinned to an edge: damage over health each gets its own line at a size worth
     reading, centred against the glyph instead of hugging a corner. The pair
     never shrinks (flex 0 0 auto) — whatever it needs is taken off the glyph,
     which has the whole rest of the card to lose it in. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-stats,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card-stats {
    position: static;
    flex: 0 0 auto;
    inset: auto !important;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* The 2px the fit above budgets for. */
    gap: 2px !important;
    letter-spacing: normal;
  }
  /* The icons were dropped at ~33px of strip width, where they cost the numbers
     their last pixels. The text column is ~68px wide, so attack and health can
     be spelled out again instead of relying on left/right position alone. They
     ride the figure's size so they never end up bigger than the number they
     label on a short cell. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-stats .cards-card-stat-icon,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card-stats .cards-card-stat-icon {
    display: inline !important;
    font-size: calc(var(--cards-field-stat-size, 19px) * .47) !important;
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-stats .cards-card-stat-value,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card-stats .cards-card-stat-value {
    font-size: var(--cards-field-stat-size, 19px) !important;
  }
  /* Three digits no longer need the 7px emergency step the one-row strip used:
     each figure owns its own line here, so it only has to stay narrow enough
     that what it takes off the glyph is affordable. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-stats .cards-card-stat-value[data-len="3"],
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card-stats .cards-card-stat-value[data-len="3"] {
    font-size: calc(var(--cards-field-stat-size, 19px) * .74) !important;
  }
  /* Badges keep the card's left edge, out of the stat pair on the right — but
     the glyph now reaches under them, so they carry their own halo instead of
     relying on the empty card surface they used to sit on. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-statuses,
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-m14-markers {
    left: 1px;
    right: auto;
    /* Two rows, both on the left edge: statuses along the top, markers along the
       bottom. A 34px card has room for exactly that, which is why neither may
       grow into the other's row — the statuses wrap by default, and a second
       wrapped line landed straight on the markers. Whatever does not fit is
       clipped instead of moving, and 45% keeps both clear of the numbers. */
    flex-direction: row;
    flex-wrap: nowrap;
    overflow: hidden;
    max-width: 45%;
    font: var(--type-display-3xs);
  }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-statuses { top: 1px; }
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-m14-markers {
    top: auto;
    bottom: 1px;
  }
  /* The status plates shrink to the 7px icon (one device pixel a cell). */
  :is(.cards-game-view, .cards-lab-sandbox) :is(.cards-board-card-statuses, .cards-board-card-m14-markers) [data-tone] {
    padding: 1px;
    border: 0;
  }
  /* A 9px plate has no room for the heat scale: the number says it. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-statuses [data-tone="heat"][data-level] {
    padding-bottom: 1px;
    background: var(--cards-tone-face);
  }
  :is(.cards-game-view, .cards-lab-sandbox) :is(.cards-board-card-statuses, .cards-board-card-m14-markers) .cards-pixel-icon {
    width: 7px;
    height: 7px;
  }
  /* Here the art leans past the frame on purpose (see the bleed above), so it
     has no window to sit in; the numbers are ink beside their coloured icons
     rather than chips, which the stacked column has no room for (02 §3.1 XS). */
  :is(.cards-game-view, .cards-lab-sandbox) :is(.cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat {
    min-width: 0;
    height: auto;
    padding: 0;
    border: 0;
    background: transparent;
    box-shadow: none;
    color: var(--cards-card-ink);
    text-shadow: none;
  }
  :is(.cards-game-view, .cards-lab-sandbox) :is(.cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat-value {
    font-family: var(--font-text);
    font-weight: 700;
  }
  :is(.cards-game-view, .cards-lab-sandbox) :is(.cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat:first-child .cards-card-stat-icon { color: var(--c-attack); }
  :is(.cards-game-view, .cards-lab-sandbox) :is(.cards-board-card-stats, .cards-staged-card-stats) .cards-card-stat:last-child .cards-card-stat-icon { color: var(--c-health); }
  /* Without a chip there is no corner to mark; the details still say it. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-board-card-stats .cards-card-stat[data-stat-change]::before { display: none; }
  /* The plan tag moves off the number column to the bottom-left corner. */
  :is(.cards-game-view, .cards-lab-sandbox) .cards-staged-card:not([data-buried="true"])::before {
    display: block;
    top: auto;
    right: auto;
    bottom: 1px;
    left: 1px;
  }
}
@media (max-width: 420px) {
  .cards-player-panel { gap: 4px 7px !important; }
  .cards-player-name-badge { font-size: var(--type-display-3xs-size) !important; }
  .cards-board-field { padding: 3px !important; }
  .cards-lobby {
    padding:
      max(10px, env(safe-area-inset-top))
      max(10px, env(safe-area-inset-right))
      max(10px, env(safe-area-inset-bottom))
      max(10px, env(safe-area-inset-left)) !important;
  }
  .cards-lobby-panel { padding: 16px !important; }
}
/* Landscape phone (05 §2.4, K3): height is the scarce side — the hand under
   the field left it 47px for three locations. So the hand becomes a two-card
   column at the field's right with READY under it beside your panel, and the
   field keeps the whole height between the two panels. */
@media (max-height: 520px) and (pointer: coarse) and (orientation: landscape) {
  .cards-game-main {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) 164px;
    grid-template-rows: auto minmax(0, 1fr) auto;
    grid-template-areas: "opp opp" "board hand" "mine ready";
  }
  .cards-scoreboard-opponent { grid-area: opp; }
  .cards-board-stage { grid-area: board; }
  .cards-scoreboard-mine { grid-area: mine; }
  .cards-hand-area > .cards-hand {
    grid-area: hand;
    min-height: 0;
    margin-inline: 0;
    padding: 0 !important;
  }
  .cards-hand-list {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    height: 100%;
    padding: 0;
  }
  .cards-ready-button {
    grid-area: ready;
    width: 100%;
    align-self: center !important;
  }
}
@media (max-height: 520px) and (pointer: coarse) {
  .cards-hand-card {
    flex-basis: 76px;
    width: 76px !important;
    min-width: 76px !important;
  }
  .cards-hand-reorder-placeholder {
    flex-basis: 76px;
    width: 76px !important;
    min-width: 76px !important;
  }
}
@media (prefers-reduced-motion: reduce) {
  .cards-card-detail-dialog { animation: none; }
  .cards-hand-card,
  .cards-hand-reorder-placeholder {
    transform: none !important;
    will-change: auto;
  }
  .cards-hand-card::after { opacity: 0 !important; }
  .cards-hand-glyph { transform: none !important; }
  .cards-staged-card[data-motion-active="true"] { transform: none !important; }
  .cards-staged-card::after { opacity: 0 !important; }
  /* The global rule below truncates durations but not delays, and the draw-in
     fills backwards: any delay on it would hold the card invisible instead of
     merely skipping the travel. Keep it pinned at zero. */
  .cards-hand-card[data-drawn="true"] { animation-delay: 0s !important; }
  .cards-touch-drag-preview {
    /* No tilt/skew under reduced motion, but the pointer position must keep
       riding the transform chain or the preview sticks to the viewport corner. */
    transform:
      translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
      translate(-50%, calc(-100% - var(--cards-drag-gap, 18px))) !important;
  }
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}


`;

export const SURFACE_CSS = `
/* Challenges (05 §7). The felt spans the whole screen; the content keeps the
   1180px column the deck and catalog pages use. */
.challenge-page {
  width: 100%;
  padding-block: clamp(var(--space-4), 3vw, var(--space-8));
  padding-inline: max(clamp(var(--space-4), 4vw, var(--space-12)), calc((100% - 1180px) / 2));
}
.challenge-track-list { display: grid; gap: 0; }
.challenge-track {
  min-width: 0;
  padding: var(--space-5) 0 var(--space-6);
  border: 0;
  border-bottom: var(--stroke-l) solid var(--c-table-rule);
  background: transparent;
}
.challenge-track-content { min-width: 0; display: grid; gap: var(--space-4); }
.challenge-track .cards-kit-panel-header-title { margin: 0 0 var(--space-2); }
.challenge-track ol { margin-block: 0; }
.challenge-stage-flow {
  /* One shared node band: every timeline node — stages and reward alike — sits
     on the same axis, and the connectors live inside that band only, so no rail
     can ever cross a stage card. Nodes are 40px coins. */
  --challenge-node: 40px;
  --challenge-node-gap: var(--space-3);
  --challenge-stage-gap: var(--space-3);
  --challenge-flow-gap: var(--space-5);
  --challenge-reward-col: ${CATALOG_CARD_WIDTH}px;
  --challenge-card-pad: var(--space-3);
  --challenge-card-pad-x: var(--space-4);
  --challenge-rail-width: var(--stroke-l);
  /* A pixel dotted rail: 6px dashes on a 12px period, cream at 30%. */
  --challenge-rail: repeating-linear-gradient(90deg, color-mix(in srgb, var(--c-text-on-dark) 30%, transparent) 0 6px, transparent 6px 12px);
  --challenge-rail-done: var(--c-selection);
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) var(--challenge-reward-col);
  align-items: stretch;
  column-gap: var(--challenge-flow-gap);
  row-gap: var(--challenge-flow-gap);
}
.challenge-stage-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  /* Stages stretch to each other, never to the reward column: an unlocked card
     is 338px tall and would otherwise strand the actions far below the lesson. */
  align-self: start;
  align-items: stretch;
  column-gap: var(--challenge-stage-gap);
  padding: 0;
  list-style: none;
}
.challenge-stage-list > li {
  position: relative;
  min-width: 0;
  display: grid;
  grid-template-rows: var(--challenge-node) minmax(0, 1fr);
  row-gap: var(--challenge-node-gap);
}
/* Each stage owns the rail to its successor; the last one reaches the reward. */
.challenge-stage-list > li::after {
  content: "";
  position: absolute;
  top: calc(var(--challenge-node) / 2 - var(--challenge-rail-width) / 2);
  left: calc(50% + var(--challenge-node) / 2);
  width: calc(100% + var(--challenge-stage-gap) - var(--challenge-node));
  height: var(--challenge-rail-width);
  background: var(--challenge-rail);
}
.challenge-stage-list > li:last-child::after {
  width: calc(50% + var(--challenge-flow-gap) + var(--challenge-reward-col) / 2 - var(--challenge-node));
}
.challenge-stage-list > li[data-status="completed"]::after {
  background: var(--challenge-rail-done);
}
/* Stage coin (05 §7): passed — gold with a check, current — a gold-rimmed coin
   under a blinking aim, locked — slate with a padlock. */
.challenge-stage-node,
.challenge-reward-node {
  position: relative;
  z-index: 1;
  grid-row: 1;
  justify-self: center;
  display: grid;
  width: var(--challenge-node);
  height: var(--challenge-node);
  place-items: center;
  box-sizing: border-box;
  border: var(--stroke-l) solid var(--c-outline);
  border-radius: var(--radius-round);
  background: var(--c-panel-raised);
  box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
  color: var(--c-text-disabled);
  line-height: 1;
}
.challenge-stage-node .cards-pixel-icon,
.challenge-reward-node .cards-pixel-icon { width: 14px; height: 14px; }
.challenge-stage-accessible-state {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}
.challenge-stage-list > li[data-status="completed"] {
  color: var(--c-text-on-dark);
}
.challenge-stage-list > li[data-status="completed"] .challenge-stage-node,
.challenge-reward[data-status="unlocked"] .challenge-reward-node {
  background: var(--c-energy);
  color: var(--c-text-on-energy);
}
/* Current stage: a gold-rimmed coin with a gold pip, framed by an aim that
   breathes in two frames — the visible «Бой N» already numbers it. */
.challenge-stage-list > li[data-status="available"] .challenge-stage-node {
  border-color: var(--c-selection);
  background: var(--c-panel-bg);
}
.challenge-stage-list > li[data-status="available"] .challenge-stage-node::after {
  content: "";
  width: 12px;
  height: 12px;
  border-radius: var(--radius-round);
  background: var(--c-selection);
}
.challenge-stage-list > li[data-status="available"] .challenge-stage-node::before {
  content: "";
  position: absolute;
  inset: calc(var(--space-2) * -1);
  border: var(--stroke-m) solid var(--c-selection);
  border-radius: var(--radius-xs);
  /* Only the corners show: an aim, not a box. */
  -webkit-mask: linear-gradient(var(--c-outline) 0 0) top left / 8px 8px no-repeat, linear-gradient(var(--c-outline) 0 0) top right / 8px 8px no-repeat,
    linear-gradient(var(--c-outline) 0 0) bottom left / 8px 8px no-repeat, linear-gradient(var(--c-outline) 0 0) bottom right / 8px 8px no-repeat;
  mask: linear-gradient(var(--c-outline) 0 0) top left / 8px 8px no-repeat, linear-gradient(var(--c-outline) 0 0) top right / 8px 8px no-repeat,
    linear-gradient(var(--c-outline) 0 0) bottom left / 8px 8px no-repeat, linear-gradient(var(--c-outline) 0 0) bottom right / 8px 8px no-repeat;
  animation: cards-aim-breathe var(--dur-loader) var(--steps-blink) infinite;
}
@keyframes cards-aim-breathe {
  from { transform: scale(1); }
  to { transform: scale(1.12); }
}
.challenge-stage-list > li[data-status="locked"] { color: var(--c-text-muted-on-table); }
.challenge-stage-list > li[data-status="locked"] .challenge-stage-card { opacity: .6; }
.challenge-stage-card {
  /* Identical box in every column — the shared padding and the flexible lesson
     row are what align the meta, title and action baselines across stages. */
  grid-row: 2;
  min-width: 0;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr) auto;
  gap: var(--space-2);
  box-sizing: border-box;
  padding: var(--challenge-card-pad) var(--challenge-card-pad-x);
  border: var(--stroke-l) solid transparent;
  border-radius: var(--radius-xl);
  background: transparent;
}
/* The fight to play next is the one object on the table. */
.challenge-stage-list > li[data-status="available"] .challenge-stage-card {
  border-color: var(--c-selection);
  background: var(--c-panel-bg);
  box-shadow: 0 var(--step-3) 0 var(--c-step-shadow);
}
.challenge-stage-meta { display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2); color: var(--c-text-muted-on-table); font: var(--type-display-3xs); text-transform: uppercase; }
.challenge-stage-list > li[data-status="available"] .challenge-stage-meta { color: var(--c-text-muted-on-dark); }
.challenge-stage-title { min-width: 0; font: var(--type-text-l); font-weight: 600; }
.challenge-stage-lesson { margin: 0; color: var(--c-text-muted-on-table); font: var(--type-text-s); }
.challenge-stage-list > li[data-status="available"] .challenge-stage-lesson { color: var(--c-text-muted-on-dark); }
.challenge-stage-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  align-self: end;
  gap: var(--space-2);
}
.challenge-deck-action { gap: var(--space-1); }
/* Ghost actions have no visible box, so their label starts on the text edge. */
.challenge-stage-actions > .cards-kit-button--ghost { padding-inline: 0; }
.challenge-deck-action .cards-pixel-icon { width: 14px; height: 14px; }
.challenge-reward {
  min-width: 0;
  display: grid;
  grid-template-rows: var(--challenge-node) minmax(0, auto);
  row-gap: var(--challenge-node-gap);
  align-self: start;
}
.challenge-reward-body {
  min-width: 0;
  display: grid;
  gap: var(--space-2);
  /* Matches a stage card's top padding, so «Награда за трек» keeps the «Бой N» baseline. */
  padding-top: var(--challenge-card-pad);
}
.challenge-reward-label { color: var(--c-selection); font: var(--type-display-3xs); text-transform: uppercase; }
/* Locked: a quiet slot drawn on the table, so the teaser never outshines the
   «Играть» button. */
.challenge-reward-slot {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  box-sizing: border-box;
  width: 100%;
  padding: var(--space-2) var(--space-3);
  border: var(--stroke-l) dashed var(--cards-slot-dash);
  border-radius: var(--radius-m);
  background: var(--c-zone-bg);
  color: var(--c-text-muted-on-table);
  font: var(--type-text-s);
}
.challenge-reward-body > .cards-kit-alert { width: 100%; }
.challenge-result-actions { flex-wrap: wrap; }
@media (prefers-reduced-motion: reduce) {
  .challenge-stage-list > li[data-status="available"] .challenge-stage-node::before { animation: none; }
}
/* Below the two-column threshold the reward becomes the timeline's last row. */
@media (max-width: 1050px) {
  .challenge-stage-flow { grid-template-columns: minmax(0, 1fr); }
  .challenge-stage-list > li:last-child::after { display: none; }
  .challenge-reward { grid-template-rows: auto; }
  /* Wrapped under the stages, the reward shares their text gutter. */
  .challenge-reward-body {
    justify-items: start;
    padding-top: 0;
    padding-inline-start: var(--challenge-card-pad-x);
  }
  .challenge-reward-slot { width: auto; }
  .challenge-reward-body > .cards-catalog-card { width: min(100%, var(--challenge-reward-col)); }
}
@media (min-width: 721px) and (max-width: 1050px) {
  .challenge-stage-flow { --challenge-card-pad-x: var(--space-3); }
  /* The timeline is still horizontal while the reward has wrapped underneath it,
     so a reward node here would float with nothing to connect to: the label and
     the card itself carry the locked/unlocked state instead. */
  .challenge-reward { grid-template-columns: minmax(0, 1fr); }
  .challenge-reward-node { display: none; }
}
@media (max-width: 720px) {
  /* Vertical timeline: the reward rejoins the node axis as its final stop. */
  .challenge-reward {
    grid-template-columns: var(--challenge-node) minmax(0, 1fr);
    column-gap: var(--space-4);
  }
  .challenge-reward-body {
    grid-row: 1;
    grid-column: 2;
    padding-top: var(--challenge-card-pad);
  }
}
@media (max-width: 720px) {
  .challenge-track { padding: var(--space-4) 0 var(--space-5); }
  .challenge-stage-flow {
    --challenge-rail: repeating-linear-gradient(180deg, color-mix(in srgb, var(--c-text-on-dark) 30%, transparent) 0 6px, transparent 6px 12px);
  }
  .challenge-stage-list { grid-template-columns: minmax(0, 1fr); row-gap: var(--challenge-stage-gap); }
  .challenge-stage-list > li {
    grid-template-columns: var(--challenge-node) minmax(0, 1fr);
    grid-template-rows: auto;
    column-gap: var(--space-4);
  }
  .challenge-stage-card { grid-row: 1; grid-column: 2; }
  /* The rail turns vertical: node bottom down to the next node's top edge. */
  .challenge-stage-list > li::after {
    top: var(--challenge-node);
    left: calc(var(--challenge-node) / 2 - var(--challenge-rail-width) / 2);
    width: var(--challenge-rail-width);
    height: calc(100% - var(--challenge-node) + var(--challenge-stage-gap));
  }
  .challenge-stage-list > li:last-child::after {
    display: block;
    width: var(--challenge-rail-width);
    height: calc(100% - var(--challenge-node) + var(--challenge-flow-gap));
  }
  /* The page header must not eat the first screen before the first action. */
  .challenge-page > .cards-kit-panel-header .cards-kit-panel-header-title { margin: 0 0 var(--space-1); }
}

        .auth-app-shell {
          width: 100%;
          height: calc(100dvh - var(--cards-beta-ticker-reserve));
          min-height: 0;
          display: grid;
          grid-template-rows: 40px minmax(0, 1fr);
          overflow: hidden;
          background: var(--c-app-bg);
          color: var(--c-text-on-dark);
          /* Family only: the battle inherits size and line height from here, and
             its layout is measured against them until DS4b. */
          font-family: var(--font-text);
        }
        .auth-app-shell-menu-hidden { grid-template-rows: minmax(0, 1fr); }
        /* Top menu (02 §9): a slate strip with a 3px dark floor; pixel icon
           links in cream, the current one gold and underlined. */
        .auth-top-menu {
          position: relative;
          z-index: 30;
          display: flex;
          align-items: center;
          justify-content: space-between;
          min-width: 0;
          box-sizing: border-box;
          padding: 0 var(--space-3);
          border-bottom: var(--stroke-l) solid var(--c-step-shadow);
          background: var(--c-housing-bg);
          color: var(--c-text-on-dark);
          font: var(--type-text-s);
          font-weight: 600;
          line-height: 1;
        }
        .auth-home-link,
        .auth-feedback-trigger,
        .auth-changelog-trigger,
        .auth-menu-logout {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          border: 0;
          padding: 0;
          background: none;
          box-shadow: none;
          color: inherit;
          font: inherit;
          line-height: inherit;
          text-decoration: none;
          cursor: pointer;
        }
        .auth-home-link { align-self: stretch; border-bottom: var(--stroke-l) solid transparent; margin-bottom: calc(var(--stroke-l) * -1); }
        /* The link spans the whole 40px bar, so its focus ring is drawn inside. */
        .auth-home-link:focus-visible { outline-offset: calc(var(--stroke-l) * -1); }
        .auth-home-link[aria-current="page"] {
          border-bottom-color: var(--c-selection);
          color: var(--c-selection);
        }
        .auth-home-link:hover,
        .auth-feedback-trigger:hover,
        .auth-changelog-trigger:hover,
        .auth-menu-logout:hover:not(:disabled) {
          color: var(--c-selection);
          text-decoration: none;
          transform: none;
        }
        .auth-menu-end,
        .auth-menu-account {
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: var(--space-4);
        }
        .auth-menu-links { display: flex; align-self: stretch; align-items: stretch; gap: var(--space-5); }
        .auth-menu-logo { display: block; flex: 0 0 auto; align-self: center; }
        /* Menu glyphs are 9x9 bitmaps drawn in an 18px box — exactly two device
           pixels per cell; any other size renders uneven rows. */
        .auth-menu-icon {
          display: block;
          flex: 0 0 auto;
          width: 18px;
          height: 18px;
        }
        /* The tail (фидбек / обновления / выйти) is glyph-only at every width;
           its labels stay in the a11y tree and the title carries the wording. */
        .auth-feedback-trigger .auth-menu-label,
        .auth-changelog-trigger .auth-menu-label,
        .auth-menu-logout .auth-menu-label {
          position: absolute;
          width: 1px;
          height: 1px;
          overflow: hidden;
          clip-path: inset(50%);
          white-space: nowrap;
        }
        .auth-menu-nickname {
          max-width: min(190px, 45vw);
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .auth-menu-logout:disabled { cursor: wait; color: var(--c-text-disabled); }
        .auth-feedback-trigger,
        .auth-changelog-trigger,
        .auth-menu-logout {
          min-height: 0;
          border: 0;
          padding: 0;
          color: inherit;
          font: inherit;
        }
        .auth-menu-error {
          position: absolute;
          top: 100%;
          right: var(--space-2);
          max-width: min(320px, 85vw);
          padding: var(--space-2) var(--space-3);
          border: var(--stroke-m) solid var(--c-negative);
          border-radius: var(--radius-m);
          background: var(--c-panel-bg);
          color: var(--c-text-negative-on-dark);
          font: var(--type-text-s);
        }
        .auth-app-content {
          min-height: 0;
          overflow: auto;
        }
        .auth-app-content > :not(.auth-account-panel) {
          height: 100% !important;
          min-height: 0 !important;
          max-height: 100% !important;
        }
        @media (max-width: 620px) {
          .auth-app-shell {
            grid-template-rows: 34px minmax(0, 1fr);
          }
          .auth-app-shell-menu-hidden {
            grid-template-rows: minmax(0, 1fr);
          }
          .auth-top-menu { padding-inline: var(--space-2); }
          .auth-menu-links { gap: var(--space-4); }
          .auth-menu-logo { width: 16px; height: auto; }
          .auth-menu-end, .auth-menu-account { gap: var(--space-3); }
          /* Phones keep only the glyphs; the labels stay in the a11y tree. */
          .auth-menu-label {
            position: absolute;
            width: 1px;
            height: 1px;
            overflow: hidden;
            clip-path: inset(50%);
            white-space: nowrap;
          }
        }

        .feedback-modal {
          width: min(620px, 100%);
          height: min(600px, 100%);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .feedback-modal-header { flex: 0 0 auto; }
        .feedback-modal-header .cards-kit-panel-header-title { margin-bottom: var(--space-1); }
        .feedback-modal-header .cards-kit-panel-header-description { max-width: 510px; }
        .feedback-form {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
          margin-top: var(--space-4);
        }
        .feedback-form-body {
          display: grid;
          gap: 15px;
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          grid-auto-rows: max-content;
        }
        .feedback-field { display: grid; gap: var(--space-2); min-width: 0; }
        /* Pin the description to a fixed height so the field scrolls internally instead of resizing the form. */
        .feedback-form .cards-kit-rich-text-editor { min-height: 128px; max-height: 128px; }
        /* On phones the whole form must fit above the actions, attachments included. */
        @media (max-width: 620px) {
          .feedback-form .cards-kit-rich-text-editor { min-height: 88px; max-height: 88px; }
        }
        .feedback-label { color: var(--c-text-muted-on-dark); font: var(--type-text-xs); letter-spacing: .06em; text-transform: uppercase; }
        .feedback-counter { justify-self: end; color: var(--c-text-muted-on-dark); font: var(--type-text-xs); font-variant-numeric: tabular-nums; }
        .feedback-counter[data-invalid="true"] { color: var(--c-text-negative-on-dark); }
        .feedback-file-row {
          display: flex;
          align-items: center;
          gap: var(--space-3);
          color: var(--c-text-muted-on-dark);
          font: var(--type-text-xs);
        }
        /* The file picker is a label styled as a secondary button (05 §9). */
        .feedback-file-picker {
          display: inline-flex;
          align-items: center;
          min-height: 32px;
          box-sizing: border-box;
          border: var(--stroke-l) solid var(--c-outline);
          border-radius: var(--radius-m);
          padding: 0 var(--space-3);
          background: var(--c-panel-raised);
          box-shadow: 0 var(--step-2) 0 var(--c-step-shadow);
          color: var(--c-text-on-dark);
          cursor: pointer;
          font: var(--type-text-s);
          font-weight: 600;
        }
        .feedback-file-picker:hover { background: color-mix(in srgb, var(--c-panel-raised) 85%, var(--c-text-on-color)); }
        .feedback-file-picker:focus-within { outline: var(--stroke-l) solid var(--c-selection); outline-offset: var(--stroke-l); }
        .feedback-file-input {
          position: absolute;
          width: 1px;
          height: 1px;
          overflow: hidden;
          clip-path: inset(50%);
          white-space: nowrap;
        }
        .feedback-files {
          display: flex;
          flex-wrap: nowrap;
          gap: 6px;
          margin: 0;
          padding: 2px 0;
          list-style: none;
          overflow-x: auto;
          scrollbar-width: thin;
        }
        .feedback-files li {
          display: inline-flex;
          align-items: center;
          gap: var(--space-1);
          flex: 0 1 auto;
          min-width: 0;
          max-width: 128px;
          padding: 2px 2px 2px var(--space-2);
          border: var(--stroke-m) solid var(--c-outline);
          border-radius: var(--radius-xs);
          background: var(--c-panel-raised);
          font: var(--type-text-xs);
        }
        .feedback-files li > span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .feedback-actions {
          display: flex;
          flex: 0 0 auto;
          align-items: center;
          justify-content: space-between;
          gap: var(--space-3);
          margin-top: var(--space-4);
          padding-top: var(--space-3);
          border-top: var(--stroke-s) solid var(--c-panel-edge);
        }

        .changelog-modal {
          width: min(620px, 100%);
          height: min(600px, 100%);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .changelog-modal-header { flex: 0 0 auto; }
        .changelog-modal-header .cards-kit-panel-header-title { margin-bottom: var(--space-1); }
        .changelog-modal-header .cards-kit-panel-header-description { max-width: 510px; }
        .changelog-body {
          display: flex;
          flex-direction: column;
          gap: var(--space-5);
          flex: 1 1 auto;
          min-height: 0;
          margin-top: var(--space-4);
          padding-right: var(--space-2);
          overflow-y: auto;
          scrollbar-width: thin;
        }
        .changelog-empty { margin: 0; color: var(--c-text-muted-on-dark); font: var(--type-text-s); }
        .changelog-release { display: grid; gap: var(--space-2); }
        .changelog-release-header {
          display: flex;
          align-items: baseline;
          gap: var(--space-3);
          padding-bottom: var(--space-2);
          border-bottom: var(--stroke-s) solid var(--c-panel-edge);
        }
        .changelog-release-version { color: var(--c-selection); font: var(--type-display-xs); }
        .changelog-release-date { color: var(--c-text-muted-on-dark); font: var(--type-text-xs); }
        .changelog-release-title { margin: 0; color: var(--c-text-muted-on-dark); font: var(--type-text-s); }
        .changelog-groups { display: grid; gap: var(--space-3); }
        .changelog-group { display: grid; grid-template-columns: 96px 1fr; gap: var(--space-3); align-items: start; }
        /* Release-note kind: a tag in the family colour (02 §9, 04 §2.1). */
        .changelog-kind {
          justify-self: start;
          box-sizing: border-box;
          padding: var(--space-1) var(--space-2);
          border: var(--stroke-m) solid var(--c-outline);
          border-radius: var(--radius-xs);
          background: var(--c-panel-raised);
          box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
          color: var(--c-text-on-dark);
          font: var(--type-display-3xs);
          text-align: center;
          text-transform: uppercase;
        }
        .changelog-kind[data-kind="feature"] { background: var(--c-positive); color: var(--c-text-on-card); }
        .changelog-kind[data-kind="fix"] { background: var(--c-negative); color: var(--c-text-on-color); text-shadow: var(--text-outline-1); }
        .changelog-kind[data-kind="balance"] { background: var(--c-energy); color: var(--c-text-on-energy); }
        .changelog-group-items { display: grid; gap: var(--space-2); margin: 0; padding: 0; list-style: none; }
        .changelog-group-item {
          position: relative;
          padding-left: var(--space-4);
          color: var(--c-text-on-dark);
          font: var(--type-text-s);
        }
        .changelog-group-item::before {
          content: "";
          position: absolute;
          left: 2px;
          top: .55em;
          width: 4px;
          height: 4px;
          background: var(--c-selection);
        }
        @media (max-width: 460px) {
          .changelog-group { grid-template-columns: 1fr; gap: var(--space-2); }
        }
        .changelog-actions {
          display: flex;
          flex: 0 0 auto;
          align-items: center;
          justify-content: flex-end;
          margin-top: var(--space-4);
          padding-top: var(--space-3);
          border-top: var(--stroke-s) solid var(--c-panel-edge);
        }


        /* Login and nickname gates: the felt with one panel on it (05 §8). */
        .auth-blocking-shell {
          min-height: 0;
          height: 100%;
          box-sizing: border-box;
          display: grid;
          place-items: center;
          padding: var(--space-6);
          background: var(--cards-felt);
          color: var(--c-text-on-dark);
          font: var(--type-text-m);
        }
        .auth-blocking-panel {
          width: min(540px, 100%);
          box-sizing: border-box;
          --cards-panel-step: var(--step-4);
          padding: clamp(var(--space-6), 6vw, var(--space-8));
        }
        .auth-blocking-panel h1 {
          margin: 0 0 var(--space-3);
          font: var(--type-display-m);
          text-transform: uppercase;
        }
        .auth-blocking-panel > p {
          margin: 0 0 var(--space-6);
          color: var(--c-text-muted-on-dark);
        }
        .auth-profile-form { display: grid; gap: var(--space-2); }
        .auth-profile-label {
          color: var(--c-text-muted-on-dark);
          font: var(--type-text-xs);
          letter-spacing: .06em;
          text-transform: uppercase;
        }
        .auth-profile-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: var(--space-3);
        }
        .auth-profile-input { min-width: 0; }
        .auth-secondary-button { margin-top: var(--space-3); }
        .auth-form-error {
          margin-top: var(--space-1);
          color: var(--c-text-negative-on-dark);
          font: var(--type-text-s);
        }
        @media (max-width: 520px) {
          .auth-blocking-shell { padding: var(--space-4); }
          .auth-profile-row { grid-template-columns: 1fr; }
          .auth-primary-button { width: 100%; }
        }

${SCROLLING_CARD_NAME_STYLES}
${BETA_TICKER_STYLES}
${CATALOG_CARD_STYLES}
${DECK_SURFACE_CSS}

          .lobby-header { display: block; }
          .cards-lobby-title { margin: var(--space-2) 0; }
          @media (max-height: 740px) {
            .cards-lobby-panel { padding: 14px 18px !important; }
            .cards-lobby-title { margin-block: var(--space-1) !important; }
            .lobby-searching-list { min-height: 28px !important; }
          }

        @keyframes cards-card-consume {
  0% { opacity: 1; transform: scale(1); }
  55% { opacity: .9; transform: scale(.82); }
  100% { opacity: 0; transform: scale(.35); }
}
/* Превращение (03 §4.10): a short turn in place, as a reveal — away to
   edge-on, where the new face is already standing, and back — with a held
   brightness beside it. Linear overall, a curve per stop. */
@keyframes cards-card-transform {
  0% { transform: perspective(420px) rotateY(0deg) scale(1); animation-timing-function: ${MOTION_EASINGS.in}; }
  45% { transform: perspective(420px) rotateY(90deg) scale(1.08); animation-timing-function: ${MOTION_EASINGS.out}; }
  100% { transform: perspective(420px) rotateY(0deg) scale(1); }
}
@keyframes cards-card-transform-tone {
  0%, 100% { filter: brightness(1); }
  45% { filter: brightness(2) saturate(.5); }
  65% { filter: brightness(1.5); }
}
        @keyframes cards-card-timed-destroy {
          0% { opacity: 1; filter: grayscale(0); }
          60% { opacity: .45; filter: grayscale(1) brightness(1.5); }
          100% { opacity: 0; filter: grayscale(1); transform: translateY(5px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .cards-board-card { animation-duration: 1ms !important; }
        }





  /* Lab — card catalog (05 §5): a reference screen on the felt. A pixel page
     title, the catalog counts as one row of tags (not a dashboard of tiles),
     the search row, then one section per role. */
  .cards-lab {
    min-height: calc(100vh - var(--cards-beta-ticker-reserve));
    box-sizing: border-box;
    /* Scrolls itself for the same reason as .cards-kit-screen: the felt stays whole. */
    overflow-y: auto;
    padding: clamp(var(--space-4), 4vw, var(--space-12));
    background: var(--cards-felt);
    color: var(--c-text-on-dark);
    font: var(--type-text-m);
  }
  .lab-layout { width: min(1180px, 100%); margin: 0 auto; }
  .lab-main { min-width: 0; }
  .lab-title {
    max-width: 900px;
    margin: 0 0 var(--space-3);
    color: var(--c-text-on-dark);
    font: var(--type-display-l);
    text-transform: uppercase;
    text-shadow: var(--text-outline-2);
    text-wrap: balance;
  }
  .lab-intro { max-width: 65ch; margin: 0; color: var(--c-text-muted-on-table); }
  .lab-overview { display: flex; flex-wrap: wrap; gap: var(--space-2); margin: var(--space-5) 0 var(--space-4); }
  .lab-overview > div {
    display: inline-flex;
    align-items: baseline;
    gap: var(--space-2);
    border: var(--stroke-m) solid var(--c-outline);
    border-radius: var(--radius-xs);
    padding: var(--space-1) var(--space-2);
    background: var(--c-panel-bg);
    box-shadow: 0 var(--step-1) 0 var(--c-step-shadow);
  }
  .lab-overview strong { color: var(--c-selection); font: var(--type-display-2xs); font-variant-numeric: tabular-nums; }
  .lab-overview span { color: var(--c-text-on-dark); font: var(--type-text-xs); letter-spacing: .06em; text-transform: uppercase; }
  .lab-controls { display: grid; grid-template-columns: minmax(0, 1fr) minmax(180px, 240px); gap: var(--space-3); margin-bottom: var(--space-12); }
  .lab-search-wrap { position: relative; }
  .lab-search-icon {
    position: absolute;
    left: var(--space-3);
    top: 50%;
    z-index: 1;
    display: grid;
    color: var(--c-text-muted-on-dark);
    transform: translateY(-50%);
    pointer-events: none;
  }
  .lab-search-icon .cards-pixel-icon { width: 14px; height: 14px; }
  .lab-search { padding: 0 96px 0 calc(var(--space-3) * 2 + 14px); }
  .lab-search-count {
    position: absolute;
    right: var(--space-3);
    top: 50%;
    z-index: 1;
    color: var(--c-text-muted-on-dark);
    font: var(--type-text-xs);
    font-variant-numeric: tabular-nums;
    transform: translateY(-50%);
  }
  .lab-section { scroll-margin-top: var(--space-6); margin-bottom: var(--space-12); }
  .lab-section-header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: end;
    gap: var(--space-5);
    margin-bottom: var(--space-5);
    padding-bottom: var(--space-3);
    border-bottom: var(--stroke-l) solid var(--c-table-rule);
  }
  .lab-section-eyebrow { color: var(--c-selection); font: var(--type-display-2xs); text-transform: uppercase; }
  .lab-section h2 {
    margin: var(--space-2) 0;
    color: var(--c-text-on-dark);
    font: var(--type-display-s);
    text-transform: uppercase;
    text-shadow: var(--text-outline-2);
  }
  .lab-section-description { max-width: 65ch; margin: 0; color: var(--c-text-muted-on-table); font: var(--type-text-s); }
  .lab-section-count { align-self: center; color: var(--c-text-muted-on-table); font: var(--type-text-xs); letter-spacing: .06em; text-transform: uppercase; white-space: nowrap; }
  .lab-card-missing { min-width: 0; padding: var(--space-2); border: var(--stroke-m) dashed var(--c-panel-edge); border-radius: var(--radius-m); color: var(--c-text-muted-on-table); }
  @media (max-width: 850px) {
    .lab-section-header { grid-template-columns: 1fr; }
  }
  @media (max-width: 620px) {
    .lab-title { font: var(--type-display-m); }
  }
  @media (max-width: 560px) {
    .lab-controls { grid-template-columns: 1fr; margin-bottom: var(--space-8); }
    .lab-section { margin-bottom: var(--space-8); }
  }
  @media (max-width: 500px) {
    .cards-lab { padding: var(--space-4) var(--space-3) var(--space-8); }
  }

  /* --- Персональная песочница /lab/sandbox (roadmap SB3) --- */
  /* Экран не скроллится: страница — flex-колонка на всю высоту вьюпорта,
     каталоги карт/пассивок живут в выдвижной панели справа, поле при нехватке
     высоты скроллится внутри себя. Боковые паддинги — как у вкладки «Колоды»
     (.deck-page). */
  .cards-lab-sandbox {
    /* Единый минимальный отступ от краёв экрана. */
    --lab-sandbox-pad: 12px;
    /* Ширина панели = ровно 3 карты руки в ряд: 3×112 + 2×8 гэпы + 24 паддинги
       тела + 2 рамка. */
    --lab-sandbox-drawer-w: min(378px, 44vw);
    /* Ширина клетки выводится из ширины экрана: 9 клеток + ~170px рамок и
       паддингов поля; на ультрашироких клетки ограничены боевым максимумом. */
    --cards-cell-width: clamp(52px, calc((100vw - 2 * var(--lab-sandbox-pad) - 170px) / 9), 174px);
    width: 100%;
    max-width: 100%; min-width: 0; box-sizing: border-box; margin: 0;
    height: calc(100dvh - var(--cards-beta-ticker-reserve));
    /* Снизу ровно 10px и НИ пикселя safe-area: она уже сидит внутри
       --cards-beta-ticker-reserve (22px + env(safe-area-inset-bottom)), который
       вычтен из высоты строкой выше. Прибавленная второй раз, она давала на
       айфоне мёртвую полосу в 34px между кнопками раунда и строкой BETA — на
       десктопе env равен нулю, поэтому там этого и не было видно. */
    padding: 14px var(--lab-sandbox-pad) 10px;
    /* Ряд: основная колонка + панель карт в потоке (десктоп) — при открытии
       панели контент сжимается влево, а не перекрывается. */
    display: flex; flex-direction: row; align-items: stretch;
    overflow: hidden;
    background: var(--cards-felt);
    color: var(--c-text-on-dark);
  }
  .lab-sandbox-main {
    flex: 1 1 auto; min-width: 0; min-height: 0;
    display: flex; flex-direction: column; gap: 12px;
  }
  /* Название страницы — подпись в кегле киккера, а не полноразмерный h1: то же
     слово уже стоит в верхнем меню, а высота здесь нужна столу. */
  .lab-sandbox-heading .cards-kit-panel-header-title {
    margin: 0;
    color: var(--c-text-muted-on-table);
    font: var(--type-display-2xs);
    text-shadow: none;
  }
  /* Открытая панель сузила колонку — пересчитываем ширину клетки поля. */
  @media (min-width: 901px) {
    .cards-lab-sandbox[data-drawer-open="true"] {
      --cards-cell-width: clamp(52px, calc((100vw - 2 * var(--lab-sandbox-pad) - var(--lab-sandbox-drawer-w) - 14px - 170px) / 9), 174px);
    }
  }
  /* Клетки с боевым максимумом ширины центрируются в растянутых локациях. */
  .cards-lab-sandbox .cards-board-group { justify-content: center; }
  /* Поле как в бою: соперник сверху, доска, вьюер снизу. Занимает всю свободную
     высоту; если панели+доска выше вьюпорта — скроллится само, не страница.
     Поле некопируемое: перенос карты не должен выделять текст/«плюсики». */
  .lab-sandbox-field {
    flex: 1 1 auto; min-height: 0; overflow-y: auto;
    display: grid; gap: 8px; min-width: 0; align-content: start;
    -webkit-user-select: none; user-select: none;
  }
  .lab-sandbox-board-surface { position: relative; min-width: 0; max-width: 100%; }
  /* Страховка от переполнения: поле всегда ужимается в контейнер (как боевой
     мобильный layout), а не обрезается overflow-ом. */
  .cards-lab-sandbox .cards-board-scroll { overflow-x: hidden; }
  .cards-lab-sandbox .cards-board-field { width: 100%; min-width: 0; padding-inline: 0; }
  .cards-lab-sandbox .cards-board-location { min-width: 0; flex: 1 1 0; }
  /* В sandbox содержимое ячеек перекрывает обычный inset-highlight локации.
     Единый верхний слой сохраняет боевую семантику «цель — вся локация»:
     рамка и tint проходят поверх обеих сторон и центральной линии, но не
     перехватывают клики по вложенным ячейкам. */
  .cards-lab-sandbox .cards-board-location[data-table-highlight="true"]::before {
    content: "";
    position: absolute;
    z-index: 8;
    inset: 0;
    box-sizing: border-box;
    border: var(--stroke-m) solid var(--c-selection);
    border-radius: inherit;
    background: color-mix(in srgb, var(--c-selection) 8%, transparent);
    box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
    pointer-events: none;
  }
  .cards-lab-sandbox .cards-board-location[data-table-interactive="true"]:hover::before {
    background: color-mix(in srgb, var(--c-selection) 14%, transparent);
    box-shadow: inset 0 0 0 var(--stroke-l) var(--c-selection);
  }
  .cards-lab-sandbox .cards-board-group { min-width: 0; }
  .cards-lab-sandbox .cards-board-cell { min-width: 0; }
  /* Сторона — ровно две узкие строки: панель игрока (в её строке характеристик
     сидит бейдж пассивки, как в бою) и строка настроек статов. Целью дропа
     пассивки стала вся панель: отдельная мишень размером в бейдж была меньше
     пальца, а место под неё отнимало у стола целую колонку. */
  .lab-sandbox-side { display: grid; gap: 8px; align-items: stretch; min-width: 0; border-radius: var(--radius-m); }
  /* Пассивку несут — обе стороны обводятся: иначе не видно, куда её положить.
     Обводка рисуется ВНУТРЬ (outline-offset: -1px): снаружи её обрезали и поле
     со своим overflow: hidden, и края экрана — на телефоне рамка выходила за
     вьюпорт слева и справа и читалась кривой, незамкнутой. */
  .lab-sandbox-side[data-passive-drop-ready="true"] {
    outline: var(--stroke-m) dashed var(--c-selection);
    outline-offset: calc(var(--stroke-m) * -1);
  }
  .lab-sandbox-side[data-passive-drop-ready="true"][data-pointer-drag-over="true"] {
    outline: var(--stroke-l) solid var(--c-selection);
    outline-offset: calc(var(--stroke-l) * -1);
    background: color-mix(in srgb, var(--c-selection) 18%, transparent);
  }
  /* Бейдж пассивки в песочнице — источник переноса: она уносится из панели тем же
     жестом, что карта с поля, а полная карта показывается по наведению. */
  /* Приземление привязано к ПОКАЗУ бейджа, а не к его появлению в DOM: пока
     призрак летит, бейдж уже смонтирован и спрятан, так что анимация монтирования
     проигрывалась в невидимом элементе, а на месте посадки бейдж просто
     возникал. Правило начинает матчиться в момент снятия data-passive-hidden —
     тогда и играет. */
  .cards-lab-sandbox .cards-player-passive {
    cursor: grab;
    touch-action: none;
  }
  .cards-lab-sandbox .cards-player-passive:not([data-passive-hidden="true"]) {
    animation: lab-sandbox-passive-land ${MOTION_SPRINGS.flightLandingFadeMs}ms linear;
  }
  .cards-lab-sandbox .cards-player-passive:active { cursor: grabbing; }
  .lab-sandbox-side-main { min-width: 0; display: grid; grid-template-rows: auto auto; gap: 6px; }
  .lab-sandbox-side-controls { display: flex; flex-wrap: wrap; align-items: center; align-content: center; gap: 10px; min-width: 0; padding: 6px 8px; border: var(--stroke-m) solid var(--c-outline); border-radius: var(--radius-m); background: var(--c-panel-bg); }
  .lab-sandbox-stepper { display: inline-flex; align-items: center; gap: 6px; }
  .lab-sandbox-stepper-label { display: inline-flex; align-items: center; color: var(--c-text-muted-on-dark); font: var(--type-text-xs); text-transform: uppercase; letter-spacing: .06em; }
  /* Health and energy in their role colours (02 §5.1), as on the player panel. */
  .lab-sandbox-stepper-label .cards-pixel-icon[data-icon="health"] { color: var(--c-health); }
  .lab-sandbox-stepper-label .cards-pixel-icon[data-icon="energy"] { color: var(--c-energy); }
  .lab-sandbox-stepper-controls { display: grid; grid-template-rows: auto auto auto; place-items: center; gap: 1px; }
  .lab-sandbox-stepper-value { min-width: 22px; text-align: center; font: var(--type-display-2xs); font-variant-numeric: tabular-nums; }
  .lab-sandbox-attack-toggle { display: inline-flex; align-items: center; gap: 6px; color: var(--c-text-muted-on-dark); font: var(--type-text-xs); }
  /* Бейдж встаёт ровно за те же 140ms, что призрак пассивки растворяется
     (PASSIVE_FLIGHT_LANDING_FADE_MS) — это один кроссфейд, а не два движения.
     Не просто проявление: бейдж ВЫЕЗЖАЕТ вдоль строки характеристик слева
     направо — это и есть «вставка». Кроем clip-path, а не масштабом и не
     сдвигом: содержимое всё время рисуется 1:1, поэтому пиксельные глифы внутри
     бейджа не мажутся ресемплом (тем же принципом живёт и сам призрак — он летит
     без изменения размера). Подскока по вертикали нет: призрак к этому моменту
     стоит ровно на бейдже, и любой доводчик читался как второй, лишний рывок. */
  @keyframes lab-sandbox-passive-land {
    from { opacity: 0; clip-path: inset(0 100% 0 0); }
    to { opacity: 1; clip-path: inset(0 0 0 0); }
  }
  .lab-sandbox-passive-clear { border: none; background: transparent; color: var(--c-text-negative-on-dark); cursor: pointer; font: var(--type-display-2xs); line-height: 1; }
  .lab-sandbox-card-editor { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 8px 10px; border: var(--stroke-m) solid var(--c-selection); border-radius: var(--radius-m); background: var(--c-panel-bg); font-family: var(--font-code); }
  .lab-sandbox-hint { color: var(--c-text-muted-on-dark); font: var(--type-text-xs); }
  /* Панель карт/пассивок. Десктоп: колонка справа В ПОТОКЕ (ширина 0 ↔ var,
     контент сжимается влево). Мобилка ≤900px: нижний bottom-sheet, как webapp
     в телеграме. Закрытая панель остаётся в DOM — pointer capture начатого
     переноса продолжает доставлять события её карточкам. */
  .lab-sandbox-drawer {
    flex: none;
    width: 0; margin-left: 0;
    box-sizing: border-box; min-width: 0; min-height: 0;
    display: flex; flex-direction: column;
    overflow: hidden;
    opacity: 0;
    border: var(--stroke-l) solid var(--c-outline); border-radius: var(--radius-xl);
    background: var(--c-panel-bg);
    -webkit-user-select: none; user-select: none;
    transition: width var(--dur-base) var(--ease-out), margin-left var(--dur-base) var(--ease-out), opacity var(--dur-quick) var(--ease-out);
  }
  .lab-sandbox-drawer.is-open { width: var(--lab-sandbox-drawer-w); margin-left: 14px; opacity: 1; }
  /* Хват высоты нужен только шиту: на десктопе панель стоит в потоке и её высоту
     задаёт колонка. */
  .lab-sandbox-sheet-handle { display: none; }
  /* В строке остался только крестик — держим его у правого края. */
  .lab-sandbox-drawer-head {
    flex: none; display: flex; align-items: center; justify-content: flex-end; gap: 8px;
    padding: 4px 8px;
    border-bottom: var(--stroke-s) solid var(--c-panel-edge);
    color: var(--c-text-muted-on-dark); font: var(--type-text-xs); letter-spacing: .06em; text-transform: uppercase;
    white-space: nowrap;
  }
  .lab-sandbox-drawer-body {
    flex: 1 1 auto; min-height: 0; overflow-y: auto;
    display: grid; gap: 16px; align-content: start;
    padding: 12px 12px calc(12px + env(safe-area-inset-bottom, 0px));
    /* The drawer's outline is 3px, not the old 1px: the inline padding gives the
       difference back, so three 112px cards and their gaps (352px) still fit a row. */
    padding-inline: calc(var(--space-3) - var(--stroke-l) + var(--stroke-s));
  }
  /* Список карт — те же белые карты, что и боевая рука (.cards-hand-card +
     HandCardFace); выбор подсвечивается боевым data-selected. */
  .lab-sandbox-catalog { display: grid; gap: 10px; min-width: 0; }
  .lab-sandbox-catalog-controls { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
  .lab-sandbox-catalog-grid { display: flex; flex-wrap: wrap; gap: 8px; min-width: 0; }
  /* Каталог — сотня карт, и каждая это пиксель-арт до 400 <rect>. Пока палец
     тянет высоту шита, браузер на каждый кадр перекладывал и перерисовывал их
     все, включая те, что далеко за пределами скролла: отсюда и подтормаживание.
     content-visibility оставляет живыми только видимые ряды. Собственный размер
     карточки целиком задан CSS (112×138 на десктопе, ячейка грида на мобилке), а
     вся начинка внутри — absolute с инсетами, поэтому ни пропуск содержимого, ни
     paint-containment не могут ничего сдвинуть или обрезать. */
  .lab-sandbox-catalog-item {
    cursor: grab;
    touch-action: none;
    content-visibility: auto;
    contain-intrinsic-size: 112px 138px;
  }
  .lab-sandbox-catalog-item:active { cursor: grabbing; }
  /* Пассивки — компактные MiniCard в том же футпринте, что и карты руки
     (112×138), а не крупные детальные панели. */
  .lab-sandbox-passive-catalog { display: grid; gap: 10px; min-width: 0; }
  .lab-sandbox-passive-grid { display: flex; flex-wrap: wrap; gap: 8px; min-width: 0; }
  .lab-sandbox-passive-card { position: relative; width: 112px; height: 138px; box-sizing: border-box; min-width: 0; border: 2px solid transparent; border-radius: var(--radius-m); cursor: grab; touch-action: none; }
  .lab-sandbox-passive-card:active { cursor: grabbing; }
  .lab-sandbox-passive-card.is-picked { border-color: var(--c-selection); }
  /* MiniCard внутри переносимых пассивок несёт .cards-tilt с touch-action:
     manipulation — жест доставался браузеру: Safari панорамировал и слал
     pointercancel, обрывая перенос. Жестом владеет перенос, как у карт руки. */
  .lab-sandbox-passive-card .cards-tilt { touch-action: none; }
  .lab-sandbox-board-surface .cards-board-card { touch-action: none; }
  /* A newly created/moved authoritative occupant is already measurable as the
     flight target, but remains hidden until the released ghost parks over it. */
  .lab-sandbox-board-surface .cards-board-card[data-flight-hidden="true"] { visibility: hidden; }
  /* Переносимая карта поля прячется под летящим превью — как staged в бою;
     обратно её раскрывает посадка призрака (landCardFlight). */
  .lab-sandbox-board-surface .cards-board-card[data-sandbox-carried="true"] { visibility: hidden; }
  /* Фикс-«язычок» на правом крае открывает sandbox-каталог; открытая панель
     прячет его, потому что сама показывает действие закрытия. */
  .lab-sandbox-drawer-handle {
    display: flex; position: fixed; z-index: 41; right: 0; top: 38%;
    flex-direction: column; align-items: center; gap: 5px;
    padding: 11px 5px;
    border: var(--stroke-l) solid var(--c-outline); border-right: 0; border-radius: var(--radius-xl) 0 0 var(--radius-xl);
    background: var(--c-panel-raised); color: var(--c-selection);
    font: var(--type-display-3xs);
    cursor: pointer;
    box-shadow: 0 var(--step-3) 0 var(--c-step-shadow);
    transition: opacity var(--dur-quick) var(--ease-out);
  }
  .lab-sandbox-drawer-handle[data-open="true"] { opacity: 0; pointer-events: none; }
  .lab-sandbox-drawer-handle-title { writing-mode: vertical-rl; text-orientation: mixed; }
  .lab-sandbox-drawer-handle-arrow { font: var(--type-display-2xs); line-height: 1; }
  /* Панель управления — скруглённая карточка внизу нескроллящейся колонки. */
  .lab-sandbox-bottombar {
    flex: none;
    display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
    margin: 0; padding: 10px 14px;
  }
  /* Единый слот пары round-действий: playback меняет содержимое слота, но не
     его соседей. На широком экране это обычная строка из двух кнопок. */
  .lab-sandbox-round-actions { display: inline-flex; flex: 1 1 auto; align-items: center; gap: 10px; }
  .lab-sandbox-round-actions > .cards-kit-button { flex: 1 1 0; }
  .lab-sandbox-mode-switch { display: inline-flex; align-items: center; gap: 7px; color: var(--c-text-muted-on-dark); font: var(--type-text-xs); cursor: pointer; }
  .lab-sandbox-mode-switch > input { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); clip-path: inset(50%); white-space: nowrap; }
  /* A pixel slide switch: sunken track, cream knob; on — gold. */
  .lab-sandbox-mode-switch-track { width: 34px; height: 18px; box-sizing: border-box; padding: 1px; border: var(--stroke-m) solid var(--c-outline); border-radius: var(--radius-xs); background: var(--c-field-bg); }
  .lab-sandbox-mode-switch-track > span { display: block; width: 12px; height: 12px; border-radius: var(--radius-xs); background: var(--c-text-muted-on-dark); transition: transform var(--dur-quick) var(--ease-out); }
  .lab-sandbox-mode-switch > input:checked + .lab-sandbox-mode-switch-track { background: var(--c-selection); }
  .lab-sandbox-mode-switch > input:checked + .lab-sandbox-mode-switch-track > span { background: var(--c-outline); transform: translateX(16px); }
  .lab-sandbox-mode-switch > input:focus-visible + .lab-sandbox-mode-switch-track { outline: var(--stroke-l) solid var(--c-selection); outline-offset: var(--stroke-l); }
  /* Корзина вместо слова «Очистить»: иконка задаётся в px, а не в 1em, — кнопка
     набирает свой размер от неё и не зависит от кегля надписей рядом. */
  .lab-sandbox-clear .cards-pixel-icon { width: 14px; height: 14px; vertical-align: -0.15em; }
  .lab-sandbox-confirm, .lab-sandbox-pending { display: inline-flex; align-items: center; gap: 6px; color: var(--c-text-muted-on-dark); font: var(--type-text-s); }
  .lab-sandbox-choice { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; color: var(--c-selection); }
  /* Превью перетаскиваемой пассивки — тот же MiniCard, что и в боковом меню,
     без «ручного» лэйаута: базовый хром превью руки (рамка/фон/паддинги) снят.
     Размеры с !important, чтобы мобильный override .cards-touch-drag-preview
     (84×96 под боевую руку) не сжимал пассивку — она летит в слот 1:1.
     Transform — только translate: пиксель-арт MiniCard мажется на любом
     ресемпле композитного слоя (perspective/rotate/skew/scale), поэтому
     3D-наклон боевых превью здесь отключён — арт остаётся чётким. */
  .lab-sandbox-passive-drag-preview {
    width: 112px !important; height: 138px !important; padding: 0 !important;
    border: none; background: transparent; color: var(--c-text-on-dark);
    transform:
      translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
      translate(-50%, calc(-100% - 18px)) !important;
  }
  /* Призрак-полёт пассивки (в слот или обратно в каталог) наследует чёткий
     translate-only transform и измеренный anchor gap живого превью. */
  .lab-sandbox-passive-drag-preview[data-return-flight="true"] {
    transform:
      translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
      translate(-50%, calc(-100% - var(--cards-return-gap, 18px))) !important;
  }
  /* Уносимый из панели бейдж прячется под летящим превью (как staged в бою);
     вставший по дропу — до посадки призрака (раскрывает PassiveFlightGhost). */
  .cards-lab-sandbox .cards-player-passive[data-sandbox-carried="true"],
  .cards-lab-sandbox .cards-player-passive[data-passive-hidden="true"] { visibility: hidden; }
  @media (prefers-reduced-motion: reduce) {
    .cards-lab-sandbox .cards-player-passive { animation: none; }
  }
  @media (max-width: 900px), (max-height: 520px) and (pointer: coarse) {
    .cards-lab-sandbox {
      /* Компактные боевые метрики поля + учёт боковых паддингов контейнера
         (у боевого экрана они 8px, у песочницы — как у каталога). */
      --cards-cell-width: calc((100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 2 * var(--lab-sandbox-pad) - 66px) / 9);
      --cards-cell-gap: 2px;
      --cards-location-gap: 8px;
      --cards-board-padding: 3px;
      --cards-cell-padding: 1px;
      --cards-overlay-stack-shift: 10px;
      --cards-cell-height: clamp(64px, 9.5dvh, 82px);
    }
    .cards-lab-sandbox .cards-board-field { padding-inline: 0 !important; }
    /* Боевой мобильный .cards-board-scroll выпирает на -8px в обе стороны
       (width:100%+16px) — в песочнице это давало горизонтальный скролл поля.
       Скроллов у стола быть не должно: панели и доска обязаны помещаться. */
    .cards-lab-sandbox .cards-board-scroll {
      width: 100%;
      max-width: 100% !important;
      margin-inline: 0;
      padding-inline: 0 !important;
    }
    .lab-sandbox-field { overflow: hidden; }
    /* Обе строки стороны — по содержимому. Степперы стоят в строку («− значение
       +», DOM-порядок «+ значение −» разворачивается) и занимают одну строку
       ~28px; вытягивать их во весь остаток нечего — остаток нужен столу. */
    .lab-sandbox-side-main { align-content: start; }
    .lab-sandbox-side-controls { gap: 8px; padding: 2px 6px; }
    .lab-sandbox-stepper-controls {
      display: inline-flex;
      flex-direction: row-reverse;
      align-items: center;
      gap: 2px;
    }
    .lab-sandbox-stepper-value { min-width: 16px; }
    /* Компактная боевая рука на этом брейкпоинте задаёт .cards-hand-card
       width/height:100% !important под грид 4-в-ряд — повторяем её сетку. */
    .lab-sandbox-catalog-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      grid-auto-rows: 74px;
      gap: 4px;
    }
    /* Панель управления — grid с фиксированными местами: первая строка — слот
       действий раунда и корзина, вторая — оба тумблера. Поэтому «Пропустить
       анимацию» заменяет лишь содержимое первого слота, не вытесняя корзину. */
    .lab-sandbox-bottombar {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
    }
    .lab-sandbox-round-actions { grid-column: 1; grid-row: 1; gap: 6px; min-width: 0; }
    .lab-sandbox-clear { grid-column: 2; grid-row: 1; }
    /* Подтверждение очистки шире кнопки, которую заменяет, — своей строкой,
       чтобы не выдавливать «Отменить раунд» на перенос. */
    .lab-sandbox-confirm { grid-column: 1 / -1; grid-row: 2; }
    .lab-sandbox-bottombar > .lab-sandbox-mode-switch { grid-row: 3; min-width: 0; }
    .lab-sandbox-bottombar > .lab-sandbox-mode-switch:nth-of-type(1) { grid-column: 1; }
    .lab-sandbox-bottombar > .lab-sandbox-mode-switch:nth-of-type(2) { grid-column: 2; }
    .lab-sandbox-pending, .lab-sandbox-choice { grid-column: 1 / -1; }
    /* Кнопки раунда на телефоне — размера compact (32 px): высота уходит у
       стола (панель стоит под полем, полю остаётся остаток), а 36 px sm здесь
       лишние. Те же значения, что у .cards-kit-button--compact. */
    .lab-sandbox-bottombar .cards-kit-button {
      min-height: 32px;
      border-radius: var(--radius-m);
      padding: 0 var(--space-3);
      font: var(--type-text-s);
      font-weight: 600;
      white-space: nowrap;
    }
    /* The assistant coin sits fixed in the bottom-right corner, over the bar's
       last switch: the bar keeps its column free. */
    .lab-sandbox-bottombar { padding-right: calc(24px + var(--space-4)); }
    /* Мобилка: панель карт — нижний bottom-sheet (как webapp в телеграме),
       выезжает снизу и не сдвигает контент. */
    .lab-sandbox-drawer {
      position: fixed; z-index: 40; left: 0; right: 0; bottom: 0; top: auto;
      width: auto; height: var(--lab-sandbox-sheet-h, min(72dvh, 560px));
      max-height: 100dvh;
      margin: 0; opacity: 1;
      border-bottom: none; border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
      box-shadow: none;
      transform: translateY(calc(100% + 32px));
      transition: transform var(--dur-move) var(--ease-out), height var(--dur-base) var(--ease-out);
    }
    .lab-sandbox-drawer.is-open { width: auto; margin: 0; transform: translateY(0); }
    /* Пока палец тянет — высота обязана идти за ним кадр в кадр, любая анимация
       читается как залипание. */
    .lab-sandbox-drawer[data-sheet-dragging="true"] { transition: none; }
    /* Хват шита. Полоска нарисована ::before и стоит там же, где стояла раньше, а
       сама кнопка много выше неё: пальцем в четыре пикселя не попасть. */
    .lab-sandbox-sheet-handle {
      flex: none;
      display: flex; align-items: center; justify-content: center;
      width: 100%; height: 26px;
      padding: 0; border: 0; background: none;
      touch-action: none; cursor: ns-resize;
      -webkit-tap-highlight-color: transparent;
    }
    /* The grip is a pixel bar 32×4 in cream-500 (05 §6). */
    .lab-sandbox-sheet-handle::before {
      content: "";
      width: 32px; height: 4px;
      background: var(--c-text-muted-on-dark);
    }
    .lab-sandbox-sheet-handle:focus-visible { outline: none; }
    .lab-sandbox-sheet-handle:focus-visible::before { background: var(--c-selection); }
    /* Крестик садится в строку хвата, а не под неё: своей строкой он отбирал у
       списка карт ещё около тридцати пикселей. */
    .lab-sandbox-drawer-head {
      margin-top: -26px;
      height: 26px;
      padding: 0 4px;
      border-bottom: 0;
      pointer-events: none;
    }
    .lab-sandbox-drawer-head > * { pointer-events: auto; }
    .lab-sandbox-drawer-body { padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px)); }
    /* Внутри шита вертикальный свайп должен скроллить список; основной способ
       поставить карту на мобилке — тап (шит закрывается, тап по клетке). Карт в
       каталоге сотни, и скролл по ним важнее переноса. */
    .lab-sandbox-catalog-item { touch-action: pan-y; }
    /* Пассивки — исключение: их пять, скроллить по ним нечего, а несут их именно
       пальцем вверх, к своей панели. С pan-y браузер на середине жеста решал,
       что палец скроллит шит, забирал указатель (pointercancel) — и пассивка
       соскакивала. Здесь жест наш целиком, вместе с .cards-tilt внутри MiniCard. */
    .lab-sandbox-passive-card, .lab-sandbox-passive-card .cards-tilt { touch-action: none; }
    /* Пассивки — та же компактная сетка 4-в-ряд, что и карты. */
    .lab-sandbox-passive-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      grid-auto-rows: 74px;
      gap: 4px;
    }
    .lab-sandbox-passive-card { width: auto; height: auto; }
    /* Мобильный anchor gap превью — 12px, как у previewAnchorGap(). */
    .lab-sandbox-passive-drag-preview {
      transform:
        translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
        translate(-50%, calc(-100% - 12px)) !important;
    }
    .lab-sandbox-passive-drag-preview[data-return-flight="true"] {
      transform:
        translate3d(var(--cards-drag-x, 0px), var(--cards-drag-y, 0px), 0)
        translate(-50%, calc(-100% - var(--cards-return-gap, 12px))) !important;
    }
  }
  /* Высокая мобилка: стол — как в бою, три локации столбиком, три клетки в ряд.
     Сама стопка описана в GLOBAL_BASE_CSS и матчит обе поверхности сразу
     (:is(.cards-game-view, .cards-lab-sandbox)); здесь — только то, без чего ей
     нечего делить: высота доски должна быть определённой. Поле отдаёт доске
     весь остаток (панели сторон — по содержимому), а доска становится
     flex-колонкой, в которой локации и делят эту высоту. */
  @media (max-width: 900px) and (min-height: 700px) {
    .cards-lab-sandbox {
      /* Три полосы вместо девяти, от собственных боковых паддингов: 30px — это
         боевые 52 минус 16 паддинга game-main (у песочницы вместо них
         --lab-sandbox-pad с каждой стороны) и минус 6 паддинга поля, который
         песочница и так обнуляет. */
      --cards-cell-width: calc((100vw - env(safe-area-inset-left) - env(safe-area-inset-right) - 2 * var(--lab-sandbox-pad) - 30px) / 3);
      /* Кольцо, в которое вылезает арт карты (боевое значение стопки). */
      --cards-cell-padding: 3px;
    }
    .lab-sandbox-field {
      grid-template-rows: auto minmax(0, 1fr) auto;
      align-content: stretch;
    }
    .lab-sandbox-board-surface {
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  }

`;
